// @ts-nocheck
/**
 * GeoTIFF Viewer - Multi-mode map with GeoTIFF overlay and polygons
 * Supports: GeoTIFF-only, Google Maps 2D, Leaflet Satellite
 */

'use client';

import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, CircleMarker, Polyline, Polygon, Marker } from 'react-leaflet';
import L from 'leaflet';
import { ChevronDown, ChevronUp, ChevronRight, Layers, Image as ImageIcon, Map as MapIcon, Edit3, Save, X, Home as HomeIcon, AlertTriangle, Grid3x3, Ruler, Square, Trash2, PenTool, Circle, Pentagon, Plus } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Aurora Components
import { ModeSwitcher } from '../components/aurora/ModeSwitcher';
import { RoofControls } from '../components/aurora/RoofControls';
import { ObstructionControls } from '../components/aurora/ObstructionControls';
import { ModuleControls } from '../components/aurora/ModuleControls';
import { SystemMetrics } from '../components/aurora/SystemMetrics';

// Extracted GeoTIFF Components (Phase 2)
import { PolygonDrawer } from '../components/geotiff/drawing/PolygonDrawer';
import { ObstructionDrawer } from '../components/geotiff/drawing/ObstructionDrawer';
import { CircleObstructionDrawer } from '../components/geotiff/drawing/CircleObstructionDrawer';
import { RulerTool } from '../components/geotiff/drawing/RulerTool';
import { ModuleGrid } from '../components/geotiff/modules/ModuleGrid';
import { DraggableModule } from '../components/geotiff/modules/DraggableModule';
import { SelectionBoxDrawer } from '../components/geotiff/selection/SelectionBoxDrawer';
import { MultiSelectionBox } from '../components/geotiff/selection/MultiSelectionBox';

// Extracted GeoTIFF Components (Phase 3 - Additional Components)
import { ModuleBoxDrawer } from '../components/geotiff/drawing/ModuleBoxDrawer';
import { DraggableObstruction } from '../components/geotiff/obstructions/DraggableObstruction';
import { EditablePolygon } from '../components/geotiff/polygon/EditablePolygon';
import { EdgeMeasurements } from '../components/geotiff/polygon/EdgeMeasurements';
import { SnapEdgeHighlight } from '../components/geotiff/polygon/SnapEdgeHighlight';
import { EdgeIndicators } from '../components/geotiff/polygon/EdgeIndicators';
import { RoofPolygonLayer } from '../components/geotiff/polygon/RoofPolygonLayer';
import { SpacebarPanController } from '../components/geotiff/interactions/SpacebarPanController';
import { MapInteractionController } from '../components/geotiff/interactions/MapInteractionController';
import { CustomPanesInitializer } from '../components/geotiff/map/CustomPanesInitializer';
import { MapLoadingOverlay } from '../components/geotiff/map/MapLoadingOverlay';
import { MapVisibilityHandler, FitBounds, MapRefCapture, ZoomSync } from '../components/geotiff/map/MapHelpers';

// Extracted GeoTIFF Components (Phase 4 - Overlays)
import { GeoTiffImageOverlay } from '../components/geotiff/overlays/GeoTiffImageOverlay';

// Extracted GeoTIFF Components (Phase 5 - UI)
import {
  FloatingRulerButton,
  FloatingScreenshotButton,
  ModeToggleButtons,
  MapModeSelector,
  LeftPanelHeader,
  BypassModeBanner,
  RoofList,
  AddRoofButtons,
  OrientationToggle,
  ModulePowerInput,
  SystemMetrics as RoofSystemMetrics,
  CollapsibleSection,
  TargetModuleCountInput,
  ModuleActionButtons,
  SegmentDrawingPanel,
  SpacingSlider,
  ObstructionList,
  ObstructionDrawButtons,
  AzimuthPresetButtons,
  SegmentListItem,
  PanelSelector,
  ModuleDimensionInputs,
  FreeFieldDrawingPanel,
  SiteModePanel,
  SystemModePanel,
  CompactModePanel,
} from '../components/geotiff/ui';

// Custom Hooks (Phase 5)
import {
  useUIState,
  useObstructionState,
  useObstructionHandlers,
  useSegmentHandlers,
  useModuleState,
  usePanelSelection,
  usePolygonState,
  useRoofDrawing,
  useMapState,
  useLoadingState,
  useProjectState,
  FREE_FIELD_POLYGON_ID
} from '../components/geotiff/hooks';

// Utility functions
import { isPointInPolygon, calculatePolygonArea } from '../utils/geometry/polygonCalculations';
import { createModuleRectangle } from '../utils/modules/moduleGridCalculator';
import {
  calculateModulesForBox as calculateModulesForBoxPure,
  type CalculateModulesInput,
  type ModuleConfig,
  type SegmentConfig,
} from '../utils/modules/calculateModulesForBox';

// Project persistence helpers
import {
  buildSingleRoofProjectData,
  buildMultiRoofProjectDataV2,
  parseLoadedProjectData,
  type ProjectStateInput,
} from '../services/projectDataBuilders';
import { restoreSegmentsDeduplicated } from '../utils/segmentRestoreUtils';

// Types (Polygon renamed to RoofPolygon to avoid conflict with react-leaflet's Polygon)
import type {
  Polygon as RoofPolygon,
  PolygonData,
  SolarModule,
  ModuleSegment,
  GeoTiffMetrics,
} from '../types/geotiff.types';

import { env } from 'env';
// Re-export GeoTiffMetrics for consumers
export type { GeoTiffMetrics } from '../types/geotiff.types';

// API Configuration
const API_URL = env.API_URL || 'http://localhost:5001';

// Custom panes initializer for z-index control
// Component to display edge measurements
// Component for draggable obstructions with rotation and resizing
// Component to handle Spacebar + drag for map panning
// DELETED: MultiSelectionBox (~220 lines) - now using extracted component from Phase 2



// Component for drawing module box areas
// Component to highlight the selected snap edge
// Component to render clickable edge indicators with arrows
// Component to manage map interactions based on edit mode
// Editable Polygon Component
// Google Maps Component
function GoogleMapView({
  center,
  pinPosition,
  bounds,
  selectedLayer,
  polygons,
  showPolygons,
  selectedPolygon,
  handlePolygonClick,
  getPolygonStyle,
  cacheId,
  useTestFiles,
}: any) {
  const mapRef = useRef<any>(null);
  const overlayRef = useRef<any>(null);
  const polygonOverlaysRef = useRef<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadGoogleMaps = async () => {
      try {
        // Wait for Google Maps API to be available
        if (typeof window.google === 'undefined') {
          setIsLoading(false);
          console.error('Google Maps API not loaded');
          return;
        }

        // Use importLibrary to load maps library
        await window.google.maps.importLibrary('maps');

        if (!mapRef.current) {
          initializeGoogleMap();
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading Google Maps:', error);
        setIsLoading(false);
      }
    };

    loadGoogleMaps();
  }, []);

  useEffect(() => {
    if (mapRef.current && overlayRef.current) {
      overlayRef.current.setMap(null);
    }
    if (mapRef.current) {
      addGroundOverlay();
    }
  }, [selectedLayer, bounds, useTestFiles]);

  useEffect(() => {
    if (mapRef.current) {
      updatePolygons();
    }
  }, [polygons, showPolygons, selectedPolygon]);

  // Recenter map when pinPosition changes
  useEffect(() => {
    if (mapRef.current && pinPosition) {
      // Small delay to ensure map is fully rendered
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setCenter({ lat: pinPosition.lat, lng: pinPosition.lng });
        }
      }, 100);
    }
  }, [pinPosition]);

  // Recenter map when it becomes visible (component mounts or remounts)
  useEffect(() => {
    if (mapRef.current && pinPosition) {
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setCenter({ lat: pinPosition.lat, lng: pinPosition.lng });
        }
      }, 300);
    }
  }, []);

  const initializeGoogleMap = () => {
    const mapDiv = document.getElementById('google-map');
    if (!mapDiv || !window.google?.maps?.Map) return;

    // Use pinPosition if available, otherwise fall back to center
    const mapCenter = pinPosition
      ? { lat: pinPosition.lat, lng: pinPosition.lng }
      : { lat: center.lat, lng: center.lng };

    mapRef.current = new window.google.maps.Map(mapDiv, {
      center: mapCenter,
      zoom: 20,
      maxZoom: 23,
      mapTypeId: 'roadmap',
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });

    addGroundOverlay();
  };

  const addGroundOverlay = () => {
    if (!mapRef.current || !bounds || !window.google) return;

    const imageBounds = {
      north: bounds.north,
      south: bounds.south,
      east: bounds.east,
      west: bounds.west,
    };

    // Build render URL with cache_id
    const renderUrl = cacheId
      ? `${API_URL}/api/geotiff/render/${selectedLayer}?cache_id=${cacheId}`
      : `${API_URL}/api/geotiff/render/${selectedLayer}?use_test_files=${useTestFiles}`;


    overlayRef.current = new window.google.maps.GroundOverlay(
      renderUrl,
      imageBounds,
      { opacity: 0.8, clickable: false }
    );

    overlayRef.current.setMap(mapRef.current);
  };

  const updatePolygons = () => {
    // Clear existing polygons
    polygonOverlaysRef.current.forEach(poly => poly.setMap(null));
    polygonOverlaysRef.current = [];

    if (!showPolygons || !polygons || !mapRef.current || !window.google) return;

    // Add new polygons
    polygons.forEach((polygon: any) => {
      // Map ALL coordinate rings: [0] = exterior, [1+] = holes
      const paths = polygon.geometry.coordinates.map((ring: [number, number][]) =>
        ring.map(([lng, lat]: [number, number]) => ({
          lat,
          lng,
        }))
      );

      const style = getPolygonStyle(polygon);
      const googlePolygon = new window.google.maps.Polygon({
        paths: paths,  // Google Maps supports multiple paths for holes!
        strokeColor: style.color,
        strokeOpacity: 1,
        strokeWeight: style.weight,
        fillColor: style.fillColor,
        fillOpacity: style.fillOpacity,
        clickable: true,
      });

      googlePolygon.addListener('click', (e: any) => {
        // Only allow polygon clicks when in Roof Select Mode
        if (isRoofSelectMode && !isDrawingObstruction && !isDrawingSegment) {
          const shiftKey = e.domEvent?.shiftKey || false;
          handlePolygonClick(polygon.id, shiftKey);
        }
      });

      googlePolygon.setMap(mapRef.current);
      polygonOverlaysRef.current.push(googlePolygon);
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EBFD03] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Google Maps...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      id="google-map"
      style={{ height: '100%', width: '100%' }}
    />
  );
}

export default function GeoTiffViewer({
  buildingInsights,
  dataLayers,
  cacheId,
  pinPosition,
  setPinPosition,
  selectedLayer: selectedLayerProp,
  showMask: showMaskProp,
  mapMode: mapModeProp,
  setMapMode: setMapModeProp,
  showPinMarker = true,
  showRgbLayer = true,
  onCacheIdUpdate,
  onMetricsUpdate,
  compactMode = false,
  showMonthlyFlux: showMonthlyFluxProp = false,
  selectedMonth: selectedMonthProp = 0,
  hideSystemMetrics = false,
  mapZoom,
  isVisible = true,
  loadedProjectData = null,
  hasDetectedProject = false,
  bypassPolygonFetch: bypassPolygonFetchProp = false,
  injectedPolygonData: injectedPolygonDataProp = null,
  polygonLoadingMode: polygonLoadingModeProp = 'auto',
  targetModuleConfig = null,
  onScreenshotCapture,
  simpleEmbedEditMode = false,
  hideControls = false,
}: GeoTiffViewerEmbeddedProps) {
  // ========================================
  // UI State (consolidated via useUIState hook)
  // ========================================
  const {
    roofCardCollapsed, setRoofCardCollapsed,
    obstructionCardCollapsed, setObstructionCardCollapsed,
    moduleCardCollapsed, setModuleCardCollapsed,
    fieldSegmentsCollapsed, setFieldSegmentsCollapsed,
    menuCollapsed, setMenuCollapsed,
    leftPanelCollapsed, setLeftPanelCollapsed,
    controlsOpen, setControlsOpen,
    mapModeOpen, setMapModeOpen,
    simplificationOpen, setSimplificationOpen,
    polygonToggleOpen, setPolygonToggleOpen,
    obstructionsOpen, setObstructionsOpen,
    snapEdgeOpen, setSnapEdgeOpen,
    moduleAdvancedOpen, setModuleAdvancedOpen,
  } = useUIState({ compactMode });

  // ========================================
  // Map State (consolidated via useMapState hook)
  // ========================================
  const {
    siteSystemMode, setSiteSystemMode,
    localMapMode, setLocalMapMode,
    showAllPolygons, setShowAllPolygons,
    showMeasurements, setShowMeasurements,
    establishedRoofIds, setEstablishedRoofIds,
    addEstablishedRoof,
    removeEstablishedRoof,
    isRoofEstablished: isRoofEstablishedHook,
  } = useMapState();

  // Wrapper for isRoofEstablished to handle null
  const isRoofEstablished = (roofId: number | null): boolean => {
    if (roofId === null) return false;
    return isRoofEstablishedHook(roofId);
  };

  // Helper function to establish a roof (with logging)
  const establishRoof = (roofId: number) => {
    addEstablishedRoof(roofId);
  };

  // Helper function to unestablish a roof (with logging)
  const unestablishRoof = (roofId: number) => {
    removeEstablishedRoof(roofId);
  };

  // ========================================
  // Roof Drawing State (consolidated via useRoofDrawing hook)
  // ========================================
  const {
    isRoofSelectMode, setIsRoofSelectMode,
    isAddRoofMode, setIsAddRoofMode,
    isRulerMode, setIsRulerMode,
    rulerPoints, setRulerPoints,
    rulerCursorPos, setRulerCursorPos,
    isDrawingRoof, setIsDrawingRoof,
    roofDrawingVertices, setRoofDrawingVertices,
    isEditing, setIsEditing,
    editedCoordinates, setEditedCoordinates,
  } = useRoofDrawing();

  // Use layer from parent component (Solar Assessment)
  const selectedLayer = selectedLayerProp;
  const showMask = showMaskProp;
  // Monthly flux state - use props from parent (controlled from Solar Assessment)
  const showMonthlyFlux = showMonthlyFluxProp;
  const selectedMonth = selectedMonthProp;
  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  // Use mapMode from parent if provided, otherwise use local state (from useMapState hook)
  const mapMode = mapModeProp || localMapMode;
  const setMapMode = setMapModeProp || setLocalMapMode;
  
  // Debug logging for mapMode
  useEffect(() => {
  }, [mapMode, mapModeProp, localMapMode]);

  // ========================================
  // Polygon State (consolidated via usePolygonState hook)
  // ========================================
  const {
    polygonData, setPolygonData,
    originalPolygonData, setOriginalPolygonData,
    showPolygons, setShowPolygons,
    selectedPolygons, setSelectedPolygons,
    activeRoofId, setActiveRoofId,
    selectedPolygonForEdit, setSelectedPolygonForEdit,
    editingPolygonVertex, setEditingPolygonVertex,
    editedPolygonCoords, setEditedPolygonCoords,
    originalPolygonCoords, setOriginalPolygonCoords,
    snapEdgeIndex, setSnapEdgeIndex,
    snapEdgeAzimuth, setSnapEdgeAzimuth,
    isSelectingSnapEdge, setIsSelectingSnapEdge,
    manualAzimuth, setManualAzimuth,
  } = usePolygonState();

  // Legacy compatibility: Compute selectedPolygon from activeRoofId
  const selectedPolygon = activeRoofId;
  const setSelectedPolygon = (id: number | null) => {
    if (id === null) {
      setSelectedPolygons([]);
      setActiveRoofId(null);
    } else {
      setSelectedPolygons([id]);
      setActiveRoofId(id);
    }
  };

  // ========================================
  // Loading State (consolidated via useLoadingState hook)
  // ========================================
  const {
    loading, setLoading,
    polygonStatus, setPolygonStatus,
    useTestFiles, setUseTestFiles,
    restorationComplete, setRestorationComplete,
    bypassPolygonFetch, setBypassPolygonFetch,
    loadingMode, setLoadingMode,
    tolerance, setTolerance,
    angleThreshold, setAngleThreshold,
    hasInitialFit, setHasInitialFit,
  } = useLoadingState({
    initialBypassPolygonFetch: bypassPolygonFetchProp,
    initialLoadingMode: polygonLoadingModeProp === 'manual' ? 'manual' : 'new',
  });

  // RGB layer priority loading - delay other layers until RGB loads
  const [rgbLayerReady, setRgbLayerReady] = useState(false);

  // "Eliminar nodo" mode: when true, click on a vertex removes it (no Alt key). Shared by polygon and segment vertex edit.
  const [removeVertexMode, setRemoveVertexMode] = useState(false);
  // "Agregar nodo" mode: when true, click on a polyline edge adds a vertex (no Alt key). Embed-only.
  const [addVertexMode, setAddVertexMode] = useState(false);

  // Reset RGB ready state when cache changes (new location)
  useEffect(() => {
    setRgbLayerReady(false);
  }, [cacheId]);

  // Sync with parent prop changes AND trigger re-fetch when toggling auto-roof
  useEffect(() => {
    if (loadingMode !== 'existing') { // Don't override 'existing' mode
      const newMode = polygonLoadingModeProp === 'manual' ? 'manual' : 'new';

      // Detect when switching from manual to auto ('new')
      // This should trigger a fresh polygon fetch
      if (loadingMode === 'manual' && newMode === 'new') {
        // Clear polygon data to allow the fetch useEffect to run
        // Keep bounds info but clear polygons
        if (polygonData) {
          setPolygonData({
            ...polygonData,
            polygons: [], // Clear polygons to trigger re-fetch
          });
          setPolygonStatus('pending');
          setLoading(true);
        }
      }

      setLoadingMode(newMode);
    }
  }, [polygonLoadingModeProp]);

  // Sync bypass mode with prop
  useEffect(() => {
    setBypassPolygonFetch(bypassPolygonFetchProp);
  }, [bypassPolygonFetchProp]);

  // Simple embed (botón removido): el padre ya no usa simpleEmbedEditMode; entrar/salir es por doble clic en el polígono.
  // Si en el futuro se vuelve a pasar simpleEmbedEditMode, solo aplicar clear cuando no estamos en embed (hideControls).
  useEffect(() => {
    if (simpleEmbedEditMode && selectedPolygon !== null) {
      setEditMode('site');
      setSiteSystemMode('site');
      setSelectedPolygonForEdit(selectedPolygon);
      setEditingPolygonVertex(selectedPolygon);
    } else if (!simpleEmbedEditMode && !hideControls) {
      setSelectedPolygonForEdit(null);
      setEditingPolygonVertex(null);
      setRemoveVertexMode(false);
      setSiteSystemMode('system');
      setEditMode('system');
    }
  }, [simpleEmbedEditMode, selectedPolygon, hideControls]);

  // In manual mode: default to system mode and auto-engage free-field mode (unless loading a project)
  useEffect(() => {
    if (loadingMode === 'manual' && !loadedProjectData) {
      // In manual mode, go straight to System mode with free-field enabled
      setSiteSystemMode('system');
      setEditMode('system');
      setIsFreeFieldMode(true);
    }
  }, [loadingMode, loadedProjectData]);

  const googleMapRef = useRef<any>(null);
  const googleOverlayRef = useRef<any>(null);
  const leafletMapRef = useRef<any>(null);
  const moduleSegmentsRef = useRef<Record<number, ModuleSegment[]>>({});

  // Expose screenshot capture function to parent via callback
  useEffect(() => {
    if (onScreenshotCapture) {
      const captureFunc = async () => {
        const { captureMapScreenshot, getInstallationBoundsFromSegments } = await import('../utils/export/mapScreenshotUtils');
        if (!leafletMapRef.current) {
          return { success: false, error: 'Map not available' };
        }
        const frameBounds = getInstallationBoundsFromSegments(moduleSegmentsRef.current);
        const opts = frameBounds
          ? { frameBounds, zoomOutPercent: 20 }
          : {};
        return captureMapScreenshot(leafletMapRef.current, opts);
      };
      onScreenshotCapture(captureFunc);
    }
  }, [onScreenshotCapture]);

  // NOTE: tolerance, angleThreshold, hasInitialFit are now in useLoadingState hook
  // NOTE: selectedPolygonForEdit, editingPolygonVertex, editedPolygonCoords, etc. are in usePolygonState hook
  // NOTE: isEditing, editedCoordinates, isDrawingRoof, roofDrawingVertices are in useRoofDrawing hook

  // ========================================
  // Obstruction State (consolidated via useObstructionState hook)
  // ========================================
  const {
    obstructions, setObstructions,
    isDrawingObstruction, setIsDrawingObstruction,
    obstructionDrawMode, setObstructionDrawMode,
    dragPreviewRect, setDragPreviewRect,
    selectedObstruction, setSelectedObstruction,
    editingObstruction, setEditingObstruction,
    draggingObstruction, setDraggingObstruction,
    obstructionDrawingVertices, setObstructionDrawingVertices,
    circleObstructionCenter, setCircleObstructionCenter,
    circleObstructionRadius, setCircleObstructionRadius,
  } = useObstructionState();

  // ========================================
  // Solar Module and Segment State (consolidated via useModuleState hook)
  // ========================================
  const {
    modules, setModules,
    moduleSegments, setModuleSegments,
    selectedSegment, setSelectedSegment,
    editingSegmentVertices, setEditingSegmentVertices,
    isDrawingSegment, setIsDrawingSegment,
    isFreeFieldMode, setIsFreeFieldMode,
    freeFieldAzimuth, setFreeFieldAzimuth,
    freeFieldPolygonVertices, setFreeFieldPolygonVertices,
    draggingHandle, setDraggingHandle,
    isRotatingSegment, setIsRotatingSegment,
    isResizingSegmentCorner, setIsResizingSegmentCorner,
    isStretchingSegmentEdge, setIsStretchingSegmentEdge,
    isDraggingSegment, setIsDraggingSegment,
    moduleWidth, setModuleWidth,
    moduleHeight, setModuleHeight,
    moduleOrientation, setModuleOrientation,
    modulePowerWatts, setModulePowerWatts,
    moduleSpacing, setModuleSpacing,
    rowSpacingMultiplier, setRowSpacingMultiplier,
    targetModuleCount, setTargetModuleCount,
    targetFillPercentage, setTargetFillPercentage,
    edgeSetback, setEdgeSetback,
    obstructionSetback, setObstructionSetback,
    isPlacingModule, setIsPlacingModule,
    selectedModule, setSelectedModule,
    selectedModules, setSelectedModules,
    previewModules, setPreviewModules,
    isSelectingModules, setIsSelectingModules,
    selectionBoxStart, setSelectionBoxStart,
    selectionBox, setSelectionBox,
    editMode, setEditMode,
  } = useModuleState();

  moduleSegmentsRef.current = moduleSegments;

  // Instance-specific refs for segment manipulation (not part of shared hook state)
  const segmentRotationAngleRef = useRef<number>(0);
  const rotatingSegmentBoundsRef = useRef<[number, number][]>([]);
  const resizingCornerIndexRef = useRef<number>(-1);
  const resizingSegmentBoundsRef = useRef<[number, number][]>([]);
  const stretchingEdgeIndexRef = useRef<number>(-1);
  const stretchingSegmentBoundsRef = useRef<[number, number][]>([]);
  const edgeDragStartPosRef = useRef<[number, number] | null>(null);
  const edgeDragOccurredRef = useRef<boolean>(false); // Track if drag actually happened (to prevent snap on drag release)
  const draggingSegmentRef = useRef<{ polygonId: number; index: number } | null>(null);
  const segmentDragStartPosRef = useRef<[number, number] | null>(null);
  const segmentDragOffsetRef = useRef<[number, number]>([0, 0]);

  // ============================================
  // PANEL SELECTION STATE (consolidated via usePanelSelection hook)
  // ============================================
  const {
    availablePanels, setAvailablePanels,
    selectedPanel, setSelectedPanel,
    panelsLoading, setPanelsLoading,
    selectedBrand, setSelectedBrand,
    availableBrands, setAvailableBrands,
  } = usePanelSelection();

  // ============================================
  // OBSTRUCTION HANDLERS (consolidated via useObstructionHandlers hook)
  // Note: handleDragUpdate kept inline since it needs calculateModulesForBox
  // ============================================
  const obstructionState = {
    obstructions, setObstructions,
    obstructionDrawingVertices, setObstructionDrawingVertices,
    circleObstructionCenter, setCircleObstructionCenter,
    circleObstructionRadius, setCircleObstructionRadius,
    isDrawingObstruction, setIsDrawingObstruction,
    obstructionDrawMode, setObstructionDrawMode,
    dragPreviewRect, setDragPreviewRect,
    selectedObstruction, setSelectedObstruction,
    editingObstruction, setEditingObstruction,
    draggingObstruction, setDraggingObstruction,
  };

  const {
    handleAddObstructionVertex,
    handleFinishPolygonObstruction,
    handleCancelPolygonObstruction,
    handleSetCircleCenter,
    handleSetCircleRadius,
    handleFinishCircleObstruction,
    handleCancelCircleObstruction,
    handleRectangleCreated,
    handleDeleteObstruction,
    handleCopyObstruction,
    handleMoveObstruction,
    handleRotateObstruction,
  } = useObstructionHandlers({
    obstructionState,
    selectedPolygon,
    isFreeFieldMode,
  });

  // ============================================
  // SYNC TARGET MODULE CONFIG FROM CALCULATOR RUN SELECTION
  // When user selects a calculator run in ProjectDataCard, apply the target
  // Only applies in auto-roof mode (not manual mode)
  // ============================================
  useEffect(() => {
    const isAutoRoofMode = loadingMode !== 'manual';
    if (targetModuleConfig && isAutoRoofMode) {
      setTargetModuleCount(targetModuleConfig.targetModuleCount);
      // Optionally also sync modulePowerWatts if needed:
      // setModulePowerWatts(targetModuleConfig.modulePowerWatts);
    }
  }, [targetModuleConfig, loadingMode]);

  // Embed: cuando el padre pasa targetModuleConfig (ej. recomendados), refill solo el polígono seleccionado para aplicar el límite
  useEffect(() => {
    if (!hideControls || targetModuleCount == null || selectedPolygon === null) return;
    const segs = moduleSegments[selectedPolygon];
    if (!segs?.length) return;
    segs.forEach((seg, i) => {
      refillSegment(selectedPolygon, i, seg.bounds);
    });
  }, [hideControls, targetModuleCount, selectedPolygon]);

  // ============================================
  // Reset restoration flag when loadedProjectData OR polygonData changes
  // This ensures restoration re-runs after hide/show cycles
  // ============================================
  useEffect(() => {
    setRestorationComplete(false);
  }, [loadedProjectData, polygonData]);

  // ============================================
  // PROJECT RESTORATION - Restore geometry from loaded project
  // NOTE: We do NOT create polygonData from loadedProjectData
  // Instead, let the normal Solar API pipeline load polygons first,
  // then the restoration useEffect below will overlay the saved geometry
  // ============================================
  useEffect(() => {

    // Use 'manual' mode when loading a project - this ensures GeoTIFF bounds are fetched correctly
    // Manual mode: fetches real GeoTIFF bounds, clears auto-detected polygons
    // Then restoration injects the saved polygons
    if (loadedProjectData && loadingMode !== 'manual') {
      setLoadingMode('manual');

      // Auto-select System mode when loading project (for module view)
      setSiteSystemMode('system');
      setEditMode('system');
    }

    // Skip if already restored this project
    if (restorationComplete) {
      return;
    }

    if (!loadedProjectData) {
      return;
    }

    // Set project ID from loaded project data for screenshot functionality
    if (loadedProjectData.project_id) {
      setCurrentProjectId(loadedProjectData.project_id);
    }

    // Check if this is a free-field only project (no roof polygons)
    // API may return roof_polygon.coordinates[0] or roof_polygon.geometry.coordinates[0]
    const hasRoofPolygonCoords = !!(
      loadedProjectData.roof_polygon?.geometry?.coordinates?.[0]?.length ||
      loadedProjectData.roof_polygon?.coordinates?.[0]?.length
    );
    const isFreeFieldOnly = (
      !hasRoofPolygonCoords &&
      (!loadedProjectData.roof_polygons || loadedProjectData.roof_polygons.length === 0) &&
      (loadedProjectData.segments?.length ?? 0) > 0
    );

    if (isFreeFieldOnly) {

      // For free-field only projects, restore segments and modules (deduplicated)
      if (loadedProjectData.segments && loadedProjectData.segments.length > 0) {
        const { segmentsByRoofId, modulesByRoofId } = restoreSegmentsDeduplicated(
          loadedProjectData.segments,
          FREE_FIELD_POLYGON_ID
        );


        // Set the segments and modules state
        setModuleSegments(prev => ({
          ...prev,
          ...segmentsByRoofId
        }));

        setModules(prev => ({
          ...prev,
          ...modulesByRoofId
        }));
      }

      // Restore obstructions for free-field projects
      if (loadedProjectData.obstructions && loadedProjectData.obstructions.length > 0) {
        const obsMap: Record<number, [number, number][][]> = {};
        loadedProjectData.obstructions.forEach((obs: any) => {
          const obsRoofId = obs.roof_id !== undefined ? obs.roof_id : FREE_FIELD_POLYGON_ID;
          if (!obsMap[obsRoofId]) {
            obsMap[obsRoofId] = [];
          }
          if (obs.geometry?.coordinates?.[0]) {
            obsMap[obsRoofId].push(
              obs.geometry.coordinates[0].map(([lng, lat]: [number, number]) => [lat, lng] as [number, number])
            );
          }
        });
        setObstructions(prev => ({
          ...prev,
          ...obsMap
        }));
      }

      // Restore module config
      if (loadedProjectData.default_module_config) {
        const config = loadedProjectData.default_module_config;
        // Use defaults if values are missing or invalid
        setModuleWidth(config.width && config.width > 0 ? config.width : 1.134);
        setModuleHeight(config.height && config.height > 0 ? config.height : 2.376);
        setModulePowerWatts(config.power_watts && config.power_watts > 0 ? config.power_watts : 625);
        setModuleOrientation(config.orientation || 'portrait');
        setModuleSpacing(config.spacing ?? 0.02);
        setRowSpacingMultiplier(config.row_spacing_multiplier ?? 1.3);
        setEdgeSetback(config.edge_setback ?? 0.02);
        setObstructionSetback(config.obstruction_setback ?? 0.5);

      }

      setRestorationComplete(true);
      return;
    }

    if (!polygonData) {
      return;
    }

    // Wait for polygon pipeline to complete before restoring
    // This ensures we have valid bounds from the GeoTIFF API
    if (polygonStatus !== 'complete') {
      return;
    }

    // Find the best matching polygon using spatial proximity
    // This handles cases where:
    // - User deleted and redrew polygon (different ID)
    // - Solar API returned different polygon IDs
    // - Old projects without original_polygon_id

    // Handle both old format (coordinates directly in roof_polygon) and new format (in geometry)
    const savedPolygonCoords = loadedProjectData.roof_polygon?.geometry?.coordinates?.[0] ||
                                loadedProjectData.roof_polygon?.coordinates?.[0];
    if (!savedPolygonCoords || savedPolygonCoords.length === 0) {
      console.error('   ❌ No polygon coordinates in loaded project data');
      return;
    }

    // Calculate centroid of saved polygon for matching
    const savedCentroid = savedPolygonCoords.reduce(
      (acc: [number, number], coord: [number, number]) => {
        return [acc[0] + coord[0], acc[1] + coord[1]];
      },
      [0, 0] as [number, number]
    );
    savedCentroid[0] /= savedPolygonCoords.length;
    savedCentroid[1] /= savedPolygonCoords.length;


    // Find closest polygon by centroid distance
    let closestPolygon: any = null;
    let minDistance = Infinity;

    polygonData.polygons.forEach((p: any) => {
      const coords = p.geometry.coordinates[0];
      const centroid = coords.reduce(
        (acc: [number, number], coord: [number, number]) => {
          return [acc[0] + coord[0], acc[1] + coord[1]];
        },
        [0, 0] as [number, number]
      );
      centroid[0] /= coords.length;
      centroid[1] /= coords.length;

      // Calculate Euclidean distance
      const dx = centroid[0] - savedCentroid[0];
      const dy = centroid[1] - savedCentroid[1];
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < minDistance) {
        minDistance = distance;
        closestPolygon = p;
      }
    });

    // Handle case when no polygons are available (existing mode clears API polygons)
    // In this case, create a polygon from the loaded project data
    let originalPolygonId: number;
    if (!closestPolygon) {

      // Use saved polygon ID or generate new one
      originalPolygonId = loadedProjectData.roof_polygon?.original_polygon_id ??
                          loadedProjectData.roof_polygon?.id ?? 0;

      // Create polygon from loaded data and add to polygonData
      const newPolygon = {
        id: originalPolygonId,
        geometry: loadedProjectData.roof_polygon?.geometry || {
          type: 'Polygon',
          coordinates: [savedPolygonCoords],
        },
        area_m2: loadedProjectData.roof_polygon?.area_m2 || 0,
      };


      // Use functional update - ONLY add polygon, preserve bounds from fetchPolygons
      // The bounds should come from the GeoTIFF API, not be calculated from polygon
      setPolygonData(prev => {
        return {
          ...prev,
          polygons: [newPolygon],
        };
      });
    } else {
      originalPolygonId = closestPolygon.id;
    }

    // Select the matching polygon if not already selected
    if (selectedPolygon !== originalPolygonId) {
      setSelectedPolygon(originalPolygonId);
      setIsRoofSelectMode(false);
      return; // Will trigger this effect again with correct selectedPolygon
    }


    // 1. Restore edited polygon if available
    if (loadedProjectData.roof_polygon?.geometry?.coordinates) {
      const coords = loadedProjectData.roof_polygon.geometry.coordinates[0];
      // Convert from GeoJSON [lng, lat] to [lat, lng]
      const latLngCoords: [number, number][] = coords.map(([lng, lat]: [number, number]) => [lat, lng]);

      // Use functional update to avoid stale state
      setEditedPolygonCoords(prev => ({
        ...prev,
        [selectedPolygon]: latLngCoords
      }));

      // Also add the polygon to polygonData.polygons if not already there
      // This is needed when loading existing projects where API polygons are cleared
      if (polygonData && !polygonData.polygons.find((p: any) => p.id === selectedPolygon)) {
        const newPolygon = {
          id: selectedPolygon,
          geometry: loadedProjectData.roof_polygon.geometry,
          area_m2: loadedProjectData.roof_polygon.area_m2 || 0,
        };

        // Use functional update - ONLY add polygon, preserve bounds from fetchPolygons
        setPolygonData(prev => {
          return {
            ...prev,
            polygons: [...prev.polygons, newPolygon],
          };
        });
      }

      if (loadedProjectData.roof_polygon.is_edited && selectedPolygon !== null) {
        establishRoof(selectedPolygon);
      }
    }

    // 2. Restore snap edge
    if (loadedProjectData.snap_edge) {
      const snap = loadedProjectData.snap_edge;
      // Use functional updates to avoid stale state
      setSnapEdgeIndex(prev => ({
        ...prev,
        [selectedPolygon]: snap.edge_index
      }));
      setSnapEdgeAzimuth(prev => ({
        ...prev,
        [selectedPolygon]: snap.azimuth
      }));
    }

    // 3. Restore obstructions
    if (loadedProjectData.obstructions && loadedProjectData.obstructions.length > 0) {
      const restoredObstructions = loadedProjectData.obstructions.map((obs: any) => {
        const coords = obs.geometry.coordinates[0];
        // Convert from GeoJSON [lng, lat] to [lat, lng]
        return coords.map(([lng, lat]: [number, number]) => [lat, lng] as [number, number]);
      });

      // Use functional update to avoid stale state
      setObstructions(prev => ({
        ...prev,
        [selectedPolygon]: restoredObstructions
      }));
    }

    // 4. Restore segments AND modules (deduplicated via segmentRestoreUtils)
    if (loadedProjectData.segments && loadedProjectData.segments.length > 0) {
      const { segmentsByRoofId, modulesByRoofId } = restoreSegmentsDeduplicated(
        loadedProjectData.segments,
        selectedPolygon ?? FREE_FIELD_POLYGON_ID
      );


      // Restore segment boundaries - merge with existing state
      setModuleSegments(prev => ({
        ...prev,
        ...segmentsByRoofId
      }));

      // Restore modules - merge with existing state
      setModules(prev => ({
        ...prev,
        ...modulesByRoofId
      }));
    }

    // Final check: Warn if bounds are missing (they should come from fetchPolygons)
    if (!polygonData.bounds) {
      console.error('  ❌ No bounds after restoration! GeoTIFF will not render correctly.');
      console.error('     This usually means fetchPolygons did not complete or cache_id is missing.');
    } else {
    }

    setRestorationComplete(true);
  }, [loadedProjectData, selectedPolygon, polygonData, polygonStatus, restorationComplete]);

  // ============================================
  // METRICS CALLBACK - Send metrics to parent
  // ============================================
  useEffect(() => {
    if (!onMetricsUpdate) return;

    // Calculate metrics for selected polygon
    if (selectedPolygon !== null && polygonData) {
      const poly = polygonData.polygons.find((p: any) => p.id === selectedPolygon);
      if (!poly) return;

      // Use edited coords if available
      const polygonCoords: [number, number][] = editedPolygonCoords[selectedPolygon] ||
        poly.geometry.coordinates[0].map(
          ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
        );

      const roofArea = calculatePolygonArea(polygonCoords);
      const obstructionArea = (obstructions[selectedPolygon] || []).reduce((sum: number, obs: any) => {
        // Handle both array format (coordinates array) and object format (with coordinates property)
        const coords = Array.isArray(obs) ? obs : (obs?.coordinates || []);
        return sum + (coords.length > 0 ? calculatePolygonArea(coords) : 0);
      }, 0);
      const netArea = roofArea - obstructionArea;

      // Include both roof and free-field modules in totals
      const roofModuleCount = (modules[selectedPolygon] || []).length;
      const freeFieldModuleCount = (modules[FREE_FIELD_POLYGON_ID] || []).length;
      const moduleCount = roofModuleCount + freeFieldModuleCount;

      // Calculate free-field segment area
      const freeFieldSegmentArea = (moduleSegments[FREE_FIELD_POLYGON_ID] || []).reduce((total, seg) => {
        return total + (seg.bounds?.length >= 3 ? calculatePolygonArea(seg.bounds) : 0);
      }, 0);
      const totalUsableArea = roofArea + freeFieldSegmentArea;

      const totalModuleArea = moduleCount * moduleWidth * moduleHeight;
      const roofCoverage = totalUsableArea > 0 ? (totalModuleArea / totalUsableArea) * 100 : 0;
      const systemCapacity = (moduleCount * modulePowerWatts) / 1000;

      // Get azimuth from snap edge if available
      let moduleAzimuth: number | undefined = undefined;
      if (snapEdgeIndex[selectedPolygon] !== undefined) {
        moduleAzimuth = snapEdgeAzimuth[selectedPolygon];
      }

      // Convert polygon coords to GeoJSON format [lng, lat]
      const geoJsonCoords = polygonCoords.map(([lat, lng]) => [lng, lat]);

      // Extract snap edge data if available
      let snapEdgeData: any = null;
      if (snapEdgeIndex[selectedPolygon] !== undefined) {
        const edgeIdx = snapEdgeIndex[selectedPolygon];
        if (edgeIdx >= 0 && edgeIdx < polygonCoords.length) {
          const start = polygonCoords[edgeIdx];
          const end = polygonCoords[(edgeIdx + 1) % polygonCoords.length];
          snapEdgeData = {
            edge_index: edgeIdx,
            azimuth: snapEdgeAzimuth[selectedPolygon] || 0,
            manually_adjusted: false, // TODO: Track if manually adjusted
            coordinates: {
              start: [start[0], start[1]],
              end: [end[0], end[1]],
            },
          };
        }
      }

      // Extract segments with modules
      const segmentsData = Object.entries(modules)
        .filter(([polygonId]) => parseInt(polygonId) === selectedPolygon)
        .flatMap(([polygonId, mods]: [string, any]) => {
          // Group modules by segment ID
          const segmentMap = new Map<number, any[]>();
          mods.forEach((mod: any) => {
            if (!segmentMap.has(mod.segmentId)) {
              segmentMap.set(mod.segmentId, []);
            }
            segmentMap.get(mod.segmentId)!.push(mod);
          });

          // Convert each segment to proper format
          return Array.from(segmentMap.entries()).map(([segmentId, segmentMods]) => {
            const segmentModuleCount = segmentMods.length;
            const segmentArea = segmentModuleCount * moduleWidth * moduleHeight;
            const segmentCoverage = roofArea > 0 ? (segmentArea / roofArea) * 100 : 0;
            const segmentCapacity = (segmentModuleCount * modulePowerWatts) / 1000;

            // TECH-63: preservar los bounds del segment editados en estado.
            // Antes se guardaba siempre el polygon completo, descartando las
            // ediciones del evaluador. Cae al polygon como fallback si el
            // segment no tiene bounds (ej. compatibilidad con flow viejo).
            const segmentInState = moduleSegments[selectedPolygon]?.find((s: any) => s.id === segmentId);
            const segmentGeoJsonCoords = segmentInState?.bounds
              ? segmentInState.bounds.map(([lat, lng]: [number, number]) => [lng, lat])
              : geoJsonCoords;

            return {
              segment_id: segmentId,
              name: segmentMods[0]?.label || `Segment ${segmentId}`,
              bounds: {
                type: 'Polygon' as const,
                coordinates: [segmentGeoJsonCoords],
              },
              area_m2: roofArea,
              rotation: segmentMods[0]?.azimuth || moduleAzimuth || 0,
              rotation_source: (snapEdgeIndex[selectedPolygon] !== undefined ? 'snap_edge' : 'auto') as 'snap_edge' | 'auto' | 'manual',
              alignment: 'parallel' as const,
              module_config: {
                width: moduleWidth,
                height: moduleHeight,
                power_watts: modulePowerWatts,
                orientation: (moduleWidth < moduleHeight ? 'portrait' : 'landscape') as 'portrait' | 'landscape',
                spacing: 0.02,
                row_spacing_multiplier: 1.3,
                edge_setback: 0.3,
                obstruction_setback: 0.5,
              },
              modules: segmentMods
                .filter((mod: any) => Array.isArray(mod.position) && mod.position.length === 2 &&
                  typeof mod.position[0] === 'number' && typeof mod.position[1] === 'number')
                .map((mod: any, modIdx: number) => ({
                  module_id: `mod_${selectedPolygon}_${segmentId}_${modIdx}`,
                  position: [mod.position[0], mod.position[1]] as [number, number],
                  width: mod.width || moduleWidth,
                  height: mod.height || moduleHeight,
                  orientation: mod.orientation || ((mod.width || moduleWidth) < (mod.height || moduleHeight) ? 'portrait' : 'landscape') as 'portrait' | 'landscape',
                  rotation: mod.rotation || mod.azimuth || 0,
                  row_id: 0, // TODO: Calculate row ID if needed
                  power_watts: mod.modulePowerWatts || modulePowerWatts,
                })),
              metrics: {
                module_count: segmentModuleCount,
                total_area_m2: segmentArea,
                coverage_percent: segmentCoverage,
                capacity_kw: segmentCapacity,
              },
            };
          });
        });

      // Extract obstructions
      const obstructionsData = (obstructions[selectedPolygon] || [])
        .filter((obs: any) => {
          // Ensure obstruction has valid coordinates
          const obsCoords = Array.isArray(obs) ? obs : (obs?.coordinates || []);
          return obsCoords.length > 0 && obsCoords.every((coord: any) =>
            Array.isArray(coord) && coord.length >= 2 &&
            typeof coord[0] === 'number' && typeof coord[1] === 'number'
          );
        })
        .map((obs: any, idx: number) => {
          const obsCoords = Array.isArray(obs) ? obs : (obs?.coordinates || []);
          const obsGeoJson = obsCoords.map(([lat, lng]: [number, number]) => [lng, lat]);
          const obsArea = obsCoords.length > 0 ? calculatePolygonArea(obsCoords) : 0;

          return {
            obstruction_id: obs.id || `obs_${selectedPolygon}_${idx}`,
            type: (obs.type || 'custom') as 'chimney' | 'vent' | 'skylight' | 'custom',
            geometry: {
              type: 'Polygon' as const,
              coordinates: [obsGeoJson],
            },
            area_m2: obsArea,
            rotation: obs.rotation || 0,
            notes: obs.notes || '',
          };
        });

      // Also build free-field segments for combined save
      const freeFieldSegs = moduleSegments[FREE_FIELD_POLYGON_ID] || [];
      const freeFieldSegmentsForSave = freeFieldSegs.map((seg, segIdx) => {
        const segBounds = seg.bounds || [];
        const segMods = seg.modules || [];
        const segArea = segBounds.length >= 3 ? calculatePolygonArea(segBounds) : 0;
        const segModArea = segMods.length * moduleWidth * moduleHeight;
        const geoJsonBnds = segBounds.map(([lat, lng]: [number, number]) => [lng, lat]);

        return {
          segment_id: seg.id || segIdx,
          name: `Field Segment ${segIdx + 1}`,
          snap_edge_roof_id: FREE_FIELD_POLYGON_ID,
          bounds: { type: 'Polygon' as const, coordinates: [geoJsonBnds] },
          area_m2: segArea,
          rotation: seg.rotation || 0,
          rotation_source: 'auto' as const,
          alignment: 'parallel' as const,
          module_config: {
            width: moduleWidth, height: moduleHeight, power_watts: modulePowerWatts,
            orientation: (moduleWidth < moduleHeight ? 'portrait' : 'landscape') as 'portrait' | 'landscape',
            spacing: 0.02, row_spacing_multiplier: 1.3, edge_setback: 0.3, obstruction_setback: 0.5,
          },
          modules: segMods
            .filter((mod: any) => Array.isArray(mod.position) && mod.position.length === 2 &&
              typeof mod.position[0] === 'number' && typeof mod.position[1] === 'number')
            .map((mod: any, modIdx: number) => ({
              module_id: `mod_freefield_${seg.id || segIdx}_${modIdx}`,
              position: [mod.position[0], mod.position[1]] as [number, number],
              width: mod.width || moduleWidth, height: mod.height || moduleHeight,
              orientation: mod.orientation || ((mod.width || moduleWidth) < (mod.height || moduleHeight) ? 'portrait' : 'landscape') as 'portrait' | 'landscape',
              rotation: mod.rotation || mod.azimuth || 0, row_id: 0, power_watts: mod.modulePowerWatts || modulePowerWatts,
            })),
          metrics: {
            module_count: segMods.length, total_area_m2: segModArea,
            coverage_percent: segArea > 0 ? (segModArea / segArea) * 100 : 0,
            capacity_kw: (segMods.length * modulePowerWatts) / 1000,
          },
        };
      });

      // Build free-field obstructions
      const freeFieldObsForSave = (obstructions[FREE_FIELD_POLYGON_ID] || [])
        .filter((obs: any) => {
          const oc = Array.isArray(obs) ? obs : (obs?.coordinates || []);
          return oc.length > 0 && oc.every((c: any) => Array.isArray(c) && c.length >= 2);
        })
        .map((obs: any, idx: number) => {
          const oc = Array.isArray(obs) ? obs : (obs?.coordinates || []);
          const og = oc.map(([lat, lng]: [number, number]) => [lng, lat]);
          const oa = oc.length > 0 ? calculatePolygonArea(oc) : 0;
          return {
            obstruction_id: obs.id || `obs_freefield_${idx}`, roof_id: FREE_FIELD_POLYGON_ID,
            type: (obs.type || 'custom') as 'chimney' | 'vent' | 'skylight' | 'custom',
            geometry: { type: 'Polygon' as const, coordinates: [og] },
            area_m2: oa, rotation: obs.rotation || 0, notes: obs.notes || '',
          };
        });

      // Combine roof segments with free-field segments
      const allSegments = [...segmentsData, ...freeFieldSegmentsForSave];
      const allObstructions = [...obstructionsData, ...freeFieldObsForSave];


      onMetricsUpdate({
        // Original metrics (now includes free-field)
        roofArea: totalUsableArea,
        obstructionArea,
        netArea: totalUsableArea - obstructionArea,
        moduleCount,
        totalModuleArea,
        roofCoverage,
        systemCapacity,
        modulePower: modulePowerWatts,
        moduleModel: selectedPanel?.model,
        moduleBrand: selectedPanel?.brand || selectedBrand,
        moduleAzimuth,

        // Free-field breakdown
        freeFieldModuleCount: freeFieldModuleCount > 0 ? freeFieldModuleCount : undefined,
        freeFieldSegmentArea: freeFieldSegmentArea > 0 ? freeFieldSegmentArea : undefined,

        // NEW: Polygon geometry
        selectedPolygonId: selectedPolygon,
        polygonGeometry: {
          type: 'Polygon',
          coordinates: [geoJsonCoords],
        },
        isEdited: !!editedPolygonCoords[selectedPolygon],
        polygonArea: roofArea,
        polygonPerimeter: 0, // TODO: Calculate perimeter if needed

        // NEW: Segments with modules (COMBINED roof + free-field)
        segments: allSegments,

        // NEW: Snap edge
        snapEdge: snapEdgeData,

        // NEW: Obstructions (COMBINED roof + free-field)
        obstructions: allObstructions,
      });
    } else {
      // No polygon selected - but might have free-field data
      // Check both modules dictionary AND modules within segments
      const freeFieldSegmentsArray = moduleSegments[FREE_FIELD_POLYGON_ID] || [];
      const modulesInDictionary = (modules[FREE_FIELD_POLYGON_ID] || []).length;
      const modulesInSegments = freeFieldSegmentsArray.reduce((total, seg) => total + (seg.modules?.length || 0), 0);
      const freeFieldModuleCount = modulesInDictionary > 0 ? modulesInDictionary : modulesInSegments;

      const freeFieldSegmentArea = freeFieldSegmentsArray.reduce((total, seg) => {
        return total + (seg.bounds?.length >= 3 ? calculatePolygonArea(seg.bounds) : 0);
      }, 0);
      const freeFieldObstructionArea = (obstructions[FREE_FIELD_POLYGON_ID] || []).reduce((sum: number, obs: any) => {
        const coords = Array.isArray(obs) ? obs : (obs?.coordinates || []);
        return sum + (coords.length > 0 ? calculatePolygonArea(coords) : 0);
      }, 0);


      if (freeFieldModuleCount > 0 || freeFieldSegmentArea > 0 || freeFieldSegmentsArray.length > 0) {
        // Send free-field metrics
        const totalModuleArea = freeFieldModuleCount * moduleWidth * moduleHeight;
        const netArea = freeFieldSegmentArea - freeFieldObstructionArea;

        // Build free-field segments data for saving
        const freeFieldSegmentsData = (moduleSegments[FREE_FIELD_POLYGON_ID] || []).map((seg, segIdx) => {
          const segmentBounds = seg.bounds || [];
          const segmentModules = seg.modules || [];
          const segmentArea = segmentBounds.length >= 3 ? calculatePolygonArea(segmentBounds) : 0;
          const segmentModuleArea = segmentModules.length * moduleWidth * moduleHeight;
          const geoJsonBounds = segmentBounds.map(([lat, lng]: [number, number]) => [lng, lat]);

          return {
            segment_id: seg.id || segIdx,
            name: `Field Segment ${segIdx + 1}`,
            snap_edge_roof_id: FREE_FIELD_POLYGON_ID,
            bounds: {
              type: 'Polygon' as const,
              coordinates: [geoJsonBounds],
            },
            area_m2: segmentArea,
            rotation: seg.rotation || 0,
            rotation_source: 'auto' as const,
            alignment: 'parallel' as const,
            module_config: {
              width: moduleWidth,
              height: moduleHeight,
              power_watts: modulePowerWatts,
              orientation: (moduleWidth < moduleHeight ? 'portrait' : 'landscape') as 'portrait' | 'landscape',
              spacing: 0.02,
              row_spacing_multiplier: 1.3,
              edge_setback: 0.3,
              obstruction_setback: 0.5,
            },
            modules: segmentModules
              .filter((mod: any) => Array.isArray(mod.position) && mod.position.length === 2 &&
                typeof mod.position[0] === 'number' && typeof mod.position[1] === 'number')
              .map((mod: any, modIdx: number) => ({
                module_id: `mod_freefield_${seg.id || segIdx}_${modIdx}`,
                position: [mod.position[0], mod.position[1]] as [number, number],
                width: mod.width || moduleWidth,
                height: mod.height || moduleHeight,
                orientation: mod.orientation || ((mod.width || moduleWidth) < (mod.height || moduleHeight) ? 'portrait' : 'landscape') as 'portrait' | 'landscape',
                rotation: mod.rotation || mod.azimuth || 0,
                row_id: 0,
                power_watts: mod.modulePowerWatts || modulePowerWatts,
              })),
            metrics: {
              module_count: segmentModules.length,
              total_area_m2: segmentModuleArea,
              coverage_percent: segmentArea > 0 ? (segmentModuleArea / segmentArea) * 100 : 0,
              capacity_kw: (segmentModules.length * modulePowerWatts) / 1000,
            },
          };
        });

        // Build free-field obstructions data for saving
        const freeFieldObstructionsData = (obstructions[FREE_FIELD_POLYGON_ID] || [])
          .filter((obs: any) => {
            const obsCoords = Array.isArray(obs) ? obs : (obs?.coordinates || []);
            return obsCoords.length > 0 && obsCoords.every((coord: any) =>
              Array.isArray(coord) && coord.length >= 2 &&
              typeof coord[0] === 'number' && typeof coord[1] === 'number'
            );
          })
          .map((obs: any, idx: number) => {
            const obsCoords = Array.isArray(obs) ? obs : (obs?.coordinates || []);
            const obsGeoJson = obsCoords.map(([lat, lng]: [number, number]) => [lng, lat]);
            const obsArea = obsCoords.length > 0 ? calculatePolygonArea(obsCoords) : 0;

            return {
              obstruction_id: obs.id || `obs_freefield_${idx}`,
              roof_id: FREE_FIELD_POLYGON_ID,
              type: (obs.type || 'custom') as 'chimney' | 'vent' | 'skylight' | 'custom',
              geometry: {
                type: 'Polygon' as const,
                coordinates: [obsGeoJson],
              },
              area_m2: obsArea,
              rotation: obs.rotation || 0,
              notes: obs.notes || '',
            };
          });


        onMetricsUpdate({
          roofArea: freeFieldSegmentArea,
          obstructionArea: freeFieldObstructionArea,
          netArea: netArea > 0 ? netArea : freeFieldSegmentArea,
          moduleCount: freeFieldModuleCount,
          totalModuleArea,
          roofCoverage: freeFieldSegmentArea > 0 ? (totalModuleArea / freeFieldSegmentArea) * 100 : 0,
          systemCapacity: (freeFieldModuleCount * modulePowerWatts) / 1000,
          modulePower: modulePowerWatts,
          moduleModel: selectedPanel?.model,
          moduleBrand: selectedPanel?.brand || selectedBrand,
          freeFieldModuleCount,
          freeFieldSegmentArea,
          // NEW: Include segments and obstructions for saving
          segments: freeFieldSegmentsData,
          obstructions: freeFieldObstructionsData,
        });
      } else {
        onMetricsUpdate({});
      }
    }
  }, [
    selectedPolygon,
    modules,
    moduleSegments,
    obstructions,
    moduleWidth,
    moduleHeight,
    modulePowerWatts,
    selectedPanel,
    selectedBrand,
    snapEdgeIndex,
    snapEdgeAzimuth,
    polygonData,
    editedPolygonCoords,
    onMetricsUpdate,
  ]);

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  // Create Google Maps-style red pointer icon for pin marker using the Map_Pin.svg
  const createRedPointerIcon = () => {
    return L.icon({
      iconUrl: '/Map_Pin.svg',
      iconSize: [40, 48], // Width and height of the icon
      iconAnchor: [20, 48], // Point of the icon which will correspond to marker's location (bottom center)
      popupAnchor: [0, -48], // Point from which the popup should open relative to the iconAnchor
    });
  };

  // Calculate distance between two lat/lng points using Haversine formula (returns meters)
  const calculateDistance = (point1: [number, number], point2: [number, number]): number => {
    const R = 6371000; // Earth's radius in meters
    const lat1 = point1[0] * Math.PI / 180;
    const lat2 = point2[0] * Math.PI / 180;
    const deltaLat = (point2[0] - point1[0]) * Math.PI / 180;
    const deltaLng = (point2[1] - point1[1]) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  // Calculate total distance for ruler
  const calculateTotalDistance = (points: [number, number][]): number => {
    if (points.length < 2) return 0;
    let total = 0;
    for (let i = 0; i < points.length - 1; i++) {
      total += calculateDistance(points[i], points[i + 1]);
    }
    return total;
  };

  // Format distance for display
  const formatDistance = (meters: number): string => {
    if (meters < 1) {
      return `${(meters * 100).toFixed(1)} cm`;
    } else if (meters < 1000) {
      return `${meters.toFixed(2)} m`;
    } else {
      return `${(meters / 1000).toFixed(3)} km`;
    }
  };

  // ============================================
  // PROJECT PERSISTENCE STATE (consolidated via useProjectState hook)
  // ============================================
  const {
    currentProjectId, setCurrentProjectId,
    isSaving, setIsSaving,
    lastSaved, setLastSaved,
    projectStatus, setProjectStatus,
    geotiffCacheId, setGeotiffCacheId,
    buildingInsightsId, setBuildingInsightsId,
    currentRPU, setCurrentRPU,
    currentAddress, setCurrentAddress,
  } = useProjectState();

  const layers = [
    { id: 'rgb', name: 'RGB Imagery', color: 'blue' },
    { id: 'flux', name: 'Solar Flux', color: 'orange' },
    { id: 'dsm', name: 'Surface Model (DSM)', color: 'green' },
  ];

  // Fetch polygons on mount and when simplification parameters or cacheId changes
  useEffect(() => {

    // Skip fetch if polygonData already exists (avoids unnecessary re-fetches)
    // Cases to skip:
    // 1. Has polygons loaded (auto mode completed)
    // 2. Manual mode with bounds already fetched
    //
    // Exceptions that ALWAYS fetch:
    // - 'existing' mode with cacheId but missing/invalid bounds (need real GeoTIFF bounds)
    // - 'new' mode with empty polygons (switching from manual to auto mode)
    const hasPolygonsLoaded = polygonData && polygonData.polygons?.length > 0;
    const needsAutoRoofFetch = loadingMode === 'new' && polygonData && polygonData.polygons?.length === 0;

    // Check if we have REAL bounds (not mock bounds which are very small ~0.0002)
    const hasRealBounds = polygonData?.bounds &&
      (polygonData.bounds.north - polygonData.bounds.south) > 0.001; // Real GeoTIFF bounds are ~0.002

    // In manual mode, skip only if we have real bounds already
    // This allows re-fetch when loading projects (mock bounds get replaced with real ones)
    const hasBoundsInManualMode = loadingMode === 'manual' && hasRealBounds;

    if (hasPolygonsLoaded && !needsAutoRoofFetch) {
      return;
    }

    if (hasBoundsInManualMode) {
      return;
    }

    if (needsAutoRoofFetch) {
    }

    // Allow fetch if we have cacheId (for free-field projects without buildingInsights)
    // OR if we have buildingInsights center (normal flow)
    if (!buildingInsights?.center && !cacheId) {
      return;
    }


    if (loadingMode === 'manual') {
    }

    // Fetch polygons in both 'new' and 'manual' modes
    // In manual mode, we need the real GeoTIFF bounds for correct ImageOverlay scaling
    if (cacheId) {
      if (loadingMode === 'manual') {
      } else {
      }
      fetchPolygons();
    } else if (buildingInsights?.center) {
      // Fallback: Mock polygon data if no cache_id but have buildingInsights
      const mockData = {
        success: true,
        bounds: {
          south: buildingInsights.center.latitude - 0.0001,
          north: buildingInsights.center.latitude + 0.0001,
          west: buildingInsights.center.longitude - 0.0001,
          east: buildingInsights.center.longitude + 0.0001,
        },
        center: {
          lat: buildingInsights.center.latitude,
          lng: buildingInsights.center.longitude,
        },
        polygons: [],
      };
      setPolygonData(mockData);
      setOriginalPolygonData(JSON.parse(JSON.stringify(mockData))); // Deep clone for reset
      setPolygonStatus('complete');
      setLoading(false);
    }
  }, [tolerance, angleThreshold, cacheId, buildingInsights, polygonData, loadingMode, bypassPolygonFetch]);

  // Fetch brands and default TW Solar panels on mount
  useEffect(() => {
    fetchBrands();
    fetchPanelsForBrand('TW Solar');
  }, []);

  // Combined keyboard shortcuts: Delete/Backspace, Escape, Enter
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip keyboard shortcuts when user is typing in an input or textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) {
        return;
      }

      // ========== DELETE / BACKSPACE ==========
      // Only handle delete in System mode
      if ((e.key === 'Delete' || e.key === 'Backspace') && siteSystemMode === 'system') {
        // Prevent default backspace behavior (browser back)
        e.preventDefault();

        // Priority 1: Delete selected segment
        if (selectedSegment) {
          const { polygonId, index } = selectedSegment;

          const segments = moduleSegments[polygonId] || [];
          const segmentToDelete = segments[index];

          if (segmentToDelete) {
            // Remove all modules belonging to this segment
            const polygonModules = modules[polygonId] || [];
            const remainingModules = polygonModules.filter(m => m.segmentId !== segmentToDelete.id);

            // Remove the segment
            const remainingSegments = segments.filter((_, i) => i !== index);

            setModules({
              ...modules,
              [polygonId]: remainingModules,
            });

            setModuleSegments({
              ...moduleSegments,
              [polygonId]: remainingSegments,
            });

            setSelectedSegment(null);
          }
          return;
        }

        // Priority 2: Handle multi-selection delete (modules)
        if (selectedModules.size > 0 && selectedPolygon !== null) {
          const polygonModules = modules[selectedPolygon] || [];
          // Filter out selected modules
          const remainingModules = polygonModules.filter((_, index) => !selectedModules.has(index));

          setModules({
            ...modules,
            [selectedPolygon]: remainingModules,
          });

          // Clear multi-selection
          setSelectedModules(new Set());
        }
        // Priority 3: Handle single selection delete (module)
        else if (selectedModule) {
          const { polygonId, index } = selectedModule;
          const polygonModules = modules[polygonId] || [];
          const remainingModules = polygonModules.filter((_, i) => i !== index);

          setModules({
            ...modules,
            [polygonId]: remainingModules,
          });

          // Clear single selection
          setSelectedModule(null);
        }
        return;
      }

      // ========== ESCAPE ==========
      // Clear selections and exit modes
      if (e.key === 'Escape') {
        // Exit roof select mode
        if (isRoofSelectMode) {
          setIsRoofSelectMode(false);
          return;
        }

        // Exit edge selection mode
        if (isSelectingSnapEdge) {
          setIsSelectingSnapEdge(false);
          return;
        }

        // Exit obstruction drawing mode
        if (isDrawingObstruction) {
          setIsDrawingObstruction(false);
          return;
        }

        // Exit obstruction edit mode
        if (editingObstruction !== null) {
          setEditingObstruction(null);
          return;
        }

        // Clear obstruction selection
        if (selectedObstruction !== null) {
          setSelectedObstruction(null);
          return;
        }

        // Exit module placement modes
        if (isPlacingModule) {
          setIsPlacingModule(false);
          return;
        }

        if (isDrawingSegment) {
          setIsDrawingSegment(false);
          setPreviewModules([]);
          setFreeFieldPolygonVertices([]); // Clear free-field polygon vertices
          return;
        }

        // Clear module selections
        if (selectedModules.size > 0) {
          setSelectedModules(new Set());
          return;
        }

        if (selectedModule) {
          setSelectedModule(null);
          return;
        }

        // Exit segment vertex edit mode
        if (editingSegmentVertices) {
          setEditingSegmentVertices(null);
          setRemoveVertexMode(false);
          setAddVertexMode(false);
          return;
        }

        // Clear segment selection
        if (selectedSegment) {
          setSelectedSegment(null);
          return;
        }

        // Exit polygon edit modes
        if (editingPolygonVertex !== null) {
          setEditingPolygonVertex(null);
          return;
        }

        if (selectedPolygonForEdit !== null) {
          setSelectedPolygonForEdit(null);
          return;
        }

        // DO NOT clear roof polygon selection - roof stays selected
        // ESC key is only for clearing obstructions, modules, and edit modes
        return;
      }

      // ========== ENTER ==========
      // Save/accept changes
      if (e.key === 'Enter') {
        // Exit edge selection mode
        if (isSelectingSnapEdge) {
          setIsSelectingSnapEdge(false);
          return;
        }

        // Exit obstruction drawing mode
        if (isDrawingObstruction) {
          setIsDrawingObstruction(false);
          return;
        }

        // Exit module placement mode
        if (isPlacingModule) {
          setIsPlacingModule(false);
          return;
        }

        // Complete segment drawing
        if (isDrawingSegment) {
          // For free-field mode, finish the polygon if we have enough vertices
          if (isFreeFieldMode && freeFieldPolygonVertices.length >= 3) {
            handleFreeFieldFinish();
          } else {
            setIsDrawingSegment(false);
            setPreviewModules([]);
            setFreeFieldPolygonVertices([]);
          }
          return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    // Mode states
    siteSystemMode,
    isRoofSelectMode,
    isSelectingSnapEdge,
    isDrawingObstruction,
    isPlacingModule,
    isDrawingSegment,
    isFreeFieldMode,
    // Selection states
    selectedModules,
    selectedModule,
    selectedPolygon,
    selectedSegment,
    selectedObstruction,
    // Edit states
    editingPolygonVertex,
    editingSegmentVertices,
    selectedPolygonForEdit,
    editingObstruction,
    // Data states
    modules,
    moduleSegments,
    freeFieldPolygonVertices,
  ]);

  // Debug: Monitor segment state changes
  useEffect(() => {
    if (selectedPolygon !== null) {
      const segments = moduleSegments[selectedPolygon] || [];
      segments.forEach((seg, idx) => {
      });
    }
  }, [moduleSegments, selectedPolygon]);

  // Debug: Monitor modules state changes
  useEffect(() => {
    if (selectedPolygon !== null) {
      const mods = modules[selectedPolygon] || [];
      const withSegmentId = mods.filter(m => m.segmentId !== undefined);
    }
  }, [modules, selectedPolygon]);

  // ============================================
  // PANEL SELECTION FUNCTIONS
  // ============================================

  // Fetch available brands
  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/api/panels/brands`);
      const data = await response.json();
      if (data.success) {
        setAvailableBrands(data.brands);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  // Fetch panels for selected brand
  const fetchPanelsForBrand = async (brand: string) => {
    setPanelsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/panels?brand=${encodeURIComponent(brand)}`);
      const data = await response.json();

      if (data.success) {
        setAvailablePanels(data.panels);

        // Auto-select TW Solar TWMNH-66HD625 (625W) if that's the brand
        if (brand === 'TW Solar') {
          const twSolar625 = data.panels.find((p: any) =>
            (p.power_watts === 625 || p.power_watts === '625') &&
            p.model.includes('TWMNH-66HD625')
          );

          if (twSolar625) {
            setSelectedPanel(twSolar625);
            applyPanelSpecs(twSolar625);
          } else if (data.panels.length > 0) {
            setSelectedPanel(data.panels[0]);
            applyPanelSpecs(data.panels[0]);
          }
        } else if (data.panels.length > 0) {
          // For other brands, select first panel
          setSelectedPanel(data.panels[0]);
          applyPanelSpecs(data.panels[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching panels:', error);
    } finally {
      setPanelsLoading(false);
    }
  };

  // Apply panel specs to module configuration
  const applyPanelSpecs = (panel: any) => {
    if (!panel) return;

    // Convert mm to meters (handle string or number values)
    // Note: Database has width/height swapped, so we swap them back for portrait orientation
    // Database stores: width_mm = 2382 (long side), height_mm = 1134 (short side)
    // For portrait: width = 1.134m (short), height = 2.382m (long)
    const widthM = parseFloat(panel.height_mm) / 1000;  // Use height_mm as width (short side)
    const heightM = parseFloat(panel.width_mm) / 1000;  // Use width_mm as height (long side)
    const powerW = parseInt(panel.power_watts);

    setModuleWidth(widthM);
    setModuleHeight(heightM);
    setModulePowerWatts(powerW);

  };

  // Handle brand selection change
  const handleBrandChange = (brand: string) => {
    setSelectedBrand(brand);
    setSelectedPanel(null);
    setAvailablePanels([]);
    fetchPanelsForBrand(brand);
  };

  // Handle panel selection change
  const handlePanelChange = (panelModel: string) => {
    const panel = availablePanels.find((p: any) => p.model === panelModel);
    if (panel) {
      setSelectedPanel(panel);
      applyPanelSpecs(panel);
    }
  };

  // ============================================
  // PROJECT PERSISTENCE FUNCTIONS
  // ============================================

  // Helper to build state input for persistence builders
  const getProjectStateInput = (): ProjectStateInput => ({
    currentProjectId,
    currentRPU,
    currentAddress,
    pinPosition,
    geotiffCacheId,
    buildingInsightsId,
    polygonData,
    selectedPolygon,
    editedPolygonCoords,
    establishedRoofIds,
    activeRoofId,
    snapEdgeIndex,
    snapEdgeAzimuth,
    manualAzimuth,
    obstructions,
    modules,
    moduleSegments,
    moduleWidth,
    moduleHeight,
    modulePowerWatts,
    moduleOrientation,
    moduleSpacing,
    rowSpacingMultiplier,
    edgeSetback,
    obstructionSetback,
    selectedPanel,
    projectStatus,
    calculatePolygonArea,
  });

  const saveProject = async () => {
    if (!selectedPolygon || !polygonData) {
      alert('Please select a roof polygon first');
      return;
    }

    setIsSaving(true);

    try {
      // Use builder to create project data
      const projectData = buildSingleRoofProjectData(getProjectStateInput());
      if (!projectData) {
        throw new Error('Failed to build project data - polygon not found');
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(projectData)
      });

      const result = await response.json();

      if (result.success) {
        setCurrentProjectId(result.project_id);
        setLastSaved(new Date());
        alert(`✅ Project saved successfully! ID: ${result.project_id}${result.geodata_updated ? '\n(GeoData updated)' : ''}`);
      } else {
        alert(`❌ Save failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Save error:', error);
      alert(`❌ Save failed: ${(error as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Multi-roof V2 save function
  const saveProjectV2MultiRoof = async () => {
    // Check if we have any data to save (roofs OR free-field segments/obstructions)
    const hasFreeFieldSegments = (moduleSegments[FREE_FIELD_POLYGON_ID]?.length || 0) > 0;
    const hasFreeFieldObstructions = (obstructions[FREE_FIELD_POLYGON_ID]?.length || 0) > 0;
    const hasFreeFieldModules = (modules[FREE_FIELD_POLYGON_ID]?.length || 0) > 0;
    const hasRoofs = establishedRoofIds.size > 0;


    if (!hasRoofs && !hasFreeFieldSegments && !hasFreeFieldObstructions && !hasFreeFieldModules) {
      alert('Please establish at least one roof or create free-field segments/obstructions first');
      return;
    }

    setIsSaving(true);

    try {
      // Use builder to create V2 project data
      const stateInput = getProjectStateInput();

      const projectDataV2 = buildMultiRoofProjectDataV2(stateInput, selectedPolygons);
      if (!projectDataV2) {
        throw new Error('Failed to build V2 project data');
      }


      // Use imported saveProjectV2 function
      const { saveProjectV2 } = await import('../services/projectPersistenceService');
      const result = await saveProjectV2(projectDataV2);

      if (result.success) {
        setCurrentProjectId(result.project_id);
        setLastSaved(new Date());
        const { system_metrics } = projectDataV2;

        // Capture and save screenshot after successful save
        try {
          const { captureAndSaveScreenshot, getInstallationBoundsFromSegments } = await import('../utils/export/mapScreenshotUtils');
          if (leafletMapRef.current) {
            const frameBounds = getInstallationBoundsFromSegments(moduleSegmentsRef.current);
            const captureOpts = frameBounds ? { frameBounds, zoomOutPercent: 20 } : {};
            await captureAndSaveScreenshot(
              leafletMapRef.current,
              result.project_id,
              { rpu: currentRPU || undefined, dealId: loadedProjectData?.metadata?.deal_id ?? undefined },
              captureOpts
            );
          }
        } catch (_screenshotError) {
          // Don't fail the save operation if screenshot fails
        }

        alert(`✅ Multi-roof project saved successfully!\n\nProject ID: ${result.project_id}\nRoofs: ${system_metrics.total_roofs}\nModules: ${system_metrics.total_modules}\nCapacity: ${system_metrics.system_capacity_kw.toFixed(2)} kW${result.geodata_updated ? '\n(GeoData updated)' : ''}`);
      } else {
        alert(`❌ Save failed: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Save V2 multi-roof error:', error);
      alert(`❌ Save failed: ${(error as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const loadProject = async (projectId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects/load/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (result.success) {
        const project = result.project;

        // Debug: Log first segment structure if available
        if (project.segments?.length > 0) {
          const firstSeg = project.segments[0];
        }

        // Restore location
        setCurrentRPU(project.rpu);
        setCurrentAddress(project.location.address);

        // Restore cache references
        setGeotiffCacheId(project.cache_references?.geotiff_cache_id || null);
        setBuildingInsightsId(project.cache_references?.building_insights_id || null);

        // Restore panel selection
        if (project.selected_panel) {
          const panel = availablePanels.find((p: any) => p.model === project.selected_panel.model);
          if (panel) {
            setSelectedPanel(panel);
          } else {
            setSelectedPanel(project.selected_panel);
            applyPanelSpecs(project.selected_panel);
          }
        }

        // Check if this is a V2 format project
        const isV2 = project.schema_version === 2;

        if (isV2) {
          // V2 Multi-roof format restoration
          const newEditedCoords: Record<number, [number, number][]> = {};
          const newSnapEdgeIndex: Record<number, number> = {};
          const newManualAzimuth: Record<number, number | null> = {};
          const newObstructions: Record<number, [number, number][][]> = {};
          const newModuleSegments: Record<number, ModuleSegment[]> = {};
          const newModules: Record<number, SolarModule[]> = {};
          const newEstablishedRoofIds = new Set<number>();

          // Restore roof polygons
          if (project.roof_polygons && project.roof_polygons.length > 0) {
            project.roof_polygons.forEach((roof: any) => {
              const roofId = roof.roof_id;
              newEstablishedRoofIds.add(roofId);

              if (roof.is_edited && roof.geometry?.coordinates?.[0]) {
                newEditedCoords[roofId] = roof.geometry.coordinates[0].map(
                  ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
                );
              }

              if (roof.snap_edge) {
                newSnapEdgeIndex[roofId] = roof.snap_edge.edge_index;
                if (roof.snap_edge.manually_adjusted) {
                  newManualAzimuth[roofId] = roof.snap_edge.azimuth;
                }
              }
            });
          }

          // Restore obstructions (including free-field with roof_id === -1)
          if (project.obstructions && project.obstructions.length > 0) {
            project.obstructions.forEach((obs: any) => {
              const roofId = obs.roof_id;
              if (!newObstructions[roofId]) {
                newObstructions[roofId] = [];
              }
              const coords = obs.geometry.coordinates[0].map(
                ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
              );
              newObstructions[roofId].push(coords);
            });
          }

          // Restore segments and modules (including free-field with snap_edge_roof_id === -1)
          if (project.segments && project.segments.length > 0) {
            project.segments.forEach((seg: any) => {
              const roofId = seg.snap_edge_roof_id;
              const isFreeField = roofId === FREE_FIELD_POLYGON_ID;

              // Get bounds from geometry
              const segmentBounds = seg.geometry?.coordinates?.[0] || [];
              const latLngBounds: [number, number][] = segmentBounds.map(
                ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
              );

              // Restore modules for this segment
              const segmentModules: SolarModule[] = (seg.modules || []).map((m: any) => ({
                position: m.position as [number, number],
                width: m.width,
                height: m.height,
                orientation: m.orientation as 'portrait' | 'landscape',
                rotation: m.rotation,
                segmentId: seg.segment_id,
                freeField: isFreeField
              }));

              // Add segment
              if (!newModuleSegments[roofId]) {
                newModuleSegments[roofId] = [];
              }
              newModuleSegments[roofId].push({
                id: seg.segment_id,
                bounds: latLngBounds,
                rotation: seg.rotation || 0,
                modules: segmentModules,
                freeField: isFreeField,
                config: {
                  moduleSpacing: seg.module_config?.spacing || 0.02,
                  rowSpacing: seg.module_config?.row_spacing_multiplier || 1.0,
                  targetCount: null
                }
              });

              // Add modules
              if (!newModules[roofId]) {
                newModules[roofId] = [];
              }
              newModules[roofId].push(...segmentModules);
            });
          }

          // Apply all restored state
          setEditedPolygonCoords(newEditedCoords);
          setSnapEdgeIndex(newSnapEdgeIndex);
          setManualAzimuth(newManualAzimuth);
          setObstructions(newObstructions);
          setModuleSegments(newModuleSegments);
          setModules(newModules);
          setEstablishedRoofIds(newEstablishedRoofIds);

          // Set active roof if available
          if (project.active_roof_id !== null && project.active_roof_id !== undefined) {
            setSelectedPolygon(project.active_roof_id);
          } else if (newEstablishedRoofIds.size > 0) {
            setSelectedPolygon(Array.from(newEstablishedRoofIds)[0]);
          }

        } else {
          // V1 Single-roof format restoration (legacy)
          // Also handles free-field only projects where roof_polygon may be null
          const hasRoofPolygon = project.roof_polygon?.original_polygon_id !== undefined &&
                                  project.roof_polygon?.original_polygon_id !== null;
          const polygonId = hasRoofPolygon ? project.roof_polygon.original_polygon_id : FREE_FIELD_POLYGON_ID;

          if (hasRoofPolygon) {
            setSelectedPolygon(polygonId);

            if (project.roof_polygon.is_edited && project.roof_polygon.geometry?.coordinates?.[0]) {
              setEditedPolygonCoords({
                [polygonId]: project.roof_polygon.geometry.coordinates[0].map(
                  ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
                )
              });
            }

            // Restore snap edge
            if (project.snap_edge) {
              setSnapEdgeIndex({
                [polygonId]: project.snap_edge.edge_index
              });

              if (project.snap_edge.manually_adjusted) {
                setManualAzimuth({
                  [polygonId]: project.snap_edge.azimuth
                });
              }
            }
          } else {
            // Free-field only project - set selectedPolygon to FREE_FIELD_POLYGON_ID
            // so that free-field modules are rendered
            setSelectedPolygon(FREE_FIELD_POLYGON_ID);
          }

          // Restore obstructions (support both roof-based and free-field)
          if (project.obstructions && project.obstructions.length > 0) {
            const obsMap: Record<number, [number, number][][]> = {};
            project.obstructions.forEach((obs: any) => {
              const obsRoofId = obs.roof_id !== undefined ? obs.roof_id : polygonId;
              if (!obsMap[obsRoofId]) {
                obsMap[obsRoofId] = [];
              }
              if (obs.geometry?.coordinates?.[0]) {
                obsMap[obsRoofId].push(
                  obs.geometry.coordinates[0].map(([lng, lat]: [number, number]) => [lat, lng] as [number, number])
                );
              }
            });
            setObstructions(obsMap);
          }

          // Restore segments and modules (deduplicated via segmentRestoreUtils)
          if (project.segments && project.segments.length > 0) {
            const { segmentsByRoofId: segmentMap, modulesByRoofId: moduleMap } =
              restoreSegmentsDeduplicated(project.segments, polygonId);


            setModuleSegments(segmentMap);
            setModules(moduleMap);
          }
        }

        // Restore module config
        const config = project.default_module_config;
        setModuleWidth(config.width);
        setModuleHeight(config.height);
        setModulePowerWatts(config.power_watts);
        setModuleOrientation(config.orientation);
        setModuleSpacing(config.spacing);
        setRowSpacingMultiplier(config.row_spacing_multiplier);
        setEdgeSetback(config.edge_setback);
        setObstructionSetback(config.obstruction_setback);

        // Restore status
        setProjectStatus(project.status);
        setCurrentProjectId(project.project_id);


        const totalModules = project.system_metrics?.total_modules || 0;
        const freeFieldSegmentCount = project.segments?.filter((s: any) => s.snap_edge_roof_id === FREE_FIELD_POLYGON_ID).length || 0;
        const freeFieldObsCount = project.obstructions?.filter((o: any) => o.roof_id === FREE_FIELD_POLYGON_ID).length || 0;

        let loadMessage = `✅ Project loaded successfully! ${totalModules} modules restored.`;
        if (freeFieldSegmentCount > 0 || freeFieldObsCount > 0) {
          loadMessage += `\n(Includes ${freeFieldSegmentCount} free-field segment(s), ${freeFieldObsCount} free-field obstruction(s))`;
        }
        alert(loadMessage);
      } else {
        alert(`❌ Load failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Load error:', error);
      alert(`❌ Load failed: ${(error as Error).message}`);
    }
  };

  const resetProjectState = () => {
    setCurrentProjectId(null);
    setCurrentRPU(null);
    setCurrentAddress('');
    setGeotiffCacheId(null);
    setBuildingInsightsId(null);
    setLastSaved(null);
    setProjectStatus('draft');
    setSelectedPolygon(null);
    setModules({});
    setModuleSegments({});
    setObstructions({});
    setEditedPolygonCoords({});
    setSnapEdgeIndex({});
    setManualAzimuth({});
    alert('✅ Project state reset. Ready for new project.');
  };

  // ==================== POLYGON ORTHOGONALIZATION ====================
  /**
   * Orthogonalize a polygon - make it more square/rectangular
   * Uses Turf.js for simplification and custom logic for angle snapping
   *
   * @param polygonId - ID of polygon to orthogonalize
   * @param options - Orthogonalization options
   */
  const orthogonalizePolygon = async (
    polygonId: number,
    options: {
      angleThreshold?: number;      // Degrees to snap angles (default: 10)
      simplify?: boolean;            // Whether to simplify vertices (default: true)
      tolerance?: number;            // Simplification tolerance in meters (default: 1.0)
      maxVertices?: number;          // Maximum vertices to keep (default: 12)
    } = {}
  ) => {
    const polygon = polygonData?.polygons.find(p => p.id === polygonId);
    if (!polygon) {
      console.error('❌ Polygon not found:', polygonId);
      return;
    }


    const {
      angleThreshold = 10,  // More lenient: 2x original (was 5°)
      simplify = true,
      tolerance = 1.0,      // More lenient: 2x original (was 0.5m)
      maxVertices = 12
    } = options;

    try {
      // Import Turf.js functions
      const turf = await import('@turf/turf');

      // Get coordinates (convert from GeoJSON [lng, lat] to our [lat, lng])
      let coords: [number, number][] = polygon.geometry.coordinates[0].map(
        ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
      );

      const verticesBefore = coords.length - 1; // Exclude closing vertex

      // Step 1: Simplify if requested (reduce vertex count)
      if (simplify) {
        // Convert to Turf polygon ([lng, lat] format)
        const turfPolygon = turf.polygon([[...coords.map(([lat, lng]) => [lng, lat])]]);

        // Simplify using Douglas-Peucker algorithm
        // tolerance in km (convert from meters)
        const toleranceKm = tolerance / 1000;
        const simplified = turf.simplify(turfPolygon, { tolerance: toleranceKm, highQuality: true });

        // Convert back to our format
        coords = simplified.geometry.coordinates[0].map(
          ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
        );

      }

      // Step 2: Snap angles to orthogonal (0°, 90°, 180°, 270°)
      const orthogonalCoords: [number, number][] = [];

      for (let i = 0; i < coords.length - 1; i++) { // Skip closing vertex
        const current = coords[i];
        const next = coords[i + 1];

        // Calculate edge angle
        const dx = next[1] - current[1]; // lng difference
        const dy = next[0] - current[0]; // lat difference
        let angle = Math.atan2(dy, dx) * (180 / Math.PI);

        // Normalize to 0-360
        if (angle < 0) angle += 360;

        // Find nearest 90° increment
        const snapAngles = [0, 90, 180, 270, 360];
        let closestAngle = snapAngles[0];
        let minDiff = Math.abs(angle - snapAngles[0]);

        for (const snapAngle of snapAngles) {
          const diff = Math.abs(angle - snapAngle);
          if (diff < minDiff) {
            minDiff = diff;
            closestAngle = snapAngle;
          }
        }

        // Snap if within threshold
        if (minDiff <= angleThreshold) {
          // Calculate edge length
          const edgeLength = Math.sqrt(dx * dx + dy * dy);

          // Calculate new endpoint based on snapped angle
          const radians = (closestAngle * Math.PI) / 180;
          const newDx = edgeLength * Math.cos(radians);
          const newDy = edgeLength * Math.sin(radians);

          const newNext: [number, number] = [
            current[0] + newDy,
            current[1] + newDx
          ];

          orthogonalCoords.push(current);

          // Update next point for continuity
          if (i < coords.length - 2) {
            coords[i + 1] = newNext;
          }
        } else {
          // Keep original if not snappable
          orthogonalCoords.push(current);
        }
      }

      // Close the polygon
      if (orthogonalCoords.length > 0) {
        orthogonalCoords.push(orthogonalCoords[0]);
      }

      // Step 3: Limit vertex count if needed
      let finalCoords = orthogonalCoords;
      if (finalCoords.length - 1 > maxVertices) {

        // Re-simplify more aggressively
        const turfPolygon = turf.polygon([[...finalCoords.map(([lat, lng]) => [lng, lat])]]);
        const aggressiveSimplified = turf.simplify(turfPolygon, { tolerance: tolerance * 2 / 1000, highQuality: true });

        finalCoords = aggressiveSimplified.geometry.coordinates[0].map(
          ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
        );
      }

      const verticesAfter = finalCoords.length - 1;
      const simplificationRatio = verticesAfter / verticesBefore;


      // Update edited polygon coordinates
      setEditedPolygonCoords(prev => ({
        ...prev,
        [polygonId]: finalCoords
      }));


    } catch (error) {
      console.error('❌ [ORTHOGONALIZATION] Failed:', error);
    }
  };

  const fetchPolygons = async () => {
    // ==================== BYPASS MODE ====================
    // When loading a saved project, use injected polygon data BUT still fetch GeoTIFF bounds
    if (bypassPolygonFetch && injectedPolygonDataProp) {

      // Still need to fetch bounds from GeoTIFF for RGB overlay positioning
      // Use bounds_only=true to skip polygon extraction (fast path)
      let geotiffBounds = null;
      if (cacheId) {
        try {
          const boundsUrl = `${API_URL}/api/geotiff/polygons?cache_id=${cacheId}&bounds_only=true`;
          const boundsResponse = await fetch(boundsUrl);
          const boundsData = await boundsResponse.json();
          if (boundsData.success && boundsData.bounds) {
            geotiffBounds = boundsData.bounds;
          }
        } catch (e) {
          console.warn('⚠️ [BYPASS MODE] Failed to fetch GeoTIFF bounds:', e);
        }
      }

      // Merge injected polygons with fetched bounds
      const mergedData = {
        ...injectedPolygonDataProp,
        bounds: geotiffBounds || injectedPolygonDataProp.bounds,
      };

      setPolygonData(mergedData);
      setOriginalPolygonData(JSON.parse(JSON.stringify(mergedData))); // Deep clone
      setPolygonStatus('complete');
      setLoading(false);

      // Store original coordinates for reset functionality
      const originals: { [polygonId: number]: [number, number][] } = {};
      injectedPolygonDataProp.polygons.forEach((polygon: Polygon) => {
        const coords: [number, number][] = polygon.geometry.coordinates[0].map(
          ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
        );
        originals[polygon.id] = coords;
      });
      setOriginalPolygonCoords(originals);

      // Auto-select the injected polygon (it's the only one)
      if (injectedPolygonDataProp.polygons.length > 0) {
        const polygonId = injectedPolygonDataProp.polygons[0].id;
        setSelectedPolygon(polygonId);
        // DON'T set isRoofEstablished here - let restoration logic handle it
        // This prevents auto-fill from creating a field segment before project data loads
      }

      return; // Skip normal fetch
    }

    // ==================== NORMAL MODE ====================
    setLoading(true);
    setPolygonStatus('fetching');
    try {
      // Build URL with cache_id and multi-cache merge parameters + force re-extraction for holes
      let url = `${API_URL}/api/geotiff/polygons?tolerance=${tolerance}&angle_threshold=${angleThreshold}&force_reextract=true`;

      // Always enable multi-cache merge if we have pinPosition
      if (cacheId && pinPosition) {
        url += `&cache_id=${cacheId}`;
        url += `&merge_overlapping=true`;
        url += `&lat=${pinPosition.lat}`;
        url += `&lng=${pinPosition.lng}`;
      } else if (cacheId) {
        url += `&cache_id=${cacheId}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      // Log multi-cache merge results
      if (data.source === 'multi_cache_merge') {
      }

      if (data.success) {

        // Check if backend selected a different cache (multi-cache smart selection)
        if (data.cache_id && cacheId && data.cache_id !== cacheId) {
          if (onCacheIdUpdate) {
            onCacheIdUpdate(data.cache_id);
          } else {
            console.warn(`   ⚠️  No onCacheIdUpdate callback provided - RGB/DSM/flux may be misaligned!`);
          }
        }

        // MANUAL MODE: Clear polygons but keep correct GeoTIFF bounds
        // This is also used when loading saved projects - restoration will inject saved polygons
        if (loadingMode === 'manual') {
          data.polygons = []; // Clear polygons but keep bounds from GeoTIFF
        }

        setPolygonData(data);
        setOriginalPolygonData(JSON.parse(JSON.stringify(data))); // Deep clone for reset
        setPolygonStatus('complete');

        // Store original coordinates for reset functionality
        const originals: { [polygonId: number]: [number, number][] } = {};
        data.polygons.forEach((polygon: Polygon) => {
          const coords: [number, number][] = polygon.geometry.coordinates[0].map(
            ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
          );
          originals[polygon.id] = coords;
        });
        setOriginalPolygonCoords(originals);

        // Auto-select closest polygon to center ONLY if no project detected or loaded
        // If there's a detected/loaded project, let the restoration logic handle polygon selection
        if (!hasDetectedProject && !loadedProjectData && data.polygons.length > 0 && data.center) {
          const closestId = findClosestPolygon(data.polygons, data.center);
          setSelectedPolygon(closestId);

          // Auto-establish roof on selection
          establishRoof(closestId);

          // Auto-detect best edge for the selected polygon
          const coords: [number, number][] = data.polygons
            .find((p: Polygon) => p.id === closestId)
            ?.geometry.coordinates[0]
            .map(([lng, lat]: [number, number]) => [lat, lng] as [number, number]) || [];

          if (coords.length > 0) {
            const bestEdgeIndex = autoDetectBestEdge(coords);
            setSnapEdgeIndex({ [closestId]: bestEdgeIndex });
            // Calculate and store azimuth
            if (bestEdgeIndex >= 0 && bestEdgeIndex < coords.length - 1) {
              const start = coords[bestEdgeIndex];
              const end = coords[bestEdgeIndex + 1];
              const dLat = end[0] - start[0];
              const dLng = end[1] - start[1];
              const metersPerDegreeLat = 111320;
              const metersPerDegreeLng = 111320 * Math.cos(start[0] * Math.PI / 180);
              const dy = dLat * metersPerDegreeLat;
              const dx = dLng * metersPerDegreeLng;
              let edgeAzimuth = Math.atan2(dx, dy) * (180 / Math.PI);
              edgeAzimuth = (edgeAzimuth + 360) % 360;
              let faceAzimuth = (edgeAzimuth + 90) % 360;
              setSnapEdgeAzimuth({ [closestId]: faceAzimuth });
            }
          } else {
          }
        } else if (hasDetectedProject || loadedProjectData) {
        }
      } else {
        console.error(`❌ [POLYGON PIPELINE] Failed:`, data);
        setPolygonStatus('error');
      }
    } catch (error) {
      console.error('❌ [POLYGON PIPELINE] Error:', error);
      setPolygonStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const findClosestPolygon = (polygons: Polygon[], center: { lat: number; lng: number }) => {
    let closestId = polygons[0].id;
    let minDistance = Infinity;

    polygons.forEach((polygon) => {
      // Calculate centroid of polygon
      const coords = polygon.geometry.coordinates[0];
      let sumLat = 0;
      let sumLng = 0;
      coords.forEach(([lng, lat]: [number, number]) => {
        sumLat += lat;
        sumLng += lng;
      });
      const centroidLat = sumLat / coords.length;
      const centroidLng = sumLng / coords.length;

      // Calculate distance to site center
      const distance = Math.sqrt(
        Math.pow(centroidLat - center.lat, 2) + Math.pow(centroidLng - center.lng, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestId = polygon.id;
      }
    });

    return closestId;
  };

  // TECH-54: auto-select closest polygon ONLY when no polygon is selected yet.
  // The guard `selectedPolygon === null` is what prevents the re-selection on
  // pin drag: the effect still runs when pinPosition changes, but does nothing
  // if a polygon is already active. That preserves the panels the user placed
  // on the previous polygon (they live in moduleSegments[previousId] and only
  // render when selectedPolygon points there).
  // The user must explicitly click another polygon to change the active one.
  useEffect(() => {
    if (
      polygonData &&
      polygonData.polygons.length > 0 &&
      pinPosition &&
      selectedPolygon === null
    ) {
      const closestId = findClosestPolygon(polygonData.polygons, pinPosition);
      setSelectedPolygon(closestId);

      // Auto-establish roof on selection
      establishRoof(closestId);

      // Auto-detect best edge if not already set
      if (snapEdgeIndex[closestId] === undefined) {
        const polygon = polygonData.polygons.find(p => p.id === closestId);
        if (polygon) {
          // Use edited coords if available
          const coords: [number, number][] = editedPolygonCoords[closestId] ||
            polygon.geometry.coordinates[0].map(
              ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
            );
          const bestEdgeIndex = autoDetectBestEdge(coords);
          setSnapEdgeIndex({
            ...snapEdgeIndex,
            [closestId]: bestEdgeIndex,
          });
        }
      }
    }
  }, [pinPosition, polygonData]);

  const handlePolygonClick = (polygonId: number, shiftKey: boolean = false) => {
    // Phase 3: Multi-roof selection with established roof protection
    // Rule: NEVER deselect an established roof by clicking it - only switch active
    // Deselection only via X button in Roof List

    if (selectedPolygons.includes(polygonId)) {
      // Polygon already selected - check if it's established
      const isEstablished = isRoofEstablished(polygonId);

      if (isEstablished) {
        // Established roofs: ALWAYS just switch active, never deselect
        setActiveRoofId(polygonId);
        setSelectedPolygon(polygonId);
      } else {
        // Non-established polygons can be deselected by clicking
        const newSelection = selectedPolygons.filter(id => id !== polygonId);
        setSelectedPolygons(newSelection);

        // If we deselected the active roof, switch to another or clear
        if (activeRoofId === polygonId) {
          setActiveRoofId(newSelection.length > 0 ? newSelection[0] : null);
        }
      }
    } else {
      // Add to selection (works for both modes)
      const newSelection = [...selectedPolygons, polygonId];
      setSelectedPolygons(newSelection);
      setActiveRoofId(polygonId); // Make the newly selected roof active

      // Auto-detect best edge if not already set
      if (snapEdgeIndex[polygonId] === undefined) {
        const coords = getPolygonCoords(polygonId);
        const bestEdgeIndex = autoDetectBestEdge(coords);
        setSnapEdgeIndex({
          ...snapEdgeIndex,
          [polygonId]: bestEdgeIndex,
        });
      }

      // Auto-establish roof on selection
      establishRoof(polygonId);
    }

    // Only enter edit mode if in Site mode
    if (editMode === 'site') {
      setSelectedPolygonForEdit(polygonId);
    } else {
      // In System mode, keep polygon selected but not editable
      setSelectedPolygonForEdit(null);
    }
    setEditingPolygonVertex(null);
  };

  const handlePolygonDoubleClick = (polygonId: number) => {
    // Embed (hideControls): doble clic en polígono entra/sale de edición de nodos sin botón
    if (hideControls) {
      if (editingPolygonVertex === polygonId) {
        setEditingPolygonVertex(null);
        setSelectedPolygonForEdit(null);
        setRemoveVertexMode(false);
        setAddVertexMode(false);
        setSiteSystemMode('system');
        setEditMode('system');
      } else {
        setEditMode('site');
        setSiteSystemMode('site');
        setSelectedPolygonForEdit(selectedPolygon);
        setEditingPolygonVertex(polygonId);
      }
      return;
    }
    // Solo entrar en edición de vértices si estamos en modo Site
    if (editMode === 'site') {
      setEditingPolygonVertex(polygonId);
      setSelectedPolygonForEdit(null);
    }
  };

  const handlePolygonMove = (polygonId: number, newCoords: [number, number][]) => {
    setEditedPolygonCoords({
      ...editedPolygonCoords,
      [polygonId]: newCoords,
    });
  };

  const handleResetPolygon = (polygonId: number) => {
    // Reset to original coordinates
    const updated = { ...editedPolygonCoords };
    delete updated[polygonId];
    setEditedPolygonCoords(updated);
    setSelectedPolygonForEdit(null);
    setEditingPolygonVertex(null);
  };

  const getSelectedPolygonData = () => {
    if (!polygonData || selectedPolygon === null) return null;
    return polygonData.polygons.find(p => p.id === selectedPolygon);
  };

  const getPolygonCoords = (polygonId: number): [number, number][] => {
    // Return edited coords if they exist, otherwise return original
    if (editedPolygonCoords[polygonId]) {
      return editedPolygonCoords[polygonId];
    }
    if (originalPolygonCoords[polygonId]) {
      return originalPolygonCoords[polygonId];
    }
    // Fallback to polygon data
    const polygon = polygonData?.polygons.find(p => p.id === polygonId);
    if (polygon) {
      return polygon.geometry.coordinates[0].map(
        ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
      );
    }
    return [];
  };

  // Edge selection helpers
  const getPolygonEdges = (coords: [number, number][]): { start: [number, number]; end: [number, number]; index: number }[] => {
    const edges: { start: [number, number]; end: [number, number]; index: number }[] = [];
    for (let i = 0; i < coords.length - 1; i++) {
      edges.push({
        start: coords[i],
        end: coords[i + 1],
        index: i,
      });
    }
    return edges;
  };

  const getEdgeLabel = (edge: { start: [number, number]; end: [number, number] }): string => {
    const [startLat, startLng] = edge.start;
    const [endLat, endLng] = edge.end;

    // Determine if edge is more horizontal or vertical
    const latDiff = Math.abs(endLat - startLat);
    const lngDiff = Math.abs(endLng - startLng);

    if (latDiff > lngDiff) {
      // More vertical - check if going north or south
      return endLat > startLat ? 'North' : 'South';
    } else {
      // More horizontal - check if going east or west
      return endLng > startLng ? 'East' : 'West';
    }
  };

  // Convert edge angle to azimuth (compass bearing from North)
  // Returns the azimuth of the ROOF FACE (perpendicular to the edge)
  const calculateEdgeAzimuth = (polygonId: number): number | null => {
    // If we have a stored azimuth (persists through edits), use that
    if (snapEdgeAzimuth[polygonId] !== undefined) {
      return snapEdgeAzimuth[polygonId];
    }

    const edgeIndex = snapEdgeIndex[polygonId];
    if (edgeIndex === undefined) return null;

    const coords = getPolygonCoords(polygonId);
    if (edgeIndex < 0 || edgeIndex >= coords.length - 1) return null;

    const start = coords[edgeIndex];  // [lat, lng]
    const end = coords[edgeIndex + 1]; // [lat, lng]

    // For short distances on map, use simple Cartesian approach
    // We calculate the edge direction first, then rotate 90° to get face direction
    const dLat = end[0] - start[0];  // Positive = North
    const dLng = end[1] - start[1];  // Positive = East

    // Convert to meters using projected coordinates
    const metersPerDegreeLat = 111320; // meters per degree latitude (constant)
    const metersPerDegreeLng = 111320 * Math.cos(start[0] * Math.PI / 180); // varies with latitude

    const dy = dLat * metersPerDegreeLat;  // North-South in meters
    const dx = dLng * metersPerDegreeLng;  // East-West in meters

    // Calculate edge bearing: atan2(dx, dy) gives angle from North (clockwise)
    let edgeAzimuth = Math.atan2(dx, dy) * (180 / Math.PI);
    edgeAzimuth = (edgeAzimuth + 360) % 360;

    // Calculate face azimuth (perpendicular to edge, pointing "inward" toward polygon center)
    // We need to determine which perpendicular direction points inward
    // For now, add 90° to get right-hand perpendicular
    // TODO: Check polygon center to determine correct inward direction
    let faceAzimuth = (edgeAzimuth + 90) % 360;

    // Debug logging

    return Math.round(faceAzimuth);
  };

  // Helper function to get compass direction from azimuth
  const getCompassDirection = (az: number): string => {
    if (az >= 348.75 || az < 11.25) return 'N';
    if (az >= 11.25 && az < 33.75) return 'NNE';
    if (az >= 33.75 && az < 56.25) return 'NE';
    if (az >= 56.25 && az < 78.75) return 'ENE';
    if (az >= 78.75 && az < 101.25) return 'E';
    if (az >= 101.25 && az < 123.75) return 'ESE';
    if (az >= 123.75 && az < 146.25) return 'SE';
    if (az >= 146.25 && az < 168.75) return 'SSE';
    if (az >= 168.75 && az < 191.25) return 'S';
    if (az >= 191.25 && az < 213.75) return 'SSW';
    if (az >= 213.75 && az < 236.25) return 'SW';
    if (az >= 236.25 && az < 258.75) return 'WSW';
    if (az >= 258.75 && az < 281.25) return 'W';
    if (az >= 281.25 && az < 303.75) return 'WNW';
    if (az >= 303.75 && az < 326.25) return 'NW';
    return 'NNW';
  };

  // Calculate the angle of the snap edge (in degrees) - used for module rotation
  // Checks for manual azimuth override first
  const getSnapEdgeAngle = (polygonId: number): number => {
    // Check if manual azimuth is set
    const manual = manualAzimuth[polygonId];
    if (manual !== null && manual !== undefined) {
      return manual;
    }

    // Otherwise, calculate from selected edge
    const edgeIndex = snapEdgeIndex[polygonId];
    if (edgeIndex === undefined) return 0;

    const coords = getPolygonCoords(polygonId);
    if (edgeIndex < 0 || edgeIndex >= coords.length - 1) return 0;

    const start = coords[edgeIndex];
    const end = coords[edgeIndex + 1];

    // Calculate angle in degrees
    const deltaLat = end[0] - start[0];
    const deltaLng = end[1] - start[1];
    const angleRadians = Math.atan2(deltaLng, deltaLat);
    const angleDegrees = angleRadians * (180 / Math.PI);

    return angleDegrees;
  };

  // Rotate a rectangle to align with snap edge
  const rotateRectangleToSnapEdge = (rectangle: [number, number][], polygonId: number): [number, number][] => {
    const snapAngle = getSnapEdgeAngle(polygonId);
    if (snapAngle === 0) return rectangle;

    // Calculate center of rectangle
    const centerLat = rectangle.reduce((sum, [lat]) => sum + lat, 0) / rectangle.length;
    const centerLng = rectangle.reduce((sum, [, lng]) => sum + lng, 0) / rectangle.length;

    // Rotate all points around center
    const radians = (snapAngle * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    return rectangle.map(([lat, lng]) => {
      const deltaLat = lat - centerLat;
      const deltaLng = lng - centerLng;

      const newLat = centerLat + (deltaLat * cos - deltaLng * sin);
      const newLng = centerLng + (deltaLat * sin + deltaLng * cos);

      return [newLat, newLng] as [number, number];
    });
  };

  const getPolygonStyle = (polygon: Polygon) => {
    // Phase 3: Multi-selection styling
    const isActive = activeRoofId === polygon.id; // Active roof (blue)
    const isSelected = selectedPolygons.includes(polygon.id); // Any selected roof
    const isSecondarySelection = isSelected && !isActive; // Selected but not active (teal)

    const isSiteMode = siteSystemMode === 'site';
    // Make fill transparent when flux layer is selected to show flux data clearly
    const isFluxLayer = selectedLayer === 'flux';

    // Site mode: Active = blue, Secondary = teal, Unselected = red
    if (isSiteMode) {
      if (isActive) {
        return {
          color: '#2563eb', // Blue border
          fillColor: '#3b82f6', // Blue fill
          fillOpacity: isFluxLayer ? 0 : 0.3,
          opacity: 1, // Stroke fully opaque
          weight: 3,
        };
      }
      if (isSecondarySelection) {
        return {
          color: '#14b8a6', // Teal border (secondary selected)
          fillColor: '#2dd4bf', // Teal fill
          fillOpacity: isFluxLayer ? 0 : 0.25,
          opacity: 1, // Stroke fully opaque
          weight: 3,
        };
      }
      return {
        color: '#ff6b35', // Bright orange border (more visible than red)
        fillColor: '#ff8c61', // Orange fill
        fillOpacity: isFluxLayer ? 0 : 0.2,
        opacity: 1, // Stroke fully opaque
        weight: 2.5,
      };
    }

    // System mode: Active = blue, Secondary = teal, Unselected = orange
    if (isActive) {
      return {
        color: '#2563eb',
        fillColor: '#3b82f6',
        fillOpacity: isFluxLayer ? 0 : 0.5,
        opacity: 1, // Stroke fully opaque
        weight: 3,
      };
    }
    if (isSecondarySelection) {
      return {
        color: '#14b8a6',
        fillColor: '#2dd4bf',
        fillOpacity: isFluxLayer ? 0 : 0.4,
        opacity: 1, // Stroke fully opaque
        weight: 3,
      };
    }
    return {
      color: '#ff6b35', // Bright orange border (more visible)
      fillColor: '#ff8c61', // Orange fill
      fillOpacity: isFluxLayer ? 0 : 0.35,
      opacity: 1, // Stroke fully opaque
      weight: 2.5,
    };
  };

  // Smart auto-fill algorithm for solar modules
  const handleAutoFill = () => {
    if (selectedPolygon === null || !polygonData) {
      console.warn('[TECH-81] handleAutoFill bail: missing selectedPolygon or polygonData', { selectedPolygon, hasPolygonData: !!polygonData });
      return;
    }

    const poly = getSelectedPolygonData();
    if (!poly) {
      console.warn('[TECH-81] handleAutoFill bail: poly no encontrado en polygonData.polygons (race)', { selectedPolygon, polysCount: polygonData.polygons.length });
      return;
    }

    // Get polygon coordinates (edited or original)
    // BUGFIX: Use editedPolygonCoords if available, otherwise use original coords
    const polygonCoords: [number, number][] = editedPolygonCoords[selectedPolygon] ||
      poly.geometry.coordinates[0].map(
        ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
      );

    // Use existing snap edge if already set, otherwise auto-detect
    // BUGFIX: Don't reset user's manual edge selection
    const existingEdgeIndex = snapEdgeIndex[selectedPolygon];
    const edgeIndexToUse = existingEdgeIndex !== undefined
      ? existingEdgeIndex
      : autoDetectBestEdge(polygonCoords);


    // Calculate azimuth for the edge
    let snapAngle: number | undefined = undefined;
    if (edgeIndexToUse >= 0 && edgeIndexToUse < polygonCoords.length - 1) {
      const start = polygonCoords[edgeIndexToUse];
      const end = polygonCoords[edgeIndexToUse + 1];
      const dLat = end[0] - start[0];
      const dLng = end[1] - start[1];
      const metersPerDegreeLat = 111320;
      const metersPerDegreeLng = 111320 * Math.cos(start[0] * Math.PI / 180);
      const dy = dLat * metersPerDegreeLat;
      const dx = dLng * metersPerDegreeLng;
      let edgeAzimuth = Math.atan2(dx, dy) * (180 / Math.PI);
      edgeAzimuth = (edgeAzimuth + 360) % 360;
      snapAngle = (edgeAzimuth + 90) % 360; // Face azimuth (perpendicular to edge)

      // Only update snap edge states if not already set (preserve user selection)
      if (existingEdgeIndex === undefined) {
        setSnapEdgeIndex({ ...snapEdgeIndex, [selectedPolygon]: edgeIndexToUse });
        setSnapEdgeAzimuth({ ...snapEdgeAzimuth, [selectedPolygon]: snapAngle });
      } else {
        // Edge already set by user, just use the existing azimuth or recalculate if coords changed
        const existingAzimuth = snapEdgeAzimuth[selectedPolygon];
        if (existingAzimuth !== undefined) {
          snapAngle = existingAzimuth;
        } else {
          setSnapEdgeAzimuth({ ...snapEdgeAzimuth, [selectedPolygon]: snapAngle });
        }
      }
    }

    // Check if full roof segment (ID 0) already exists
    const existingSegments = moduleSegments[selectedPolygon] || [];
    const existingFullRoofSegment = existingSegments.find(s => s.id === 0);

    if (existingFullRoofSegment) {
      // Refill existing segment with CURRENT polygon shape (not old bounds)

      // Use CURRENT polygon coordinates (not old segment bounds)
      const segmentBounds = [...polygonCoords];

      // Update segment rotation/azimuth with new bounds
      refillSegment(selectedPolygon, 0, segmentBounds);

      // Update the segment's rotation and azimuth in state
      const updatedSegment = {
        ...existingFullRoofSegment,
        rotation: snapAngle || 0,
        azimuth: snapAngle || 0
      };

      const updatedSegments = [...existingSegments];
      updatedSegments[0] = updatedSegment;

      setModuleSegments({
        ...moduleSegments,
        [selectedPolygon]: updatedSegments
      });

    } else {
      // Create new full roof segment for first time

      // Create fresh segment with current polygon shape and best edge rotation
      const segmentBounds: [number, number][] = [...polygonCoords];

      // Create segment config using current global settings
      const segmentConfig = {
        moduleSpacing: moduleSpacing,
        rowSpacing: rowSpacingMultiplier,
        targetCount: targetModuleCount
      };

      // Calculate modules for this segment with EXPLICIT rotation
      const segmentModules = calculateModulesForBox(segmentBounds, true, snapAngle || 0, segmentConfig);
      if (segmentModules.length === 0) {
        console.warn('[TECH-81] calculateModulesForBox devolvio array vacio en handleAutoFill', {
          selectedPolygon,
          snapAngle,
          edgeIndexUsed: edgeIndexToUse,
          polygonCoordsLen: polygonCoords.length,
          targetModuleCount,
        });
      }

      // Full roof segment always has ID 0
      const segmentId = 0;

      // Assign segment ID to all modules
      const modulesWithSegmentId = segmentModules.map(m => ({
        ...m,
        segmentId: segmentId
      }));

      // Create the full roof segment
      const fullRoofSegment: ModuleSegment = {
        id: segmentId,
        bounds: segmentBounds,
        modules: modulesWithSegmentId,
        config: segmentConfig,
        rotation: snapAngle || 0,
        azimuth: snapAngle || 0
      };


      // Replace all segments and modules for this polygon with the single full roof segment
      setModuleSegments({
        ...moduleSegments,
        [selectedPolygon]: [fullRoofSegment] // Always exactly ONE segment
      });

      setModules({
        ...modules,
        [selectedPolygon]: modulesWithSegmentId
      });

    }
  };

  // Auto-fill modules when polygon is selected and roof is established (for quick assessment)
  useEffect(() => {
    // TECH-63: si se esta cargando un proyecto guardado, esperar a que la
    // restauracion termine antes de autofilear. Si autofileamos antes, se
    // crea un segment fantasma que se mezcla con los segments restaurados
    // del ODD original y quedan superpuestos en el mapa.
    if (loadedProjectData && !restorationComplete) return;

    // Multi-roof: Auto-fill each established roof independently
    // Keep modules from all roofs - don't clear other polygons!
    if (selectedPolygon !== null && isRoofEstablished(selectedPolygon)) {
      const currentModules = modules[selectedPolygon] || [];

      // Respect an existing free-field design: if the user already placed
      // free-field segments/modules, do NOT auto-complete roof modules on top
      // of them (they would overlap - "se enciman"). Auto-fill is only meant
      // for quick assessment of an otherwise empty roof.
      const hasFreeFieldDesign =
        (moduleSegments[FREE_FIELD_POLYGON_ID]?.length || 0) > 0 ||
        (modules[FREE_FIELD_POLYGON_ID]?.length || 0) > 0;

      // Auto-fill only if this polygon has no modules yet AND there is no
      // free-field design to preserve.
      if (currentModules.length === 0 && !hasFreeFieldDesign) {
        handleAutoFill();
      }
    }
  }, [selectedPolygon, establishedRoofIds, loadedProjectData, restorationComplete]);

  // Helper function to calculate segment rotation from its bounds
  // Returns the angle in degrees of the segment's longer edge (typical solar installation)
  const calculateSegmentRotation = (bounds: [number, number][]): number => {
    if (bounds.length < 4) return 0;

    // Calculate lengths of first two edges to determine which is longer
    const [lat1, lng1] = bounds[0];
    const [lat2, lng2] = bounds[1];
    const [lat3, lng3] = bounds[2];

    // Convert to meters for accurate length calculation
    const metersPerDegreeLat = 111320;
    const avgLat = (lat1 + lat2 + lat3) / 3;
    const metersPerDegreeLng = 111320 * Math.cos(avgLat * Math.PI / 180);

    // Length of first edge (0 to 1) in meters
    const edge1Length = Math.sqrt(
      Math.pow((lat2 - lat1) * metersPerDegreeLat, 2) +
      Math.pow((lng2 - lng1) * metersPerDegreeLng, 2)
    );

    // Length of second edge (1 to 2) in meters
    const edge2Length = Math.sqrt(
      Math.pow((lat3 - lat2) * metersPerDegreeLat, 2) +
      Math.pow((lng3 - lng2) * metersPerDegreeLng, 2)
    );

    // Use the longer edge to define module row orientation (typical for solar)
    let edgeStartLat, edgeStartLng, edgeEndLat, edgeEndLng;
    if (edge1Length >= edge2Length) {
      // First edge is longer - use it
      edgeStartLat = lat1;
      edgeStartLng = lng1;
      edgeEndLat = lat2;
      edgeEndLng = lng2;
    } else {
      // Second edge is longer - use it
      edgeStartLat = lat2;
      edgeStartLng = lng2;
      edgeEndLat = lat3;
      edgeEndLng = lng3;
    }

    // Calculate edge azimuth using proper geographic coordinates
    const dLat = edgeEndLat - edgeStartLat;
    const dLng = edgeEndLng - edgeStartLng;
    const dy = dLat * metersPerDegreeLat;
    const dx = dLng * metersPerDegreeLng;

    // Edge azimuth (direction of edge from north)
    let edgeAzimuth = Math.atan2(dx, dy) * (180 / Math.PI);
    edgeAzimuth = (edgeAzimuth + 360) % 360;

    // Face azimuth (perpendicular to edge - where modules face)
    const faceAzimuth = (edgeAzimuth + 90) % 360;


    return faceAzimuth;
  };

  // Helper function to calculate segment area in square meters
  const calculateSegmentArea = (bounds: [number, number][]): number => {
    if (bounds.length < 4) return 0;

    // Simple rectangle area calculation using first two edges
    const [lat1, lng1] = bounds[0];
    const [lat2, lng2] = bounds[1];
    const [lat3, lng3] = bounds[2];

    // Calculate edge lengths in meters (approximate)
    const metersPerDegreeLat = 111320; // meters per degree latitude
    const metersPerDegreeLng = Math.abs(Math.cos((lat1 + lat2) * Math.PI / 180)) * 111320; // varies by latitude

    const edge1Length = Math.sqrt(
      Math.pow((lat2 - lat1) * metersPerDegreeLat, 2) +
      Math.pow((lng2 - lng1) * metersPerDegreeLng, 2)
    );

    const edge2Length = Math.sqrt(
      Math.pow((lat3 - lat2) * metersPerDegreeLat, 2) +
      Math.pow((lng3 - lng2) * metersPerDegreeLng, 2)
    );

    return edge1Length * edge2Length;
  };

  // Module box creation handler - fills a drawn box with modules
  // Wrapper that gathers state and calls the pure calculateModulesForBox function
  const calculateModulesForBox = (
    box: [number, number][],
    applyFillPercentage: boolean = true,
    segmentRotation?: number,
    segmentConfig?: { moduleSpacing: number; rowSpacing: number; targetCount: number | null },
    freeField: boolean = false
  ): SolarModule[] => {
    // In free-field mode, we don't need a polygon - just fill the box
    if (!freeField && (selectedPolygon === null || !polygonData)) {
      console.warn('[TECH-81] calculateModulesForBox bail: contexto incompleto', { freeField, selectedPolygon, hasPolygonData: !!polygonData });
      return [];
    }

    // Gather polygon coordinates and obstructions based on mode
    let polygonCoords: [number, number][] = [];
    let polygonObstructions: [number, number][][] = [];

    // Global obstructions are stored under FREE_FIELD_POLYGON_ID (-1)
    // They should affect all module placements (both roof and free-field)
    const FREE_FIELD_OBSTRUCTION_ID = -1;
    const globalObstructions = obstructions[FREE_FIELD_OBSTRUCTION_ID] || [];

    if (!freeField) {
      const poly = getSelectedPolygonData();
      if (!poly) {
        console.warn('[TECH-81] calculateModulesForBox bail: poly no encontrado', { selectedPolygon, polysCount: polygonData?.polygons?.length });
        return [];
      }
      polygonCoords = editedPolygonCoords[selectedPolygon!] ||
        poly.geometry.coordinates[0].map(
          ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
        );
      // Include both polygon-specific obstructions AND global obstructions
      const polygonSpecificObstructions = obstructions[selectedPolygon!] || [];
      // Treat free-field segment footprints as exclusion zones so roof module
      // auto-fill/refill never overlaps modules the user designed as free fields.
      const freeFieldSegmentFootprints = (moduleSegments[FREE_FIELD_POLYGON_ID] || [])
        .map(seg => seg.bounds)
        .filter((b): b is [number, number][] => Array.isArray(b) && b.length >= 3);
      polygonObstructions = [
        ...polygonSpecificObstructions,
        ...globalObstructions,
        ...freeFieldSegmentFootprints,
      ];
    } else {
      polygonCoords = box;
      // In free-field mode, include all obstructions from all polygons
      polygonObstructions = Object.values(obstructions).flat();
    }

    // Determine rotation: segment rotation > snap edge > default 0
    const computedRotation = segmentRotation !== undefined
      ? segmentRotation
      : (snapEdgeIndex[selectedPolygon] !== undefined
          ? getSnapEdgeAngle(selectedPolygon) + 90
          : 0);

    // Build module config from current state
    const moduleConfig: ModuleConfig = {
      moduleWidth,
      moduleHeight,
      moduleOrientation,
      moduleSpacing,
      rowSpacingMultiplier,
      modulePowerWatts,
      obstructionSetback,
      edgeSetback,
    };

    // Build input for pure function
    const input: CalculateModulesInput = {
      box,
      moduleConfig,
      segmentRotation: computedRotation,
      segmentConfig: segmentConfig ? {
        moduleSpacing: segmentConfig.moduleSpacing,
        rowSpacing: segmentConfig.rowSpacing,
        targetCount: segmentConfig.targetCount,
      } : undefined,
      freeFieldMode: freeField,
      applyFillPercentage,
      targetModuleCount,
      polygonCoords,
      obstructions: polygonObstructions,
      panelInfo: {
        brand: selectedPanel?.brand || selectedBrand,
        model: selectedPanel?.model,
        power_watts: selectedPanel?.power_watts || modulePowerWatts,
      },
    };

    return calculateModulesForBoxPure(input);
  };

  // ========================================
  // SEGMENT HANDLERS (consolidated via useSegmentHandlers hook)
  // ========================================
  const {
    handleSegmentCreated,
    handleFreeFieldAddVertex,
    handleFreeFieldFinish,
    handleFreeFieldCancel,
    handleSegmentVerticesChange,
    refillSegment,
    handleCopySegment: handleCopySegmentFromHook,
  } = useSegmentHandlers({
    selectedPolygon,
    isFreeFieldMode,
    freeFieldAzimuth,
    moduleSpacing,
    rowSpacingMultiplier,
    targetModuleCount,
    moduleSegments,
    modules,
    freeFieldPolygonVertices,
    setModuleSegments,
    setModules,
    setIsDrawingSegment,
    setDragPreviewRect,
    setPreviewModules,
    setFreeFieldPolygonVertices,
    setSelectedSegment,
    calculateModulesForBox,
    calculateSegmentRotation,
  });

  // Wrapper to pass selectedSegment (hook doesn't access it directly)
  const handleCopySegment = () => handleCopySegmentFromHook(selectedSegment);

  // Handle segment rotation drag
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!isRotatingSegment || !selectedSegment || !map) return;

    const segments = moduleSegments[selectedSegment.polygonId] || [];
    const segment = segments[selectedSegment.index];
    if (!segment) return;

    // Calculate center of segment
    const centerLat = segment.bounds.reduce((sum, [lat]) => sum + lat, 0) / segment.bounds.length;
    const centerLng = segment.bounds.reduce((sum, [, lng]) => sum + lng, 0) / segment.bounds.length;

    const handleRotationMove = (e: any) => {
      const currentAngle = Math.atan2(
        e.latlng.lat - centerLat,
        e.latlng.lng - centerLng
      );

      const deltaAngle = currentAngle - segmentRotationAngleRef.current;

      const cos = Math.cos(deltaAngle);
      const sin = Math.sin(deltaAngle);

      // Rotate segment bounds
      const rotatedBounds = rotatingSegmentBoundsRef.current.map(([lat, lng]) => {
        const deltaLat = lat - centerLat;
        const deltaLng = lng - centerLng;

        const newLat = centerLat + (deltaLat * cos - deltaLng * sin);
        const newLng = centerLng + (deltaLat * sin + deltaLng * cos);

        return [newLat, newLng] as [number, number];
      });

      // Rotate modules along with segment (keep modules bound to segment axis)
      const rotatedModules = segment.modules.map(module => {
        const modLat = module.position[0];
        const modLng = module.position[1];

        const deltaLat = modLat - centerLat;
        const deltaLng = modLng - centerLng;

        const newLat = centerLat + (deltaLat * cos - deltaLng * sin);
        const newLng = centerLng + (deltaLat * sin + deltaLng * cos);

        return {
          ...module,
          position: [newLat, newLng] as [number, number],
          rotation: (module.rotation || 0) + (deltaAngle * 180 / Math.PI)
        };
      });

      // Update the visual bounds and modules immediately (preview)
      const updatedSegments = [...segments];
      updatedSegments[selectedSegment.index] = {
        ...segment,
        bounds: rotatedBounds,
        modules: rotatedModules
      };

      setModuleSegments({
        ...moduleSegments,
        [selectedSegment.polygonId]: updatedSegments
      });

      // Update global modules state for rendering
      const allModules = modules[selectedSegment.polygonId] || [];
      const otherModules = allModules.filter(m => m.segmentId !== segment.id);
      setModules({
        ...modules,
        [selectedSegment.polygonId]: [...otherModules, ...rotatedModules]
      });

      rotatingSegmentBoundsRef.current = rotatedBounds;
      segmentRotationAngleRef.current = currentAngle;
    };

    const handleRotationUp = () => {
      setIsRotatingSegment(false);
      // Note: Don't enable dragging here - let SpacebarPanController manage it

      // DON'T refill - modules have already been rotated correctly
      // Just update the segment's rotation angle for future operations
      const finalSegments = moduleSegments[selectedSegment.polygonId] || [];
      const finalSegment = finalSegments[selectedSegment.index];
      if (finalSegment) {
        const newRotation = calculateSegmentRotation(rotatingSegmentBoundsRef.current);
        const updatedSegment = {
          ...finalSegment,
          bounds: rotatingSegmentBoundsRef.current,
          rotation: newRotation,
          azimuth: newRotation
        };

        const updatedSegments = [...finalSegments];
        updatedSegments[selectedSegment.index] = updatedSegment;

        setModuleSegments({
          ...moduleSegments,
          [selectedSegment.polygonId]: updatedSegments
        });

      }
    };

    map.on('mousemove', handleRotationMove);
    map.on('mouseup', handleRotationUp);

    return () => {
      map.off('mousemove', handleRotationMove);
      map.off('mouseup', handleRotationUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isRotatingSegment, selectedSegment, moduleSegments, modules, refillSegment]);

  // Handle segment corner resize drag
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!isResizingSegmentCorner || !selectedSegment || !map) return;

    const segments = moduleSegments[selectedSegment.polygonId] || [];
    const segment = segments[selectedSegment.index];
    if (!segment) return;

    const cornerIndex = resizingCornerIndexRef.current;
    if (cornerIndex < 0 || cornerIndex >= segment.bounds.length) return;

    const handleCornerResizeMove = (e: any) => {
      // Update the corner being dragged
      const newBounds = [...resizingSegmentBoundsRef.current];
      newBounds[cornerIndex] = [e.latlng.lat, e.latlng.lng];

      // Update the visual bounds immediately (preview)
      const updatedSegments = [...segments];
      updatedSegments[selectedSegment.index] = {
        ...segment,
        bounds: newBounds
      };

      setModuleSegments({
        ...moduleSegments,
        [selectedSegment.polygonId]: updatedSegments
      });

      resizingSegmentBoundsRef.current = newBounds;
    };

    const handleCornerResizeUp = () => {
      setIsResizingSegmentCorner(false);
      // Note: Don't enable dragging here - let SpacebarPanController manage it

      // Finalize resize and refill modules
      refillSegment(selectedSegment.polygonId, selectedSegment.index, resizingSegmentBoundsRef.current);
    };

    map.on('mousemove', handleCornerResizeMove);
    map.on('mouseup', handleCornerResizeUp);

    return () => {
      map.off('mousemove', handleCornerResizeMove);
      map.off('mouseup', handleCornerResizeUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isResizingSegmentCorner, selectedSegment, moduleSegments, refillSegment]);

  // Handle segment edge stretch drag
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!isStretchingSegmentEdge || !selectedSegment || !map || !edgeDragStartPosRef.current) return;

    const segments = moduleSegments[selectedSegment.polygonId] || [];
    const segment = segments[selectedSegment.index];
    if (!segment) return;

    const edgeIndex = stretchingEdgeIndexRef.current;
    if (edgeIndex < 0) return;

    const handleEdgeStretchMove = (e: any) => {
      if (!edgeDragStartPosRef.current) return;

      // Mark that a drag occurred - prevents azimuth snap on click release
      edgeDragOccurredRef.current = true;

      const deltaLat = e.latlng.lat - edgeDragStartPosRef.current[0];
      const deltaLng = e.latlng.lng - edgeDragStartPosRef.current[1];

      // Get the two vertices that define this edge
      const bounds = stretchingSegmentBoundsRef.current;
      const v1Index = edgeIndex;
      const v2Index = (edgeIndex + 1) % bounds.length;

      // Calculate edge direction vector
      const edgeLat = bounds[v2Index][0] - bounds[v1Index][0];
      const edgeLng = bounds[v2Index][1] - bounds[v1Index][1];
      const edgeLength = Math.sqrt(edgeLat * edgeLat + edgeLng * edgeLng);

      // Normalize edge direction
      const edgeDirLat = edgeLat / edgeLength;
      const edgeDirLng = edgeLng / edgeLength;

      // Perpendicular to edge (for stretching)
      const perpLat = -edgeDirLng;
      const perpLng = edgeDirLat;

      // Project mouse movement onto perpendicular direction
      const perpMovement = deltaLat * perpLat + deltaLng * perpLng;

      // Move both vertices of this edge
      const newBounds = [...bounds];
      newBounds[v1Index] = [
        bounds[v1Index][0] + perpMovement * perpLat,
        bounds[v1Index][1] + perpMovement * perpLng,
      ];
      newBounds[v2Index] = [
        bounds[v2Index][0] + perpMovement * perpLat,
        bounds[v2Index][1] + perpMovement * perpLng,
      ];

      // Update the visual bounds immediately (preview)
      const updatedSegments = [...segments];
      updatedSegments[selectedSegment.index] = {
        ...segment,
        bounds: newBounds
      };

      setModuleSegments({
        ...moduleSegments,
        [selectedSegment.polygonId]: updatedSegments
      });

      stretchingSegmentBoundsRef.current = newBounds;
      edgeDragStartPosRef.current = [e.latlng.lat, e.latlng.lng];
    };

    const handleEdgeStretchUp = () => {
      setIsStretchingSegmentEdge(false);
      // Note: Don't enable dragging here - let SpacebarPanController manage it

      // Finalize stretch and refill modules
      refillSegment(selectedSegment.polygonId, selectedSegment.index, stretchingSegmentBoundsRef.current);
      edgeDragStartPosRef.current = null;
    };

    map.on('mousemove', handleEdgeStretchMove);
    map.on('mouseup', handleEdgeStretchUp);

    return () => {
      map.off('mousemove', handleEdgeStretchMove);
      map.off('mouseup', handleEdgeStretchUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isStretchingSegmentEdge, selectedSegment, moduleSegments, refillSegment]);

  // Handle segment drag to move
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!isDraggingSegment || !draggingSegmentRef.current || !map || !segmentDragStartPosRef.current) return;

    const { polygonId, index } = draggingSegmentRef.current;
    const segments = moduleSegments[polygonId] || [];
    const segment = segments[index];
    if (!segment) return;

    const handleSegmentDragMove = (e: any) => {
      if (!segmentDragStartPosRef.current) return;

      // Calculate offset from start position
      const deltaLat = e.latlng.lat - segmentDragStartPosRef.current[0];
      const deltaLng = e.latlng.lng - segmentDragStartPosRef.current[1];

      segmentDragOffsetRef.current = [deltaLat, deltaLng];

      // Move segment bounds
      const newBounds = segment.bounds.map(([lat, lng]) => [
        lat + deltaLat,
        lng + deltaLng
      ] as [number, number]);

      // Move all modules in this segment
      const polygonModules = modules[polygonId] || [];
      const updatedModules = polygonModules.map(module => {
        if (module.segmentId === segment.id) {
          return {
            ...module,
            position: [
              module.position[0] + deltaLat,
              module.position[1] + deltaLng
            ] as [number, number]
          };
        }
        return module;
      });

      // Update segment bounds AND its inner modules array (the save path
      // in projectDataBuilders.ts prefers segment.modules over the global
      // modules map, so leaving them stale persists old positions while the
      // bounds get the new ones — visible after reload as a mismatch).
      const updatedSegments = [...segments];
      updatedSegments[index] = {
        ...segment,
        bounds: newBounds,
        modules: (segment.modules || []).map(m => ({
          ...m,
          position: [m.position[0] + deltaLat, m.position[1] + deltaLng] as [number, number]
        }))
      };

      // Update both segments and modules state
      setModuleSegments({
        ...moduleSegments,
        [polygonId]: updatedSegments
      });

      setModules({
        ...modules,
        [polygonId]: updatedModules
      });

      // Update the start position for next delta calculation
      segmentDragStartPosRef.current = [e.latlng.lat, e.latlng.lng];
    };

    const handleSegmentDragUp = () => {
      setIsDraggingSegment(false);
      draggingSegmentRef.current = null;
      segmentDragStartPosRef.current = null;
      segmentDragOffsetRef.current = [0, 0];
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };

    map.on('mousemove', handleSegmentDragMove);
    map.on('mouseup', handleSegmentDragUp);

    return () => {
      map.off('mousemove', handleSegmentDragMove);
      map.off('mouseup', handleSegmentDragUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isDraggingSegment, moduleSegments, modules]);

  // Handle drag-to-select for modules
  const handleModuleSelection = (box: [number, number][]) => {
    if (selectedPolygon === null) return;

    const polygonModules = modules[selectedPolygon] || [];
    const newSelection = new Set<number>();

    // Check each module to see if its center is inside the selection box
    polygonModules.forEach((module, index) => {
      if (isPointInPolygon(module.position, box)) {
        newSelection.add(index);
      }
    });

    setSelectedModules(newSelection);
    setSelectedModule(null); // Clear single selection
  };

  // Get bounding box of selected modules (rotated to match module orientation)
  const getSelectedModulesBounds = (): [number, number][] | null => {
    if (selectedPolygon === null || selectedModules.size === 0) return null;

    const polygonModules = modules[selectedPolygon] || [];
    const selectedModulesList = Array.from(selectedModules)
      .map(index => polygonModules[index])
      .filter(m => m !== undefined);

    if (selectedModulesList.length === 0) return null;

    // Get rotation from first module (assume all have same rotation)
    const rotation = selectedModulesList[0].rotation || 0;

    // Get all corners of all selected modules
    const allPoints: [number, number][] = [];
    selectedModulesList.forEach(module => {
      const rect = createModuleRectangle(
        module.position,
        module.width,
        module.height,
        module.orientation,
        module.rotation
      );
      allPoints.push(...rect.slice(0, -1)); // Exclude the closing point
    });

    // Calculate center point
    const centerLat = allPoints.reduce((sum, p) => sum + p[0], 0) / allPoints.length;
    const centerLng = allPoints.reduce((sum, p) => sum + p[1], 0) / allPoints.length;

    // Transform points to rotated coordinate space
    const radians = -(rotation * Math.PI) / 180; // Negative for inverse rotation
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    const transformToRotated = (lat: number, lng: number): [number, number] => {
      const deltaLat = lat - centerLat;
      const deltaLng = lng - centerLng;
      return [
        deltaLat * cos - deltaLng * sin,
        deltaLat * sin + deltaLng * cos
      ];
    };

    const transformFromRotated = (rotLat: number, rotLng: number): [number, number] => {
      const rotRadians = (rotation * Math.PI) / 180;
      const rotCos = Math.cos(rotRadians);
      const rotSin = Math.sin(rotRadians);
      return [
        centerLat + (rotLat * rotCos - rotLng * rotSin),
        centerLng + (rotLat * rotSin + rotLng * rotCos)
      ];
    };

    const rotatedPoints = allPoints.map(([lat, lng]) => transformToRotated(lat, lng));
    const minRotLat = Math.min(...rotatedPoints.map(p => p[0]));
    const maxRotLat = Math.max(...rotatedPoints.map(p => p[0]));
    const minRotLng = Math.min(...rotatedPoints.map(p => p[1]));
    const maxRotLng = Math.max(...rotatedPoints.map(p => p[1]));

    // Transform corners back to lat/lng
    return [
      transformFromRotated(maxRotLat, minRotLng), // top-left
      transformFromRotated(maxRotLat, maxRotLng), // top-right
      transformFromRotated(minRotLat, maxRotLng), // bottom-right
      transformFromRotated(minRotLat, minRotLng), // bottom-left
      transformFromRotated(maxRotLat, minRotLng), // close
    ];
  };

  // Calculate snap offset for module-to-module alignment
  const calculateModuleSnap = (
    movingModules: SolarModule[],
    stationaryModules: SolarModule[],
    proposedDelta: { lat: number; lng: number }
  ): { lat: number; lng: number } => {
    if (stationaryModules.length === 0) return proposedDelta;

    const SNAP_THRESHOLD = 0.00002; // ~2 meters in degrees
    let snapDeltaLat = proposedDelta.lat;
    let snapDeltaLng = proposedDelta.lng;
    let minSnapDistLat = Infinity;
    let minSnapDistLng = Infinity;

    // For each moving module, check against all stationary modules
    movingModules.forEach(movingModule => {
      const movingPos = [
        movingModule.position[0] + proposedDelta.lat,
        movingModule.position[1] + proposedDelta.lng,
      ] as [number, number];

      const movingRotation = movingModule.rotation || 0;
      const movingRadians = (movingRotation * Math.PI) / 180;
      const movingCos = Math.cos(movingRadians);
      const movingSin = Math.sin(movingRadians);

      // Calculate moving module's edges in rotated space
      const movingWidth = movingModule.orientation === 'landscape' ? movingModule.width : movingModule.height;
      const movingHeight = movingModule.orientation === 'landscape' ? movingModule.height : movingModule.width;
      const movingHalfWidth = (movingWidth / 2) / 111320; // Convert to degrees
      const movingHalfHeight = (movingHeight / 2) / 111320;

      stationaryModules.forEach(stationaryModule => {
        // Only snap to modules with same rotation
        if (Math.abs((stationaryModule.rotation || 0) - movingRotation) > 1) return;

        const stationaryPos = stationaryModule.position;
        const stationaryWidth = stationaryModule.orientation === 'landscape' ? stationaryModule.width : stationaryModule.height;
        const stationaryHeight = stationaryModule.orientation === 'landscape' ? stationaryModule.height : stationaryModule.width;
        const stationaryHalfWidth = (stationaryWidth / 2) / 111320;
        const stationaryHalfHeight = (stationaryHeight / 2) / 111320;

        // Transform both module positions to rotated coordinate space
        const movingRotX = (movingPos[0] - movingPos[0]) * movingCos + (movingPos[1] - movingPos[1]) * movingSin;
        const movingRotY = -(movingPos[0] - movingPos[0]) * movingSin + (movingPos[1] - movingPos[1]) * movingCos;

        const deltaLat = stationaryPos[0] - movingPos[0];
        const deltaLng = stationaryPos[1] - movingPos[1];
        const stationaryRotX = deltaLat * movingCos + deltaLng * movingSin;
        const stationaryRotY = -deltaLat * movingSin + deltaLng * movingCos;

        // Calculate edge positions in rotated space
        const movingEdges = {
          top: movingRotX + movingHalfHeight,
          bottom: movingRotX - movingHalfHeight,
          right: movingRotY + movingHalfWidth,
          left: movingRotY - movingHalfWidth,
        };

        const stationaryEdges = {
          top: stationaryRotX + stationaryHalfHeight,
          bottom: stationaryRotX - stationaryHalfHeight,
          right: stationaryRotY + stationaryHalfWidth,
          left: stationaryRotY - stationaryHalfWidth,
        };

        // Check vertical edge alignment (in rotated space)
        [
          { moving: movingEdges.top, stationary: stationaryEdges.bottom, isLat: true },
          { moving: movingEdges.bottom, stationary: stationaryEdges.top, isLat: true },
          { moving: movingEdges.top, stationary: stationaryEdges.top, isLat: true },
          { moving: movingEdges.bottom, stationary: stationaryEdges.bottom, isLat: true },
          { moving: movingEdges.right, stationary: stationaryEdges.left, isLat: false },
          { moving: movingEdges.left, stationary: stationaryEdges.right, isLat: false },
          { moving: movingEdges.right, stationary: stationaryEdges.right, isLat: false },
          { moving: movingEdges.left, stationary: stationaryEdges.left, isLat: false },
        ].forEach(({ moving, stationary, isLat }) => {
          const distance = Math.abs(moving - stationary);
          if (distance < SNAP_THRESHOLD) {
            const snapOffset = stationary - moving;

            if (isLat) {
              // Transform back to lat/lng and apply to lat
              if (Math.abs(snapOffset) < minSnapDistLat) {
                minSnapDistLat = Math.abs(snapOffset);
                snapDeltaLat = proposedDelta.lat + (snapOffset * movingCos);
              }
            } else {
              // Transform back to lat/lng and apply to lng
              if (Math.abs(snapOffset) < minSnapDistLng) {
                minSnapDistLng = Math.abs(snapOffset);
                snapDeltaLng = proposedDelta.lng + (snapOffset / movingCos);
              }
            }
          }
        });
      });
    });

    return { lat: snapDeltaLat, lng: snapDeltaLng };
  };

  // Handle moving all selected modules together
  const handleMoveSelectedModules = (delta: { lat: number; lng: number }) => {
    if (selectedPolygon === null || selectedModules.size === 0) return;

    const polygonModules = modules[selectedPolygon] || [];

    // Check if any selected module belongs to a segment
    const hasSegmentModules = Array.from(selectedModules).some(index => {
      const module = polygonModules[index];
      return module && module.segmentId !== undefined;
    });

    if (hasSegmentModules) {
      return;
    }

    const updated = polygonModules.map((module, index) => {
      if (selectedModules.has(index)) {
        return {
          ...module,
          position: [
            module.position[0] + delta.lat,
            module.position[1] + delta.lng,
          ] as [number, number],
        };
      }
      return module;
    });

    setModules({ ...modules, [selectedPolygon]: updated });
  };

  // Handle copying selected modules
  const handleCopySelectedModules = (delta: { lat: number; lng: number }) => {
    if (selectedPolygon === null || selectedModules.size === 0) return;

    const polygonModules = modules[selectedPolygon] || [];
    const newModules: SolarModule[] = [];

    selectedModules.forEach(index => {
      const module = polygonModules[index];
      if (module) {
        newModules.push({
          ...module,
          position: [
            module.position[0] + delta.lat,
            module.position[1] + delta.lng,
          ] as [number, number],
        });
      }
    });

    setModules({
      ...modules,
      [selectedPolygon]: [...polygonModules, ...newModules],
    });

    // Select the newly created modules
    const newSelection = new Set<number>();
    for (let i = 0; i < newModules.length; i++) {
      newSelection.add(polygonModules.length + i);
    }
    setSelectedModules(newSelection);
  };

  // Handle expanding array by dragging edge
  const handleExpandEdge = (edge: 'top' | 'bottom' | 'left' | 'right', distance: number) => {
    if (selectedPolygon === null || selectedModules.size === 0) return;

    const polygonModules = modules[selectedPolygon] || [];
    const selectedList = Array.from(selectedModules)
      .map(index => polygonModules[index])
      .filter(m => m !== undefined);

    if (selectedList.length === 0) return;

    // Get module parameters
    const firstModule = selectedList[0];
    const rotation = firstModule.rotation || 0;
    const effectiveHeight = firstModule.orientation === 'landscape' ? firstModule.height : firstModule.width;
    const effectiveWidth = firstModule.orientation === 'landscape' ? firstModule.width : firstModule.height;

    // Calculate module size including spacing
    const moduleSizeWithSpacing = (edge === 'top' || edge === 'bottom')
      ? effectiveHeight * rowSpacingMultiplier  // Include row spacing for vertical expansion
      : effectiveWidth + moduleSpacing;         // Include module spacing for horizontal expansion

    // Calculate how many modules to add based on distance
    const modulesToAdd = Math.floor(distance / moduleSizeWithSpacing);

    if (modulesToAdd < 1) return;

    // Calculate offset direction in rotated space
    const latDegPerMeter = 1 / 111320;
    const lngDegPerMeter = 1 / (111320 * Math.cos((firstModule.position[0] * Math.PI) / 180));
    const radians = (rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);

    let latOffset = 0, lngOffset = 0;

    if (edge === 'top') {
      // Use row spacing for vertical expansion
      const offset = effectiveHeight * rowSpacingMultiplier * latDegPerMeter;
      latOffset = offset * cos;
      lngOffset = offset * sin;
    } else if (edge === 'bottom') {
      const offset = -(effectiveHeight * rowSpacingMultiplier) * latDegPerMeter;
      latOffset = offset * cos;
      lngOffset = offset * sin;
    } else if (edge === 'left') {
      // Use module spacing for horizontal expansion
      const offset = -(effectiveWidth + moduleSpacing) * lngDegPerMeter;
      latOffset = -offset * sin;
      lngOffset = offset * cos;
    } else if (edge === 'right') {
      const offset = (effectiveWidth + moduleSpacing) * lngDegPerMeter;
      latOffset = -offset * sin;
      lngOffset = offset * cos;
    }

    // Add new row/column of modules
    // For horizontal edges (left/right): extend each row
    // For vertical edges (top/bottom): extend each column
    const newModules: SolarModule[] = [];

    if (edge === 'left' || edge === 'right') {
      // Horizontal expansion: Add modules in the same row (don't duplicate rows)
      // Group by row position and extend each row
      const rowMap = new Map<number, SolarModule[]>();
      selectedList.forEach(module => {
        const rowKey = Math.round(module.position[0] * 10000); // Group by lat (row)
        if (!rowMap.has(rowKey)) rowMap.set(rowKey, []);
        rowMap.get(rowKey)!.push(module);
      });

      // For each row, add new modules
      rowMap.forEach(rowModules => {
        const baseModule = rowModules[0]; // Use first module in row as reference
        for (let i = 0; i < modulesToAdd; i++) {
          newModules.push({
            ...baseModule,
            position: [
              baseModule.position[0] + latOffset * (i + 1),
              baseModule.position[1] + lngOffset * (i + 1),
            ] as [number, number],
          });
        }
      });
    } else {
      // Vertical expansion: Add modules in the same column (don't duplicate columns)
      // Group by column position and extend each column
      const colMap = new Map<number, SolarModule[]>();
      selectedList.forEach(module => {
        const colKey = Math.round(module.position[1] * 10000); // Group by lng (column)
        if (!colMap.has(colKey)) colMap.set(colKey, []);
        colMap.get(colKey)!.push(module);
      });

      // For each column, add new modules
      colMap.forEach(colModules => {
        const baseModule = colModules[0]; // Use first module in column as reference
        for (let i = 0; i < modulesToAdd; i++) {
          newModules.push({
            ...baseModule,
            position: [
              baseModule.position[0] + latOffset * (i + 1),
              baseModule.position[1] + lngOffset * (i + 1),
            ] as [number, number],
          });
        }
      });
    }

    setModules({
      ...modules,
      [selectedPolygon]: [...polygonModules, ...newModules],
    });
  };

  // Obstruction handlers
  // Auto-detect best edge for solar panels (longest south-facing edge)
  const autoDetectBestEdge = (coords: [number, number][]): number => {
    let bestEdgeIndex = 0;
    let bestScore = -Infinity;

    // Analyze each edge
    for (let i = 0; i < coords.length - 1; i++) {
      const start = coords[i];
      const end = coords[i + 1];

      // Calculate edge length in meters
      const dLat = end[0] - start[0];
      const dLng = end[1] - start[1];
      const metersPerDegreeLat = 111320;
      const metersPerDegreeLng = 111320 * Math.cos(start[0] * Math.PI / 180);
      const dy = dLat * metersPerDegreeLat;
      const dx = dLng * metersPerDegreeLng;
      const edgeLength = Math.sqrt(dx * dx + dy * dy);

      // Calculate edge azimuth (direction the edge points)
      let edgeAzimuth = Math.atan2(dx, dy) * (180 / Math.PI);
      edgeAzimuth = (edgeAzimuth + 360) % 360;

      // Calculate face azimuth (perpendicular to edge, +90°)
      let faceAzimuth = (edgeAzimuth + 90) % 360;

      // Score based on south-facing direction and length
      // Best faces: South (180°), SSE (157.5°), SSW (202.5°), SE (135°), SW (225°)
      // Calculate deviation from ideal south (180°)
      let deviationFromSouth = Math.abs(faceAzimuth - 180);
      if (deviationFromSouth > 180) deviationFromSouth = 360 - deviationFromSouth;

      // Prioritize faces between 135° (SE) and 225° (SW)
      const isInGoodRange = faceAzimuth >= 135 && faceAzimuth <= 225;
      const directionScore = isInGoodRange ? (90 - deviationFromSouth) : (45 - deviationFromSouth);

      // Score = length * direction quality
      // Longer edges and south-facing edges get higher scores
      const score = edgeLength * Math.max(0, directionScore / 90);


      if (score > bestScore) {
        bestScore = score;
        bestEdgeIndex = i;
      }
    }

    return bestEdgeIndex;
  };

  // Roof polygon drawing handlers
  const handleAddRoofVertex = (vertex: [number, number]) => {
    setRoofDrawingVertices([...roofDrawingVertices, vertex]);
  };

  const handleFinishRoofPolygon = () => {
    if (roofDrawingVertices.length < 3) {
      console.warn('⚠️ Need at least 3 vertices to create a polygon');
      return;
    }

    // Create new polygon ID (find max ID and increment)
    const maxId = Math.max(...polygonData.polygons.map(p => p.id), 0);
    const newId = maxId + 1;

    // Close the polygon (add first vertex at end if not already closed)
    const closedVertices = [...roofDrawingVertices];
    const firstVertex = closedVertices[0];
    const lastVertex = closedVertices[closedVertices.length - 1];
    if (firstVertex[0] !== lastVertex[0] || firstVertex[1] !== lastVertex[1]) {
      closedVertices.push(firstVertex);
    }

    // Create new polygon object
    const newPolygon = {
      id: newId,
      geometry: {
        type: 'Polygon' as const,
        coordinates: [closedVertices.map(([lat, lng]) => [lng, lat])],
      },
      properties: {
        area_m2: calculatePolygonArea(roofDrawingVertices),
        suitable_for_solar: true,
      },
    };

    // Add to polygon data
    setPolygonData({
      ...polygonData,
      polygons: [...polygonData.polygons, newPolygon],
    });

    // Auto-detect and set best edge for solar orientation
    const bestEdgeIndex = autoDetectBestEdge(closedVertices);
    setSnapEdgeIndex({
      ...snapEdgeIndex,
      [newId]: bestEdgeIndex,
    });

    // Multi-roof support: Add to selection array instead of replacing
    const newSelection = [...selectedPolygons, newId];
    setSelectedPolygons(newSelection);
    setActiveRoofId(newId); // Make the new roof active
    setSelectedPolygon(newId); // Keep for compatibility
    establishRoof(newId); // Establish this roof for module placement

    // Clear drawing state
    setIsDrawingRoof(false);
    setRoofDrawingVertices([]);

  };

  const handleCancelRoofDrawing = () => {
    setIsDrawingRoof(false);
    setRoofDrawingVertices([]);
  };

  // Drag update handler - kept inline because it needs calculateModulesForBox
  const handleDragUpdate = (rect: [number, number][] | null) => {
    setDragPreviewRect(rect);

    // Calculate and show preview modules in real-time while drawing segment
    if (rect && isDrawingSegment) {
      const preview = calculateModulesForBox(rect, true);
      setPreviewModules(preview);
    } else {
      setPreviewModules([]);
    }
  };

  // Calculate Leaflet bounds from polygon data
  // NOTE: This is for GeoTIFF positioning, NOT polygon bounds
  // GeoTIFF bounds come from the image metadata, not polygon coordinates
  const getLeafletBounds = () => {
    // Only use explicit bounds from polygonData (set by GeoTIFF metadata)
    if (!polygonData?.bounds) return null;
    const { bounds } = polygonData;
    // Validate bounds has required properties
    if (bounds.south == null || bounds.north == null || bounds.west == null || bounds.east == null) {
      return null;
    }
    return [
      [bounds.south, bounds.west],
      [bounds.north, bounds.east]
    ];
  };

  // ============================================
  // PHASE 1: NON-BLOCKING LOADING PATTERN
  // Calculate map center from multiple fallback sources
  // This allows the map to render immediately while polygon data loads
  // ============================================
  const isValidLatLng = (lat: number | undefined, lng: number | undefined): boolean => {
    return lat !== undefined && lng !== undefined &&
      !isNaN(lat) && !isNaN(lng) &&
      isFinite(lat) && isFinite(lng);
  };

  const getMapCenter = (): { lat: number; lng: number } => {
    // Priority 1: Use polygon data center if available and valid
    if (polygonData?.center && isValidLatLng(polygonData.center.lat, polygonData.center.lng)) {
      return polygonData.center;
    }
    // Priority 2: Use buildingInsights center (available from Solar API earlier)
    if (buildingInsights?.center && isValidLatLng(buildingInsights.center.latitude, buildingInsights.center.longitude)) {
      return {
        lat: buildingInsights.center.latitude,
        lng: buildingInsights.center.longitude,
      };
    }
    // Priority 3: Use pin position (user-selected location)
    if (pinPosition && isValidLatLng(pinPosition.lat, pinPosition.lng)) {
      return pinPosition;
    }
    // Priority 4: Default to Mexico City (app's primary market)
    return { lat: 19.4326, lng: -99.1332 };
  };

  // Get initial bounds for map - uses buildingInsights if polygonData not ready
  const getInitialBounds = () => {
    // If polygonData is available, use its bounds
    if (polygonData?.bounds) {
      return [
        [polygonData.bounds.south, polygonData.bounds.west],
        [polygonData.bounds.north, polygonData.bounds.east]
      ];
    }
    // Generate approximate bounds from center (covers ~100m x 100m area)
    const center = getMapCenter();
    const offset = 0.0005; // ~50 meters at equator
    return [
      [center.lat - offset, center.lng - offset],
      [center.lat + offset, center.lng + offset]
    ];
  };

  // Determine if the map is ready to show imagery (needs bounds)
  const isMapReadyForImagery = Boolean(polygonData?.bounds);

  // Determine if we have enough data to show the map at all
  const canShowMap = Boolean(
    polygonData?.center ||
    buildingInsights?.center ||
    pinPosition
  );

  // NOTE: The blocking loading pattern has been REMOVED
  // The map will now render immediately with the loading overlay inside
  // Polygon layers will render conditionally when polygonData is available

  return (
    <div className="flex h-full w-full bg-gray-50 overflow-hidden relative">
      {/* Left Panel - Controls (Collapsible) - NOW HIDDEN BY DEFAULT, Aurora card is primary UI */}
      {!leftPanelCollapsed && (
        <div className="w-[35%] h-full bg-white border-r-2 border-gray-200 overflow-y-auto">
          {/* Header (Extracted Component) */}
          <LeftPanelHeader selectedLayer={selectedLayer} />

          {/* Mode Toggle - Site vs System (Extracted Component) */}
          <ModeToggleButtons
            editMode={editMode}
            onSiteMode={() => {
              setEditMode('site');
              setSiteSystemMode('site');
              setSelectedModules(new Set());
              setSelectedModule(null);
            }}
            onSystemMode={() => {
              setEditMode('system');
              setSiteSystemMode('system');
              setSelectedPolygonForEdit(null);
              setEditingPolygonVertex(null);
              setSelectedObstruction(null);
              setEditingObstruction(null);
              setIsDrawingObstruction(false);
              setIsDrawingSegment(false);
              setIsSelectingSnapEdge(false);
            }}
          />

          {/* Map Mode Selection (Extracted Component) */}
          <MapModeSelector
            mapMode={mapMode}
            isOpen={mapModeOpen}
            onToggleOpen={() => setMapModeOpen(!mapModeOpen)}
            onSelectMode={setMapMode}
          />

        {/* Info Panel */}
        <div className="p-6">
          <h3 className="font-semibold text-gray-900 mb-3">
            {selectedPolygon !== null ? 'Selected Polygon' : 'Layer Information'}
          </h3>
          {selectedPolygon !== null ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              {(() => {
                const poly = getSelectedPolygonData();
                return poly ? (
                  <>
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">ID:</span> {poly.id}
                    </p>
                    {(() => {
                      // Calculate polygon coordinates (use edited if available)
                      const polygonCoords: [number, number][] = editedPolygonCoords[poly.id] ||
                        poly.geometry.coordinates[0].map(
                          ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
                        );
                      const grossArea = calculatePolygonArea(polygonCoords);

                      // Calculate total obstruction area
                      const polygonObstructions = obstructions[poly.id] || [];
                      const obstructionArea = polygonObstructions.reduce((total, obstruction) => {
                        return total + calculatePolygonArea(obstruction);
                      }, 0);

                      const netArea = grossArea - obstructionArea;

                      return (
                        <div className="text-sm text-gray-700 space-y-1">
                          <p>
                            <span className="font-semibold">Gross Area:</span> {grossArea.toFixed(2)} m²
                          </p>
                          {obstructionArea > 0 && (
                            <>
                              <p className="text-red-600">
                                <span className="font-semibold">Obstructions:</span> -{obstructionArea.toFixed(2)} m²
                              </p>
                              <p className="text-green-600 font-semibold border-t border-gray-300 pt-1">
                                Net Usable: {netArea.toFixed(2)} m²
                              </p>
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* Edit Mode Controls */}
                    {isEditing ? (
                      <div className="mt-3 space-y-2">
                        <div className="text-xs text-gray-600 mb-2 space-y-1">
                          <p>• Hold <span className="font-semibold">Space</span> to pan map</p>
                          <p>• Drag <span className="text-blue-600 font-semibold">blue circles</span> to move vertices</p>
                          <p>• Hold <span className="font-semibold">Shift</span> while dragging for orthogonal movement</p>
                          <p>• <span className="font-semibold">Alt/Option+click</span> en borde para añadir nodo</p>
                          <p>• <span className="font-semibold">Alt/Option+click</span> en nodo para eliminarlo, o usa el botón abajo</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              // Save changes by updating polygon data
                              if (editedCoordinates && polygonData && selectedPolygon !== null) {
                                const updatedPolygons = polygonData.polygons.map(p => {
                                  if (p.id === selectedPolygon) {
                                    // Convert edited coords back to GeoJSON format [lng, lat]
                                    const geoJsonCoords = editedCoordinates.map(([lat, lng]) => [lng, lat]);
                                    return {
                                      ...p,
                                      geometry: {
                                        ...p.geometry,
                                        coordinates: [geoJsonCoords]
                                      }
                                    };
                                  }
                                  return p;
                                });
                                setPolygonData({
                                  ...polygonData,
                                  polygons: updatedPolygons
                                });
                              }
                              setIsEditing(false);
                              // TODO: Save to backend
                            }}
                            className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-[#EBFD03] text-black font-semibold rounded-lg hover:bg-[#d4e003] border-2 border-black transition-colors"
                          >
                            <Save className="h-4 w-4" />
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setIsEditing(false);
                              setEditedCoordinates(null);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 space-y-2">
                        {/* Polygon Editing Controls */}
                        {(selectedPolygonForEdit === selectedPolygon || editingPolygonVertex === selectedPolygon) && (
                          <div className="mb-3 p-3 bg-blue-50 border border-blue-300 rounded-lg">
                            <p className="text-xs font-semibold text-blue-800 mb-2">
                              {editingPolygonVertex === selectedPolygon ? 'Vertex Edit Mode' : 'Smooth Edit Mode'}
                            </p>
                            <div className="text-xs text-gray-600 space-y-1 mb-2">
                              {editingPolygonVertex === selectedPolygon ? (
                                <>
                                  <p>• Arrastra nodos para ajustar</p>
                                  <p>• Alt/Option+click en borde: añadir nodo</p>
                                  <p>• Alt/Option+click en nodo: eliminarlo (o botón abajo)</p>
                                </>
                              ) : (
                                <>
                                  <p>• Drag to move polygon</p>
                                  <p>• Drag purple handle to rotate</p>
                                  <p>• Drag corners/edges to resize</p>
                                  <p>• Double-click for vertex edit</p>
                                </>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {editingPolygonVertex === selectedPolygon && (
                                <button
                                  type="button"
                                  onClick={() => setRemoveVertexMode((v) => !v)}
                                  className={`flex-1 min-w-0 py-2 px-3 rounded-lg text-xs font-semibold transition-colors ${
                                    removeVertexMode
                                      ? 'bg-red-600 text-white hover:bg-red-700'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                  title={removeVertexMode ? 'Desactivar: clic en nodo ya no lo borra' : 'Activar: clic en un nodo lo elimina'}
                                >
                                  {removeVertexMode ? '✓ Eliminar nodo (activo)' : 'Eliminar nodo'}
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setSelectedPolygonForEdit(null);
                                  setEditingPolygonVertex(null);
                                  setRemoveVertexMode(false);
                                }}
                                className="flex-1 min-w-0 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-semibold"
                              >
                                Done
                              </button>
                              {editedPolygonCoords[selectedPolygon] && (
                                <button
                                  onClick={() => handleResetPolygon(selectedPolygon)}
                                  className="flex-1 min-w-0 py-2 px-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-semibold"
                                >
                                  Reset
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Edge Snap Selector - Collapsible */}
                        <div className="pt-3 border-t border-blue-300">
                          <button
                            onClick={() => setSnapEdgeOpen(!snapEdgeOpen)}
                            className="w-full flex items-center justify-between mb-2 hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                          >
                            <h4 className="text-sm font-semibold text-gray-700">
                              Snap Edge for Alignment {snapEdgeIndex[selectedPolygon] !== undefined && `(Edge ${snapEdgeIndex[selectedPolygon] + 1})`}
                            </h4>
                            {snapEdgeOpen ? (
                              <ChevronUp className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            )}
                          </button>

                          {snapEdgeOpen && (
                            <>
                              {isSelectingSnapEdge ? (
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-600 bg-yellow-50 border border-yellow-300 rounded p-2">
                                    <p className="font-semibold mb-1">Select Snap Edge:</p>
                                    <p>• Click on any edge marker on the map</p>
                                    <p>• Selected edge will be highlighted in yellow</p>
                                    <p>• Modules will snap to this edge</p>
                                  </div>
                                  <button
                                    onClick={() => setIsSelectingSnapEdge(false)}
                                    className="w-full py-2 px-4 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-semibold"
                                  >
                                    Done Selecting
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {/* Edge Selection Status */}
                                  {snapEdgeIndex[selectedPolygon] !== undefined ? (
                                    <div className="text-xs bg-green-50 border border-green-300 rounded p-2">
                                      <p className="font-semibold mb-1">
                                        Edge {snapEdgeIndex[selectedPolygon] + 1} selected
                                      </p>
                                      <p className="text-gray-700">
                                        Azimuth: <span className="font-bold">{calculateEdgeAzimuth(selectedPolygon)}°</span>
                                        {(() => {
                                          const az = calculateEdgeAzimuth(selectedPolygon);
                                          if (az === null) return '';
                                          // More precise 16-point compass
                                          if (az >= 348.75 || az < 11.25) return ' (N)';
                                          if (az >= 11.25 && az < 33.75) return ' (NNE)';
                                          if (az >= 33.75 && az < 56.25) return ' (NE)';
                                          if (az >= 56.25 && az < 78.75) return ' (ENE)';
                                          if (az >= 78.75 && az < 101.25) return ' (E)';
                                          if (az >= 101.25 && az < 123.75) return ' (ESE)';
                                          if (az >= 123.75 && az < 146.25) return ' (SE)';
                                          if (az >= 146.25 && az < 168.75) return ' (SSE)';
                                          if (az >= 168.75 && az < 191.25) return ' (S)';
                                          if (az >= 191.25 && az < 213.75) return ' (SSW)';
                                          if (az >= 213.75 && az < 236.25) return ' (SW)';
                                          if (az >= 236.25 && az < 258.75) return ' (WSW)';
                                          if (az >= 258.75 && az < 281.25) return ' (W)';
                                          if (az >= 281.25 && az < 303.75) return ' (WNW)';
                                          if (az >= 303.75 && az < 326.25) return ' (NW)';
                                          return ' (NNW)';
                                        })()}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-600">No edge selected</p>
                                  )}

                                  {/* Manual Azimuth Override */}
                                  <div>
                                    <label className="block font-semibold text-gray-700 mb-1 text-xs">
                                      Manual Azimuth (degrees)
                                    </label>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={manualAzimuth[selectedPolygon] ?? ''}
                                      onChange={(e) => {
                                        const value = e.target.value.trim();
                                        if (value === '') {
                                          setManualAzimuth({
                                            ...manualAzimuth,
                                            [selectedPolygon]: null
                                          });
                                        } else {
                                          const num = parseFloat(value);
                                          if (!isNaN(num) && num >= 0 && num <= 360) {
                                            setManualAzimuth({
                                              ...manualAzimuth,
                                              [selectedPolygon]: num
                                            });
                                          }
                                        }
                                      }}
                                      placeholder="Auto (from edge)"
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      0° = North, 90° = East, 180° = South, 270° = West
                                    </p>
                                  </div>

                                  {/* Edge Selection Button */}
                                  <button
                                    onClick={() => setIsSelectingSnapEdge(true)}
                                    className="w-full py-2 px-4 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-xs font-semibold"
                                  >
                                    {snapEdgeIndex[selectedPolygon] !== undefined ? 'Change' : 'Select'} Snap Edge
                                  </button>

                                  {/* Clear Button */}
                                  {(snapEdgeIndex[selectedPolygon] !== undefined || manualAzimuth[selectedPolygon] !== null) && (
                                    <button
                                      onClick={() => {
                                        const newSnapEdge = { ...snapEdgeIndex };
                                        delete newSnapEdge[selectedPolygon];
                                        setSnapEdgeIndex(newSnapEdge);
                                        setManualAzimuth({ ...manualAzimuth, [selectedPolygon]: null });
                                      }}
                                      className="w-full py-1 px-4 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors text-xs"
                                    >
                                      Clear Snap Edge / Azimuth
                                    </button>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Obstruction Controls - Collapsible */}
                        <div className="pt-3 border-t border-blue-300">
                          <button
                            onClick={() => setObstructionsOpen(!obstructionsOpen)}
                            className="w-full flex items-center justify-between mb-2 hover:bg-gray-50 px-2 py-1 rounded transition-colors"
                          >
                            <h4 className="text-sm font-semibold text-gray-700">
                              Obstructions ({(obstructions[selectedPolygon] || []).length})
                            </h4>
                            {obstructionsOpen ? (
                              <ChevronUp className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            )}
                          </button>

                          {obstructionsOpen && (
                            <>
                              {/* Only show Add button in Site mode */}
                              {editMode === 'site' && (
                                isDrawingObstruction ? (
                            <div className="space-y-2">
                              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-300 rounded p-2">
                                <p className="font-semibold mb-1">Drawing Obstructions:</p>
                                <p>• Click and drag to draw rectangles</p>
                                <p>• Must be inside the roof polygon</p>
                                <p>• Click to select obstruction</p>
                                <p>• <span className="font-semibold">Option+click</span> to duplicate</p>
                              </div>
                              <button
                                onClick={() => {
                                  setIsDrawingObstruction(false);
                                }}
                                className="w-full py-2 px-4 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                              >
                                Done Adding
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => setIsDrawingObstruction(true)}
                                className="w-full py-2 px-4 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                              >
                                + Add Obstruction
                              </button>

                              {/* Instructions for obstruction editing */}
                              {selectedObstruction && (
                                <div className="space-y-2 mt-2">
                                  <div className="text-xs text-gray-600 bg-green-50 border border-green-300 rounded p-2">
                                    <p className="font-semibold mb-1">Selected Obstruction:</p>
                                    <p>• Drag <span className="text-purple-600 font-semibold">purple circle</span> to rotate</p>
                                    <p>• Drag <span className="text-blue-600 font-semibold">blue corners</span> to reshape</p>
                                    <p>• Drag <span className="text-green-600 font-semibold">green edges</span> to resize</p>
                                    <p>• <span className="font-semibold">Option+click</span> to duplicate</p>
                                    <p>• <span className="font-semibold">Double-click</span> for vertex edit</p>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setSelectedObstruction(null);
                                    }}
                                    className="w-full py-2 px-4 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                                  >
                                    Clear Selection
                                  </button>
                                </div>
                              )}

                              {/* List existing obstructions */}
                              {obstructions[selectedPolygon] && obstructions[selectedPolygon].length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {obstructions[selectedPolygon].map((obstruction, index) => {
                                    const isSelected = selectedObstruction?.polygonId === selectedPolygon &&
                                                      selectedObstruction?.index === index;
                                    const isEditing = editingObstruction?.polygonId === selectedPolygon &&
                                                     editingObstruction?.index === index;
                                    const obstructionArea = calculatePolygonArea(obstruction);

                                    return (
                                      <div
                                        key={index}
                                        onClick={() => {
                                          if (!isDrawingObstruction) {
                                            setSelectedObstruction({ polygonId: selectedPolygon, index });
                                          }
                                        }}
                                        className={`p-2 rounded text-xs cursor-pointer transition-colors ${
                                          isSelected
                                            ? 'bg-green-100 border-2 border-green-500'
                                            : isEditing
                                            ? 'bg-blue-100 border-2 border-blue-500'
                                            : 'bg-gray-50 border border-gray-300 hover:bg-gray-100'
                                        }`}
                                      >
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="font-medium">
                                            {isSelected && '✓ '}
                                            {isEditing && '✏️ '}
                                            Obstruction {index + 1}
                                          </span>
                                          <span className="text-gray-600">
                                            {obstructionArea.toFixed(2)} m²
                                          </span>
                                        </div>
                                        <div className="flex gap-1">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRotateObstruction(selectedPolygon, index, 45);
                                            }}
                                            className="text-purple-600 hover:text-purple-800 font-semibold"
                                            title="Rotate 45°"
                                          >
                                            ↻
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCopyObstruction(selectedPolygon, index);
                                            }}
                                            className="text-blue-600 hover:text-blue-800 font-semibold"
                                          >
                                            Copy
                                          </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteObstruction(selectedPolygon, index);
                                          }}
                                          className="text-red-600 hover:text-red-800 font-semibold"
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                              )
                              )}
                            </>
                          )}
                        </div>

                        {/* Editing Obstruction Controls - Only in Site mode */}
                        {editMode === 'site' && editingObstruction && (
                          <div className="mt-3 p-3 bg-green-50 border border-green-300 rounded-lg">
                            <p className="text-xs font-semibold text-green-800 mb-2">
                              Editing Obstruction {editingObstruction.index + 1}
                            </p>
                            <div className="text-xs text-gray-600 space-y-1 mb-2">
                              <p>• Arrastra nodos para ajustar</p>
                              <p>• Alt/Option+click en borde: añadir nodo</p>
                              <p>• Alt/Option+click en nodo: eliminarlo (o botón Eliminar nodo en modo techo)</p>
                            </div>
                            <button
                              onClick={() => setEditingObstruction(null)}
                              className="w-full py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                            >
                              Done Editing
                            </button>
                          </div>
                        )}

                        {/* Solar Modules Section - Only in System Mode */}
                        {editMode === 'system' && (
                          <div className="pt-3 border-t border-blue-300">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Solar Modules ({(modules[selectedPolygon] || []).length})
                            </h4>

                          {/* Module Configuration */}
                          <div className="space-y-2 text-xs">
                            {/* Panel Selector */}
                            <PanelSelector
                              selectedBrand={selectedBrand}
                              availableBrands={availableBrands}
                              selectedPanel={selectedPanel}
                              availablePanels={availablePanels}
                              panelsLoading={panelsLoading}
                              onBrandChange={handleBrandChange}
                              onPanelChange={handlePanelChange}
                              onCustomDimensions={() => {
                                if (selectedPanel) {
                                  setSelectedPanel(null);
                                }
                              }}
                            />

                            {/* Dimensions (show only if no panel selected) */}
                            {!selectedPanel && (
                              <ModuleDimensionInputs
                                width={moduleWidth}
                                height={moduleHeight}
                                onWidthChange={setModuleWidth}
                                onHeightChange={setModuleHeight}
                              />
                            )}

                            {/* Orientation */}
                            <OrientationToggle
                              value={moduleOrientation}
                              onChange={setModuleOrientation}
                            />

                            {/* Module Power */}
                            <ModulePowerInput
                              value={modulePowerWatts}
                              onChange={setModulePowerWatts}
                            />

                            {/* System Metrics */}
                            {selectedPolygon !== null && modules[selectedPolygon] && modules[selectedPolygon].length > 0 && (
                              <RoofSystemMetrics
                                moduleCount={modules[selectedPolygon].length}
                                moduleWidth={moduleWidth}
                                moduleHeight={moduleHeight}
                                modulePowerWatts={modulePowerWatts}
                                roofArea={polygonData?.polygons.find(p => p.id === selectedPolygon)?.area_m2}
                              />
                            )}

                            {/* Advanced Controls - Collapsible */}
                            <CollapsibleSection
                              title="Advanced Controls"
                              isOpen={moduleAdvancedOpen}
                              onToggle={() => setModuleAdvancedOpen(!moduleAdvancedOpen)}
                            >
                              <div className="space-y-2">
                                  {/* Spacing */}
                                  <SpacingSlider
                                    label="Module Spacing"
                                    value={moduleSpacing}
                                    min={0}
                                    max={0.2}
                                    step={0.01}
                                    onChange={setModuleSpacing}
                                    formatValue={(v) => `${(v * 100).toFixed(0)}cm`}
                                    labelStyle="semibold"
                                  />

                                  {/* Row Spacing */}
                                  <SpacingSlider
                                    label="Row Spacing"
                                    value={rowSpacingMultiplier}
                                    min={1.0}
                                    max={2.0}
                                    step={0.1}
                                    onChange={setRowSpacingMultiplier}
                                    formatValue={(v) => `${v.toFixed(1)}x`}
                                    labelStyle="semibold"
                                  />

                                  {/* Edge Setback */}
                                  <SpacingSlider
                                    label="Edge Setback"
                                    value={edgeSetback}
                                    min={0}
                                    max={0.5}
                                    step={0.05}
                                    onChange={setEdgeSetback}
                                    formatValue={(v) => `${(v * 100).toFixed(0)}cm`}
                                    labelStyle="semibold"
                                  />

                                  {/* Obstruction Setback */}
                                  <SpacingSlider
                                    label="Obstruction Setback"
                                    value={obstructionSetback}
                                    min={0}
                                    max={1.0}
                                    step={0.05}
                                    onChange={setObstructionSetback}
                                    formatValue={(v) => `${(v * 100).toFixed(0)}cm`}
                                    labelStyle="semibold"
                                  />

                                  {/* Target Module Count */}
                                  <TargetModuleCountInput
                                    value={targetModuleCount}
                                    onChange={setTargetModuleCount}
                                  />
                                </div>
                            </CollapsibleSection>

                            {/* Action Buttons */}
                            <ModuleActionButtons
                              onAutoFill={handleAutoFill}
                              onClearAll={() => setModules({ ...modules, [selectedPolygon]: [] })}
                              onClearSelection={() => {
                                setSelectedModules(new Set());
                                setSelectedModule(null);
                              }}
                              onDeleteSelected={() => {
                                if (selectedPolygon === null) return;
                                const polygonModules = modules[selectedPolygon] || [];
                                if (selectedModules.size > 0) {
                                  const remainingModules = polygonModules.filter((_, index) => !selectedModules.has(index));
                                  setModules({ ...modules, [selectedPolygon]: remainingModules });
                                  setSelectedModules(new Set());
                                } else if (selectedModule) {
                                  const { polygonId, index } = selectedModule;
                                  const mods = modules[polygonId] || [];
                                  const remainingModules = mods.filter((_, i) => i !== index);
                                  setModules({ ...modules, [polygonId]: remainingModules });
                                  setSelectedModule(null);
                                }
                              }}
                              hasSelection={selectedModules.size > 0 || selectedModule !== null}
                            />

                            {/* Segment-Based Module Placement */}
                            <SegmentDrawingPanel
                              isDrawing={isDrawingSegment}
                              isFreeFieldMode={isFreeFieldMode}
                              vertexCount={freeFieldPolygonVertices.length}
                              onStartDrawing={() => setIsDrawingSegment(true)}
                              onCancelDrawing={() => {
                                setIsDrawingSegment(false);
                                setPreviewModules([]);
                                setDragPreviewRect(null);
                                setFreeFieldPolygonVertices([]);
                              }}
                            />

                            {/* Segment List and Controls - Shows both roof segments AND free-field segments */}
                            {((selectedPolygon !== null && moduleSegments[selectedPolygon]?.length > 0) || moduleSegments[FREE_FIELD_POLYGON_ID]?.length > 0) && (
                              <div className="mt-4 pt-4 border-t border-gray-300">
                                <h3 className="text-sm font-bold text-gray-800 mb-2">
                                  Module Segments ({
                                    (selectedPolygon !== null ? (moduleSegments[selectedPolygon]?.length || 0) : 0) +
                                    (moduleSegments[FREE_FIELD_POLYGON_ID]?.length || 0)
                                  })
                                </h3>

                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {/* Roof-attached segments */}
                                  {selectedPolygon !== null && moduleSegments[selectedPolygon]?.map((segment, index) => {
                                    const isSelected = selectedSegment?.polygonId === selectedPolygon && selectedSegment?.index === index;
                                    const segmentArea = calculateSegmentArea(segment.bounds);
                                    const segmentCapacity = (segment.modules.length * modulePowerWatts / 1000).toFixed(2);

                                    return (
                                      <div
                                        key={`roof-${segment.id}`}
                                        onClick={() => setSelectedSegment({ polygonId: selectedPolygon, index })}
                                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                          isSelected
                                            ? 'bg-blue-100 border-2 border-blue-500'
                                            : 'bg-gray-50 border border-gray-300 hover:bg-gray-100'
                                        }`}
                                      >
                                        <div className="flex justify-between items-start mb-2">
                                          <span className="font-semibold text-gray-800">
                                            Segment {segment.id}
                                          </span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              // Delete segment
                                              const updatedSegments = moduleSegments[selectedPolygon].filter((_, i) => i !== index);
                                              setModuleSegments({
                                                ...moduleSegments,
                                                [selectedPolygon]: updatedSegments
                                              });
                                              // Remove segment modules
                                              const remainingModules = modules[selectedPolygon].filter(
                                                m => m.segmentId !== segment.id
                                              );
                                              setModules({
                                                ...modules,
                                                [selectedPolygon]: remainingModules
                                              });
                                              // Clear selection if deleted segment was selected
                                              if (isSelected) {
                                                setSelectedSegment(null);
                                              }
                                            }}
                                            className="text-red-500 hover:text-red-700 text-xs"
                                          >
                                            Delete
                                          </button>
                                        </div>
                                        <div className="text-xs text-gray-600 space-y-1">
                                          <div className="flex justify-between">
                                            <span>Modules:</span>
                                            <span className="font-semibold">{segment.modules.length}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span>Area:</span>
                                            <span className="font-semibold">{segmentArea.toFixed(1)} m²</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span>Capacity:</span>
                                            <span className="font-semibold text-blue-600">{segmentCapacity} kW</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* Free-field segments in the same list */}
                                  {moduleSegments[FREE_FIELD_POLYGON_ID]?.map((segment, index) => {
                                    const isSelected = selectedSegment?.polygonId === FREE_FIELD_POLYGON_ID && selectedSegment?.index === index;
                                    const segmentArea = calculateSegmentArea(segment.bounds);
                                    const segmentCapacity = (segment.modules.length * modulePowerWatts / 1000).toFixed(2);
                                    const segmentAzimuth = segment.azimuth ?? segment.rotation ?? 180;

                                    return (
                                      <div
                                        key={`free-${segment.id}`}
                                        onClick={() => setSelectedSegment({ polygonId: FREE_FIELD_POLYGON_ID, index })}
                                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                          isSelected
                                            ? 'bg-green-100 border-2 border-green-500'
                                            : 'bg-green-50 border border-green-300 hover:bg-green-100'
                                        }`}
                                      >
                                        <div className="flex justify-between items-start mb-2">
                                          <span className="font-semibold text-green-800 flex items-center gap-1">
                                            <span className="text-xs">🌱</span>
                                            FF-{segment.id}
                                          </span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const updatedSegments = moduleSegments[FREE_FIELD_POLYGON_ID].filter((_, i) => i !== index);
                                              setModuleSegments({
                                                ...moduleSegments,
                                                [FREE_FIELD_POLYGON_ID]: updatedSegments
                                              });
                                              const remainingModules = (modules[FREE_FIELD_POLYGON_ID] || []).filter(
                                                m => m.segmentId !== segment.id
                                              );
                                              setModules({
                                                ...modules,
                                                [FREE_FIELD_POLYGON_ID]: remainingModules
                                              });
                                              if (isSelected) setSelectedSegment(null);
                                            }}
                                            className="text-red-500 hover:text-red-700 text-xs"
                                          >
                                            Delete
                                          </button>
                                        </div>
                                        <div className="text-xs text-gray-600 space-y-1">
                                          <div className="flex justify-between">
                                            <span>Modules:</span>
                                            <span className="font-semibold">{segment.modules.length}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span>Azimuth:</span>
                                            <span className="font-semibold">{segmentAzimuth}°</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span>Capacity:</span>
                                            <span className="font-semibold text-green-600">{segmentCapacity} kW</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* NOTE: Segment configuration moved to Field Segments section below */}
                              </div>
                            )}
                          </div>
                          </div>
                        )}

                        <button
                          onClick={() => setSelectedPolygon(null)}
                          className="w-full py-2 px-4 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors mt-3"
                        >
                          Clear Selection
                        </button>
                      </div>
                    )}
                  </>
                ) : null;
              })()}

            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">File:</span> {selectedLayer}.tif
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Location:</span> notebooks/
              </p>
              {polygonData && polygonData.center && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Center:</span> {polygonData.center.lat.toFixed(6)}, {polygonData.center.lng.toFixed(6)}
                </p>
              )}
            </div>
          )}

          {/* Free-Field Segments Section - Show in System mode when no roof selected */}
          {siteSystemMode === 'system' && selectedPolygon === null && (
            <div className="mt-4 p-3 bg-cyan-50 border border-cyan-300 rounded-lg">
              <h4 className="text-sm font-bold text-cyan-800 mb-3 flex items-center gap-2">
                <span className="text-lg">📐</span>
                Field Segments ({(moduleSegments[FREE_FIELD_POLYGON_ID] || []).length})
              </h4>

              {/* Segment List */}
              {moduleSegments[FREE_FIELD_POLYGON_ID]?.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                  {moduleSegments[FREE_FIELD_POLYGON_ID].map((segment, index) => {
                    const isSelected = selectedSegment?.polygonId === FREE_FIELD_POLYGON_ID && selectedSegment?.index === index;
                    const segmentCapacity = (segment.modules.length * modulePowerWatts / 1000).toFixed(2);
                    const segmentAzimuth = segment.azimuth ?? segment.rotation ?? 180;

                    return (
                      <div
                        key={`ff-seg-${segment.id}`}
                        onClick={() => setSelectedSegment({ polygonId: FREE_FIELD_POLYGON_ID, index })}
                        className={`p-2 rounded cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-cyan-200 border-2 border-cyan-600'
                            : 'bg-white border border-cyan-200 hover:bg-cyan-100'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-semibold text-cyan-800 text-sm">
                              Segment {segment.id}
                            </span>
                            <div className="text-xs text-gray-600">
                              {segment.modules.length} modules • {segmentCapacity} kW • {segmentAzimuth}°
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const updatedSegments = moduleSegments[FREE_FIELD_POLYGON_ID].filter((_, i) => i !== index);
                              setModuleSegments({
                                ...moduleSegments,
                                [FREE_FIELD_POLYGON_ID]: updatedSegments
                              });
                              const remainingModules = (modules[FREE_FIELD_POLYGON_ID] || []).filter(
                                m => m.segmentId !== segment.id
                              );
                              setModules({
                                ...modules,
                                [FREE_FIELD_POLYGON_ID]: remainingModules
                              });
                              if (isSelected) setSelectedSegment(null);
                            }}
                            className="text-red-500 hover:text-red-700 text-xs font-semibold"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-600 mb-3">
                  No segments yet. Draw a free-field segment or fill a roof with modules.
                </p>
              )}

              {/* Draw Segment Button */}
              <FreeFieldDrawingPanel
                isDrawing={isDrawingSegment}
                onStartDrawing={() => setIsDrawingSegment(true)}
                onCancelDrawing={() => {
                  setIsDrawingSegment(false);
                  setFreeFieldPolygonVertices([]);
                }}
              />
            </div>
          )}

          {/* Free-Field Obstructions Section - Show when in free-field mode */}
          {isFreeFieldMode && (
            <div className="mt-4 p-3 bg-red-50 border border-red-300 rounded-lg">
              <h4 className="text-sm font-bold text-red-800 mb-3 flex items-center gap-2">
                <span className="text-lg">🚧</span>
                Free-Field Obstructions ({(obstructions[FREE_FIELD_POLYGON_ID] || []).length})
              </h4>

              {!isDrawingObstruction ? (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600">
                    Draw obstructions (trees, structures, etc.) that modules should avoid.
                  </p>
                  <ObstructionDrawButtons
                    onStartDrawing={(mode) => {
                      setObstructionDrawMode(mode);
                      setIsDrawingObstruction(true);
                    }}
                  />

                  {/* List existing obstructions */}
                  <ObstructionList
                    count={(obstructions[FREE_FIELD_POLYGON_ID] || []).length}
                    onDelete={(index) => {
                      const updated = (obstructions[FREE_FIELD_POLYGON_ID] || []).filter((_, i) => i !== index);
                      setObstructions({
                        ...obstructions,
                        [FREE_FIELD_POLYGON_ID]: updated,
                      });
                    }}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs text-gray-600 bg-red-100 border border-red-300 rounded p-2">
                    <p className="font-semibold mb-1">Drawing Obstruction:</p>
                    {obstructionDrawMode === 'rectangle' && (
                      <p>• Click and drag to draw rectangles</p>
                    )}
                    {obstructionDrawMode === 'circle' && (
                      <p>• Click center, then drag to set radius</p>
                    )}
                    {obstructionDrawMode === 'polygon' && (
                      <>
                        <p>• Click to add vertices</p>
                        <p>• Click origin or double-click to close</p>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => setIsDrawingObstruction(false)}
                    className="w-full py-2 px-4 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-semibold"
                  >
                    Done Drawing
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      )}

      {/* Right Panel - Map (Full width when collapsed) */}
      <div className="flex-1 h-full relative">
        {/* PHASE 1: NON-BLOCKING - Show map immediately if we have any center coordinate */}
        {canShowMap ? (
          <>
            {/* Leaflet Maps Mode (all modes including Google Maps as basemap) */}
            {(mapMode === 'geotiff-only' || mapMode === 'google-maps' || mapMode === 'leaflet-satellite' || mapMode === 'street-map') && (() => {
              const mapCenter = getMapCenter();
              return (
              <MapContainer
                center={[mapCenter.lat, mapCenter.lng]}
                zoom={mapZoom || 19}
                maxZoom={23}
                style={{
                  height: '100%',
                  width: '100%',
                  backgroundColor: mapMode === 'geotiff-only' ? '#ffffff' : 'transparent'
                }}
                zoomControl={false}
                scrollWheelZoom={true}
                dragging={false}  // Disabled — SpacebarPanController drives pan via panBy when nav mode is active
                // Use SVG renderer for clickable interactive elements (segments, handles)
                // Canvas renderer breaks click detection in SelectionBoxDrawer
                preferCanvas={false}
              >
                {/* Capture map instance for segment handle dragging */}
                <MapRefCapture mapRef={leafletMapRef} />

                {/* Handle map visibility changes (invalidate size when becoming visible) */}
                <MapVisibilityHandler isVisible={isVisible} />

                {/* Sync zoom level from Google map camera */}
                <ZoomSync zoom={mapZoom} />

                {/* Initialize custom panes for z-index control */}
                <CustomPanesInitializer />

                {/* PHASE 1: NON-BLOCKING - Show loading overlay while polygon data loads */}
                {/* This renders INSIDE the map as an overlay, not blocking the map */}
                {(loading || !polygonData) && (
                  <MapLoadingOverlay
                    status={polygonStatus}
                    cacheId={cacheId}
                    visible={true}
                    position="bottom-right"
                  />
                )}

                {/* Roof polygon drawing component - REFACTORED: Using extracted PolygonDrawer */}
                <PolygonDrawer
                  isDrawing={isDrawingRoof}
                  vertices={roofDrawingVertices}
                  onAddVertex={handleAddRoofVertex}
                  onFinish={handleFinishRoofPolygon}
                  onCancel={handleCancelRoofDrawing}
                  color="#EBFD03"
                  fillColor="#EBFD03"
                />

                {/* Ruler measurement tool - REFACTORED: Using extracted RulerTool */}
                <RulerTool
                  isActive={isRulerMode}
                  points={rulerPoints}
                  onAddPoint={(point) => {
                    setRulerPoints([...rulerPoints, point]);
                  }}
                  onClear={() => {
                    setRulerPoints([]);
                    setRulerCursorPos(null);
                    setIsRulerMode(false);
                  }}
                  calculateDistance={calculateDistance}
                  formatDistance={formatDistance}
                />

                {/* Polygon obstruction drawing component - REFACTORED: Using extracted ObstructionDrawer */}
                {isDrawingObstruction && obstructionDrawMode === 'polygon' && (
                  <ObstructionDrawer
                    isDrawing={true}
                    vertices={obstructionDrawingVertices}
                    onAddVertex={handleAddObstructionVertex}
                    onFinish={handleFinishPolygonObstruction}
                    onCancel={handleCancelPolygonObstruction}
                  />
                )}

                {/* Circle obstruction drawing component */}
                {isDrawingObstruction && obstructionDrawMode === 'circle' && (
                  <CircleObstructionDrawer
                    isDrawing={true}
                    center={circleObstructionCenter}
                    radius={circleObstructionRadius}
                    onSetCenter={handleSetCircleCenter}
                    onSetRadius={handleSetCircleRadius}
                    onFinish={handleFinishCircleObstruction}
                    onCancel={handleCancelCircleObstruction}
                  />
                )}

                {/* Google Maps satellite basemap - rendered as tiles inside Leaflet */}
                {mapMode === 'google-maps' && (
                  <TileLayer
                    url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                    attribution="Google"
                    maxZoom={22}
                  />
                )}

                {/* Satellite basemap - only show in leaflet-satellite mode */}
                {mapMode === 'leaflet-satellite' && (
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution="Esri, Maxar, Earthstar Geographics"
                    maxZoom={22}
                  />
                )}

                {/* Street map basemap - only show in street-map mode */}
                {mapMode === 'street-map' && (
                  <TileLayer
                    key="street-map-tiles"
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
                    attribution="Esri, HERE, Garmin, USGS, NGA, EPA, USDA, NPS"
                    maxZoom={20}
                    zIndex={1}
                  />
                )}

                {/* GeoTIFF ImageOverlay - Only show when polygon pipeline is complete */}
                {/* This prevents showing stale RGB from old cache while new location loads */}
                {getLeafletBounds() && polygonStatus === 'complete' && (() => {
                  // Determine which layer to render (flux or monthly_flux with month)
                  const renderLayer = (selectedLayer === 'flux' && showMonthlyFlux) ? 'monthly_flux' : selectedLayer;

                  // Determine if we should mask (only for flux/monthly_flux when polygon is selected)
                  const shouldMask = selectedPolygon !== null &&
                    (selectedLayer === 'flux' || (selectedLayer === 'flux' && showMonthlyFlux));

                  // Determine mask type: modules (if exist) or polygon (fallback)
                  const polygonModules = selectedPolygon !== null ? modules[selectedPolygon] : null;
                  const hasModules = polygonModules && polygonModules.length > 0 && siteSystemMode === 'system';

                  // Generate module masks (array of rectangles) or single polygon mask
                  const maskData = shouldMask ? (
                    hasModules
                      ? polygonModules.map(module =>
                          createModuleRectangle(
                            module.position,
                            module.width,
                            module.height,
                            module.orientation,
                            module.rotation || 0
                          )
                        )
                      : [getPolygonCoords(selectedPolygon)]
                  ) : null;

                  // Build URL helper function
                  const buildUrl = (layer: string, addMonth: boolean = false) => {
                    let url;
                    if (cacheId) {
                      url = `${API_URL}/api/geotiff/render/${layer}?cache_id=${cacheId}`;
                    } else {
                      url = `${API_URL}/api/geotiff/render/${layer}?use_test_files=${useTestFiles}`;
                    }
                    // Add month parameter if needed
                    if (addMonth && layer === 'monthly_flux') {
                      url += `&month=${selectedMonth}`;
                    }
                    return url;
                  };

                  // When flux is selected with a polygon, render TWO layers:
                  // 1. RGB base layer (full, no mask) - provides context outside modules/polygon
                  // 2. Flux/monthly_flux on top (masked to modules or polygon, full opacity)
                  if (shouldMask) {
                    // Create key that changes when modules or obstructions change
                    // Include module positions/orientations to detect moves/rotations/resizes
                    const moduleHash = hasModules && polygonModules
                      ? polygonModules.map(m => `${m.position[0].toFixed(6)},${m.position[1].toFixed(6)},${m.orientation},${m.rotation || 0}`).join('|')
                      : '';
                    const maskKey = `${renderLayer}-masked-${hasModules ? 'modules' : 'polygon'}-${polygonModules?.length || 0}-${moduleHash.substring(0, 20)}-${(selectedPolygon !== null && obstructions[selectedPolygon]) ? obstructions[selectedPolygon].length : 0}`;

                    return (
                      <React.Fragment key="masked-layers">
                        {/* RGB base layer - PRIORITY: loads first, triggers other layers when ready */}
                        {showRgbLayer && (
                          <GeoTiffImageOverlay
                            key="rgb-base"
                            url={buildUrl('rgb', false)}
                            bounds={getLeafletBounds()}
                            opacity={mapMode === 'geotiff-only' ? 1.0 : (mapMode === 'street-map' ? 0.6 : 0.8)}
                            layer="rgb"
                            maskPolygons={null}
                            onLoadingChange={(status) => {
                              if (status === 'success') setRgbLayerReady(true);
                            }}
                          />
                        )}
                        {/* Flux/monthly_flux layer - delayed until RGB loads (priority rendering) */}
                        {(rgbLayerReady || !showRgbLayer) && (
                          <GeoTiffImageOverlay
                            key={maskKey}
                            url={buildUrl(renderLayer, renderLayer === 'monthly_flux')}
                            bounds={getLeafletBounds()}
                            opacity={mapMode === 'street-map' ? 0.8 : 1.0}
                            layer={renderLayer}
                            maskPolygons={null}
                          />
                        )}
                      </React.Fragment>
                    );

                  }

                  // Default rendering: show RGB base + selected layer on top (if not RGB)
                  // This allows RGB to be toggled on/off while viewing DSM or Flux
                  return (
                    <React.Fragment key="default-layers">
                      {/* RGB base layer - PRIORITY: loads first when enabled */}
                      {showRgbLayer && renderLayer !== 'rgb' && (
                        <GeoTiffImageOverlay
                          key="rgb-base"
                          url={buildUrl('rgb', false)}
                          bounds={getLeafletBounds()}
                          opacity={mapMode === 'geotiff-only' ? 1.0 : (mapMode === 'street-map' ? 0.5 : 0.7)}
                          layer="rgb"
                          maskPolygons={null}
                          onLoadingChange={(status) => {
                            if (status === 'success') setRgbLayerReady(true);
                          }}
                        />
                      )}
                      {/* Selected layer (RGB, DSM, or Flux) - delayed until RGB loads if RGB is enabled */}
                      {(renderLayer !== 'rgb' || showRgbLayer) && (rgbLayerReady || renderLayer === 'rgb' || !showRgbLayer) && (
                        <GeoTiffImageOverlay
                          key={`${renderLayer}-default`}
                          url={buildUrl(renderLayer, renderLayer === 'monthly_flux')}
                          bounds={getLeafletBounds()}
                          opacity={
                            renderLayer === 'rgb'
                              ? (mapMode === 'geotiff-only' ? 1.0 : (mapMode === 'street-map' ? 0.6 : 0.8))
                              : (mapMode === 'street-map' ? 0.5 : 0.7)
                          }
                          layer={renderLayer}
                          maskPolygons={null}
                          onLoadingChange={(status) => {
                            // If RGB is selected layer, also mark RGB as ready
                            if (renderLayer === 'rgb' && status === 'success') setRgbLayerReady(true);
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })()}

                {/* Polygon overlays - PHASE 2: Stable rendering with RoofPolygonLayer */}
                {showPolygons && polygonData?.polygons && (
                  <RoofPolygonLayer
                    polygons={polygonData.polygons}
                    selectedPolygon={selectedPolygon}
                    selectedPolygons={selectedPolygons}
                    editMode={editMode}
                    showAllPolygons={showAllPolygons}
                    editedPolygonCoords={editedPolygonCoords}
                    originalPolygonCoords={originalPolygonCoords}
                    editingPolygonVertex={editingPolygonVertex}
                    selectedPolygonForEdit={selectedPolygonForEdit}
                    isDrawingObstruction={isDrawingObstruction}
                    isDrawingSegment={isDrawingSegment}
                    isRoofSelectMode={isRoofSelectMode}
                    isAddRoofMode={isAddRoofMode}
                    bypassPolygonFetch={bypassPolygonFetch}
                    getPolygonStyle={getPolygonStyle}
                    onPolygonClick={handlePolygonClick}
                    onPolygonDoubleClick={handlePolygonDoubleClick}
                    onPolygonMove={handlePolygonMove}
                    removeVertexOnClick={removeVertexMode}
                    addVertexOnClick={addVertexMode}
                  />
                )}

                {/* Edge measurements */}
                {showMeasurements && selectedPolygon !== null && (
                  <EdgeMeasurements
                    coordinates={getPolygonCoords(selectedPolygon)}
                    show={showMeasurements}
                  />
                )}

                {/* Highlight selected snap edge */}
                {selectedPolygon !== null && snapEdgeIndex[selectedPolygon] !== undefined && (
                  <SnapEdgeHighlight
                    coords={getPolygonCoords(selectedPolygon)}
                    edgeIndex={snapEdgeIndex[selectedPolygon]}
                  />
                )}

                {/* Edge indicators for snap edge selection */}
                {selectedPolygon !== null && (isSelectingSnapEdge || snapEdgeIndex[selectedPolygon] !== undefined) && (
                  <EdgeIndicators
                    coords={getPolygonCoords(selectedPolygon)}
                    selectedEdgeIndex={snapEdgeIndex[selectedPolygon] ?? -1}
                    isSelectMode={isSelectingSnapEdge}
                    onEdgeClick={(index) => {
                      setSnapEdgeIndex({
                        ...snapEdgeIndex,
                        [selectedPolygon]: index,
                      });
                      // Calculate and store the azimuth for this edge
                      const coords = getPolygonCoords(selectedPolygon);
                      if (index >= 0 && index < coords.length - 1) {
                        const start = coords[index];
                        const end = coords[index + 1];
                        const dLat = end[0] - start[0];
                        const dLng = end[1] - start[1];
                        const metersPerDegreeLat = 111320;
                        const metersPerDegreeLng = 111320 * Math.cos(start[0] * Math.PI / 180);
                        const dy = dLat * metersPerDegreeLat;
                        const dx = dLng * metersPerDegreeLng;
                        let edgeAzimuth = Math.atan2(dx, dy) * (180 / Math.PI);
                        edgeAzimuth = (edgeAzimuth + 360) % 360;
                        let faceAzimuth = (edgeAzimuth + 90) % 360;
                        setSnapEdgeAzimuth({
                          ...snapEdgeAzimuth,
                          [selectedPolygon]: faceAzimuth,
                        });
                      }
                    }}
                  />
                )}

                {/* Render obstructions for all polygons - always visible on top */}
                {Object.entries(obstructions).map(([polygonId, polygonObstructions]) =>
                  polygonObstructions.map((obstruction, index) => {
                    const isEditing = editingObstruction?.polygonId === Number(polygonId) &&
                                     editingObstruction?.index === index;

                    if (isEditing) {
                      // Render editable version
                      return (
                        <EditablePolygon
                          key={`obstruction-edit-${polygonId}-${index}`}
                          coordinates={obstruction}
                          onCoordinatesChange={(newCoords) => {
                            handleMoveObstruction(Number(polygonId), index, newCoords);
                          }}
                          style={{
                            color: '#6b7280', // Gray
                            fillColor: '#ffffff', // White
                            fillOpacity: 0.45,
                            weight: 2,
                            dashArray: '5, 5',
                          }}
                          vertexFillColor="#ffffff"
                          vertexBorderColor="#6b7280"
                          removeVertexOnClick={removeVertexMode}
                        />
                      );
                    }

                    // Render draggable version (only interactive in Site mode)
                    return (
                      <DraggableObstruction
                        key={`obstruction-${polygonId}-${index}`}
                        obstruction={obstruction}
                        isSelected={
                          editMode === 'site' &&
                          selectedObstruction?.polygonId === Number(polygonId) &&
                          selectedObstruction?.index === index
                        }
                        onSelect={() => {
                          if (editMode === 'site' && !isDrawingObstruction) {
                            setSelectedObstruction({ polygonId: Number(polygonId), index });
                          }
                        }}
                        onMove={(newCoords) => {
                          if (editMode === 'site' && !isDrawingObstruction) {
                            handleMoveObstruction(Number(polygonId), index, newCoords);
                          }
                        }}
                        onRotate={(newCoords) => {
                          if (editMode === 'site' && !isDrawingObstruction) {
                            handleMoveObstruction(Number(polygonId), index, newCoords);
                          }
                        }}
                        onDoubleClick={() => {
                          if (editMode === 'site' && !isDrawingObstruction) {
                            setEditingObstruction({ polygonId: Number(polygonId), index });
                          }
                        }}
                        onDuplicate={(duplicateCoords) => {
                          if (editMode === 'site' && !isDrawingObstruction) {
                            const polygonObstructions = obstructions[Number(polygonId)] || [];
                            const newIndex = polygonObstructions.length;
                            setObstructions({
                              ...obstructions,
                              [Number(polygonId)]: [...polygonObstructions, duplicateCoords],
                            });
                            // Select the newly created duplicate
                            setSelectedObstruction({ polygonId: Number(polygonId), index: newIndex });
                          }
                        }}
                      />
                    );
                  })
                )}

                {/* Obstruction drawing component - works for roof polygons, free-field mode, and global obstructions */}
                {isDrawingObstruction && (() => {
                  let bounds: [number, number][];
                  let rotation: number;

                  if (isFreeFieldMode || selectedPolygon === null) {
                    // Free-field mode or no polygon selected: use a large default area
                    // Use the map center with a ~100m x 100m area for unrestricted drawing
                    const center = getMapCenter();
                    const offset = 0.001; // ~100 meters
                    bounds = [
                      [center.lat - offset, center.lng - offset],
                      [center.lat - offset, center.lng + offset],
                      [center.lat + offset, center.lng + offset],
                      [center.lat + offset, center.lng - offset],
                    ];
                    rotation = isFreeFieldMode ? freeFieldAzimuth : 0; // Use free-field azimuth or default to 0
                  } else {
                    const poly = getSelectedPolygonData();
                    if (!poly) return null;
                    // Use edited coords if available
                    bounds = editedPolygonCoords[selectedPolygon!] ||
                      poly.geometry.coordinates[0].map(
                        ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
                      );
                    rotation = snapEdgeIndex[selectedPolygon!] !== undefined
                      ? getSnapEdgeAngle(selectedPolygon!)
                      : 0;
                  }

                  return (
                    <ModuleBoxDrawer
                      isDrawing={isDrawingObstruction}
                      polygonBounds={bounds}
                      onBoxCreated={handleRectangleCreated}
                      onDragUpdate={handleDragUpdate}
                      rotation={rotation}
                    />
                  );
                })()}

                {/* Module segment drawing - Free-field mode uses polygon drawing (click-to-add vertices) */}
                {isDrawingSegment && isFreeFieldMode && (
                  <PolygonDrawer
                    isDrawing={isDrawingSegment}
                    vertices={freeFieldPolygonVertices}
                    onAddVertex={handleFreeFieldAddVertex}
                    onFinish={handleFreeFieldFinish}
                    onCancel={handleFreeFieldCancel}
                    color="#00ff00"
                    fillColor="#00ff00"
                  />
                )}

                {/* Module segment drawing - Roof-attached mode uses drag rectangle */}
                {isDrawingSegment && !isFreeFieldMode && selectedPolygon !== null && (() => {
                  const poly = getSelectedPolygonData();
                  if (!poly) return null;
                  // Use edited coords if available
                  const bounds = editedPolygonCoords[selectedPolygon!] ||
                    poly.geometry.coordinates[0].map(
                      ([lng, lat]: [number, number]) => [lat, lng] as [number, number]
                    );
                  const rotation = snapEdgeIndex[selectedPolygon!] !== undefined
                    ? getSnapEdgeAngle(selectedPolygon!)
                    : 0;

                  return (
                    <ModuleBoxDrawer
                      isDrawing={isDrawingSegment}
                      polygonBounds={bounds}
                      onBoxCreated={handleSegmentCreated}
                      onDragUpdate={handleDragUpdate}
                      rotation={rotation}
                    />
                  );
                })()}

                {/* Preview Modules - Semi-transparent while drawing */}
                {previewModules.length > 0 && previewModules.map((module, index) => {
                  const rect = createModuleRectangle(
                    module.position,
                    module.width,
                    module.height,
                    module.orientation,
                    module.rotation
                  );
                  // Normalize coordinates
                  const normalizedRect = rect.map(([lat, lng]) => [
                    Math.round(lat * 1e8) / 1e8,
                    Math.round(lng * 1e8) / 1e8
                  ] as [number, number]);
                  return (
                    <Polygon
                      key={`preview-module-${index}`}
                      positions={normalizedRect}
                      pathOptions={{
                        color: '#EBFD03',
                        fillColor: '#EBFD03',
                        fillOpacity: 0.3,
                        weight: 1,
                        dashArray: '4, 4',
                      }}
                      pane="modulesPane"
                      interactive={false}
                    />
                  );
                })}

                {/* Segment Boundaries Rendering - Only visible in System mode */}
                {/* Render segments for selected polygon AND free-field segments (id: -1) */}
                {siteSystemMode === 'system' && Object.entries(moduleSegments)
                  .filter(([polygonId]) => {
                    const id = Number(polygonId);
                    // Always show free-field segments
                    if (id === FREE_FIELD_POLYGON_ID) return true;
                    // Show polygon segments when no polygon selected or when it matches
                    return selectedPolygon === null || id === selectedPolygon;
                  })
                  .map(([polygonId, segments]) =>
                  segments.map((segment, segmentIndex) => {
                    // Validate segment bounds - skip rendering if invalid coordinates
                    const hasValidBounds = segment.bounds?.length >= 3 &&
                      segment.bounds.every(([lat, lng]: [number, number]) =>
                        !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)
                      );

                    if (!hasValidBounds) {
                      console.warn(`[Segment ${segment.id}] Invalid bounds, skipping render`);
                      return null;
                    }

                    // Normalize coordinates to 8 decimal places to prevent floating point precision issues at high zoom
                    const normalizedBounds = segment.bounds.map(([lat, lng]: [number, number]) => [
                      Math.round(lat * 1e8) / 1e8,
                      Math.round(lng * 1e8) / 1e8
                    ] as [number, number]);

                    const isSegmentSelected = selectedSegment?.polygonId === Number(polygonId) && selectedSegment?.index === segmentIndex;
                    const isEditingVertices = editingSegmentVertices?.polygonId === Number(polygonId) && editingSegmentVertices?.index === segmentIndex;
                    const isFreeFieldSegment = Number(polygonId) === FREE_FIELD_POLYGON_ID;

                    // If segment is in vertex edit mode, render EditablePolygon instead
                    if (isEditingVertices) {
                      // Add closing point for EditablePolygon
                      const coordsWithClose: [number, number][] = [
                        ...segment.bounds,
                        segment.bounds[0] // Close the polygon
                      ];

                      return (
                        <React.Fragment key={`segment-edit-${polygonId}-${segmentIndex}`}>
                          <EditablePolygon
                            coordinates={coordsWithClose}
                            onCoordinatesChange={(newCoords) => handleSegmentVerticesChange(Number(polygonId), segmentIndex, newCoords)}
                            style={{
                              color: '#ff6600',
                              weight: 3,
                              fillColor: '#ff6600',
                              fillOpacity: 0.1,
                            }}
                            vertexFillColor="#ff6600"
                            vertexBorderColor="#ffffff"
                            removeVertexOnClick={removeVertexMode}
                            addVertexOnClick={addVertexMode}
                          />
                        </React.Fragment>
                      );
                    }

                    return (
                      <React.Fragment key={`segment-${polygonId}-${segmentIndex}`}>
                        {/* Segment boundary polygon - cyan for roof segments, green for free-field */}
                        <Polygon
                          positions={normalizedBounds}
                          pathOptions={{
                            color: isSegmentSelected ? '#00ff00' : (isFreeFieldSegment ? '#22c55e' : '#00ffff'),
                            fillColor: isSegmentSelected ? 'rgba(0,255,0,0.15)' : (isFreeFieldSegment ? 'rgba(34,197,94,0.08)' : 'rgba(0,255,255,0.08)'),
                            weight: isSegmentSelected ? 4 : 3,
                            dashArray: isSegmentSelected ? undefined : '8, 4', // Solid when selected, dashed otherwise
                            opacity: 1,
                          }}
                          pane="segmentPane"
                          eventHandlers={{
                            click: () => {
                              setSelectedSegment({ polygonId: Number(polygonId), index: segmentIndex });
                            },
                            dblclick: () => {
                              // Double-click to enter vertex edit mode
                              setEditingSegmentVertices({ polygonId: Number(polygonId), index: segmentIndex });
                            },
                            mousedown: (e: any) => {
                              if (e.originalEvent.button !== 0) return; // Only left click

                              setIsDraggingSegment(true);
                              draggingSegmentRef.current = { polygonId: Number(polygonId), index: segmentIndex };
                              segmentDragStartPosRef.current = [e.latlng.lat, e.latlng.lng];
                              segmentDragOffsetRef.current = [0, 0];
                              leafletMapRef.current?.dragging.disable();
                              L.DomEvent.stopPropagation(e);
                            },
                          }}
                        />


                        {/* Editing Handles (only show when segment is selected) */}
                        {isSegmentSelected && (
                          <>
                            {/* Corner Resize Handles (blue squares) */}
                            {segment.bounds.map((corner, cornerIndex) => (
                              <CircleMarker
                                key={`corner-${cornerIndex}`}
                                center={corner}
                                radius={6}
                                pathOptions={{
                                  color: '#0000ff',
                                  fillColor: '#0000ff',
                                  fillOpacity: 1,
                                  weight: 2
                                }}
                                pane="segmentHandlesPane"
                                eventHandlers={{
                                  mousedown: (e: any) => {
                                    L.DomEvent.stopPropagation(e);

                                    resizingCornerIndexRef.current = cornerIndex;
                                    resizingSegmentBoundsRef.current = segment.bounds;
                                    setIsResizingSegmentCorner(true);
                                    leafletMapRef.current?.dragging.disable();
                                  }
                                }}
                              />
                            ))}

                            {/* Edge Midpoint Handles (green diamonds) - Click to snap azimuth, Drag to stretch */}
                            {segment.bounds.map((corner, i) => {
                              const nextCorner = segment.bounds[(i + 1) % segment.bounds.length];
                              const midpoint: [number, number] = [
                                (corner[0] + nextCorner[0]) / 2,
                                (corner[1] + nextCorner[1]) / 2
                              ];

                              // Calculate edge azimuth for this edge
                              const latDiff = nextCorner[0] - corner[0];
                              const lngDiff = nextCorner[1] - corner[1];
                              // Convert to meters (approximate)
                              const latMeters = latDiff * 111320;
                              const lngMeters = lngDiff * 111320 * Math.cos(corner[0] * Math.PI / 180);
                              // Calculate bearing (azimuth from North, clockwise)
                              let edgeBearing = Math.atan2(lngMeters, latMeters) * (180 / Math.PI);
                              edgeBearing = (edgeBearing + 360) % 360;
                              // Face azimuth is perpendicular to edge (modules face this direction)
                              const faceAzimuth = Math.round((edgeBearing + 90) % 360);

                              return (
                                <CircleMarker
                                  key={`edge-${i}`}
                                  center={midpoint}
                                  radius={6}
                                  pathOptions={{
                                    color: '#00ff00',
                                    fillColor: '#00ff00',
                                    fillOpacity: 1,
                                    weight: 2
                                  }}
                                  pane="segmentHandlesPane"
                                  eventHandlers={{
                                    click: (e: any) => {
                                      L.DomEvent.stopPropagation(e);

                                      // Only snap azimuth on click if NO drag occurred
                                      // This prevents accidental rotation when stretching the edge
                                      if (edgeDragOccurredRef.current) {
                                        edgeDragOccurredRef.current = false;
                                        return;
                                      }

                                      // Update segment azimuth and refill in one operation
                                      // Create updated segment with new azimuth
                                      const updatedSegment = {
                                        ...segment,
                                        rotation: faceAzimuth,
                                        azimuth: faceAzimuth
                                      };

                                      // Calculate new modules with the new azimuth
                                      const isFreeFieldSegment = Number(polygonId) === FREE_FIELD_POLYGON_ID || segment.freeField;
                                      const newModules = calculateModulesForBox(
                                        segment.bounds,
                                        true,
                                        faceAzimuth,
                                        segment.config,
                                        isFreeFieldSegment
                                      );

                                      // Assign segment ID to new modules
                                      const modulesWithSegmentId = newModules.map(m => ({
                                        ...m,
                                        segmentId: segment.id,
                                        freeField: isFreeFieldSegment
                                      }));

                                      // Update segment with modules
                                      updatedSegment.modules = modulesWithSegmentId;

                                      // Update segments state
                                      const updatedSegments = [...moduleSegments[Number(polygonId)]];
                                      updatedSegments[segmentIndex] = updatedSegment;
                                      setModuleSegments({
                                        ...moduleSegments,
                                        [polygonId]: updatedSegments
                                      });

                                      // Update modules state - remove old, add new
                                      const polygonModules = modules[Number(polygonId)] || [];
                                      const otherModules = polygonModules.filter(m => m.segmentId !== segment.id);
                                      setModules({
                                        ...modules,
                                        [Number(polygonId)]: [...otherModules, ...modulesWithSegmentId]
                                      });

                                    },
                                    mousedown: (e: any) => {
                                      L.DomEvent.stopPropagation(e);

                                      // Reset drag flag - will be set to true if mouse moves
                                      edgeDragOccurredRef.current = false;

                                      stretchingEdgeIndexRef.current = i;
                                      stretchingSegmentBoundsRef.current = segment.bounds;
                                      edgeDragStartPosRef.current = [e.latlng.lat, e.latlng.lng];
                                      setIsStretchingSegmentEdge(true);
                                      leafletMapRef.current?.dragging.disable();
                                    }
                                  }}
                                />
                              );
                            })}

                            {/* Rotation Handle (purple circle OUTSIDE segment) */}
                            {(() => {
                              // Calculate center of segment
                              const centerLat = segment.bounds.reduce((sum, [lat]) => sum + lat, 0) / segment.bounds.length;
                              const centerLng = segment.bounds.reduce((sum, [, lng]) => sum + lng, 0) / segment.bounds.length;

                              // Position rotation handle outside first corner, 30% beyond center-to-corner distance
                              const rotationCorner = segment.bounds[0];
                              const rotationHandleLat = rotationCorner[0] + (rotationCorner[0] - centerLat) * 0.3;
                              const rotationHandleLng = rotationCorner[1] + (rotationCorner[1] - centerLng) * 0.3;

                              return (
                                <CircleMarker
                                  center={[rotationHandleLat, rotationHandleLng]}
                                  radius={8}
                                  pathOptions={{
                                    color: '#ff00ff',
                                    fillColor: '#ff00ff',
                                    fillOpacity: 0.8,
                                    weight: 3
                                  }}
                                  pane="segmentHandlesPane"
                                  eventHandlers={{
                                    mousedown: (e: any) => {
                                      L.DomEvent.stopPropagation(e);

                                      // Calculate initial angle from center to rotation handle
                                      const angle = Math.atan2(
                                        rotationHandleLat - centerLat,
                                        rotationHandleLng - centerLng
                                      );

                                      segmentRotationAngleRef.current = angle;
                                      rotatingSegmentBoundsRef.current = segment.bounds;
                                      setIsRotatingSegment(true);
                                      leafletMapRef.current?.dragging.disable();
                                    }
                                  }}
                                />
                              );
                            })()}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })
                )}

                {/* Solar Modules Rendering - Only show in System mode for performance */}
                {/* In embed Ajustar área keep panels visible so user sees layout while moving nodes; else hide during vertex edit */}
                {((siteSystemMode === 'system' && !editingPolygonVertex && !selectedPolygonForEdit) || (hideControls && editingPolygonVertex !== null)) && Object.entries(modules)
                  .filter(([polygonId]) => {
                    const id = Number(polygonId);
                    // Always show free-field modules
                    if (id === FREE_FIELD_POLYGON_ID) return true;
                    // Show modules for ALL established roofs (not just selected ones)
                    return establishedRoofIds.size === 0 || establishedRoofIds.has(id);
                  })
                  .map(([polygonId, polygonModules]) =>
                  polygonModules.map((module, index) => {
                    const moduleId = `${polygonId}-${index}`;
                    const isSingleSelected = selectedModule?.polygonId === Number(polygonId) && selectedModule?.index === index;
                    const isMultiSelected = selectedModules.has(index);

                    return (
                      <DraggableModule
                        key={`module-${moduleId}`}
                        module={module}
                        isSelected={isSingleSelected || isMultiSelected}
                        isInteractive={true}
                        opacity={selectedLayer === 'flux' ? 0 : 0.7}
                        onSelect={() => {
                          setSelectedModule({ polygonId: Number(polygonId), index });
                        }}
                        onMove={(newPosition) => {
                          // Modules in segments cannot be dragged individually
                          if (module.segmentId !== undefined) {
                            return;
                          }

                          // Only allow individual drag if no multi-selection active
                          if (selectedModules.size === 0) {
                            const updated = [...polygonModules];
                            updated[index] = { ...module, position: newPosition };
                            setModules({ ...modules, [Number(polygonId)]: updated });
                          }
                        }}
                        onClick={(e) => {

                          // Shift + Click = Select entire segment (all modules in same segment)
                          if (e.originalEvent?.shiftKey && module.segmentId !== undefined) {

                            // Select all modules in the same segment
                            const newSelection = new Set<number>();
                            polygonModules.forEach((m, idx) => {
                              if (m.segmentId === module.segmentId) {
                                newSelection.add(idx);
                              }
                            });

                            setSelectedModules(newSelection);
                            setSelectedModule(null); // Clear single selection
                          }
                          // Shift + Click but no segmentId
                          else if (e.originalEvent?.shiftKey && module.segmentId === undefined) {
                            console.warn('  ⚠️ Module has no segmentId, cannot select segment');
                          }
                          // Cmd/Ctrl + Click = Toggle individual module in multi-selection
                          else if (e.originalEvent?.metaKey || e.originalEvent?.ctrlKey) {
                            const newSelection = new Set(selectedModules);
                            if (newSelection.has(index)) {
                              newSelection.delete(index);
                            } else {
                              newSelection.add(index);
                            }
                            setSelectedModules(newSelection);
                            setSelectedModule(null); // Clear single selection
                          }
                          // Normal Click = Single selection
                          else {
                            setSelectedModule({ polygonId: Number(polygonId), index });
                            setSelectedModules(new Set()); // Clear multi-selection
                          }
                        }}
                      />
                    );
                  })
                )}

                {/* Multi-Selection Bounding Box (only for non-segment modules) */}
                {(() => {
                  const bounds = getSelectedModulesBounds();
                  if (!bounds) return null;

                  // Don't show selection box for modules that belong to segments
                  if (selectedPolygon !== null) {
                    const polygonModules = modules[selectedPolygon] || [];
                    const hasSegmentModules = Array.from(selectedModules).some(index => {
                      const module = polygonModules[index];
                      return module && module.segmentId !== undefined;
                    });

                    if (hasSegmentModules) {
                      return null;
                    }
                  }

                  return (
                    <MultiSelectionBox
                      bounds={bounds}
                      onMove={handleMoveSelectedModules}
                      onCopy={handleCopySelectedModules}
                      // onExpandEdge removed - using segment system handles instead
                    />
                  );
                })()}

                {/* Selection Box for Drag-to-Select (System Mode only) */}
                {editMode === 'system' && !isDrawingSegment && !isPlacingModule && (
                  <SelectionBoxDrawer
                    isActive={true}
                    onBoxDrawn={handleModuleSelection}
                    modules={modules}
                    selectedPolygon={selectedPolygon}
                  />
                )}

                {/* Fit to bounds - only on initial load when visible */}
                {getLeafletBounds() && (
                  <FitBounds
                    bounds={getLeafletBounds()}
                    shouldFit={!hasInitialFit}
                    isVisible={isVisible}
                    onFitted={() => setHasInitialFit(true)}
                  />
                )}

                {/* Pin Marker - Draggable Red Pointer (Google Maps style) */}
                {showPinMarker && pinPosition && (
                  <Marker
                    position={[pinPosition.lat, pinPosition.lng]}
                    draggable={!!setPinPosition}
                    icon={createRedPointerIcon()}
                    eventHandlers={{
                      dragend: (e: any) => {
                        if (setPinPosition) {
                          const newPos = e.target.getLatLng();
                          setPinPosition({ lat: newPos.lat, lng: newPos.lng });
                        }
                      }
                    }}
                  />
                )}

                {/* Control map interactions based on edit mode */}
                <MapInteractionController isEditing={isEditing} />

                {/* Spacebar + drag for map panning */}
                <SpacebarPanController />
              </MapContainer>
              );
            })()}
          </>
        ) : (
          /* PHASE 1: Fallback when we don't even have a center coordinate (rare edge case) */
          <div className="flex items-center justify-center h-full bg-gray-100">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EBFD03] mx-auto mb-4"></div>
              <p className="text-gray-600">Waiting for location data...</p>
              <p className="text-gray-400 text-sm mt-2">Provide coordinates or search for an address</p>
            </div>
          </div>
        )}

        {/* Embed: botón para entrar en modo edición del segmento cuando no está editando */}
        {hideControls && editingSegmentVertices === null && (
          <div className="absolute left-4 bottom-24 z-[9999]">
            <button
              type="button"
              onClick={() => {
                // Enter vertex edit mode for the first available module segment
                const firstEntry = Object.entries(moduleSegments).find(([, segs]) => segs.length > 0);
                if (firstEntry) {
                  setEditingSegmentVertices({ polygonId: Number(firstEntry[0]), index: 0 });
                }
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl shadow-xl text-sm transition-colors"
            >
              <Edit3 className="h-4 w-4" />
              Editar área
            </button>
          </div>
        )}

        {/* Embed: panel de modos cuando está editando el segmento */}
        {hideControls && editingSegmentVertices !== null && (
          <div className="absolute left-4 bottom-24 z-[9999] w-[220px] bg-white/95 backdrop-blur rounded-xl shadow-xl border-2 border-amber-400 p-3">
            <p className="text-xs font-semibold text-gray-800 mb-2">Editando área</p>
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              <button
                type="button"
                onClick={() => { setAddVertexMode((v) => !v); setRemoveVertexMode(false); }}
                className={`py-2 px-1 rounded-lg text-xs font-semibold transition-colors ${
                  addVertexMode ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Agregar nodo
              </button>
              <button
                type="button"
                onClick={() => { setRemoveVertexMode((v) => !v); setAddVertexMode(false); }}
                className={`py-2 px-1 rounded-lg text-xs font-semibold transition-colors ${
                  removeVertexMode ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Eliminar nodo
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2.5 min-h-[2rem]">
              {!removeVertexMode && !addVertexMode && 'Arrastrá los nodos para mover.'}
              {addVertexMode && 'Clic en el borde para agregar un nodo.'}
              {removeVertexMode && 'Clic en un nodo para eliminarlo.'}
            </p>
            <button
              type="button"
              onClick={() => { setEditingSegmentVertices(null); setRemoveVertexMode(false); setAddVertexMode(false); }}
              className="w-full py-2 px-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold transition-colors"
            >
              Listo
            </button>
          </div>
        )}

        {/* Aurora Controls - Hidden in embed mode (hideControls) */}
        {!hideControls && (
        <div className={`absolute left-4 top-4 bottom-20 z-[9999] ${menuCollapsed ? 'w-16' : (compactMode ? 'w-60' : 'w-72')} transition-all duration-300 max-h-[calc(100vh-7rem)] ${menuCollapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
          {/* Unified Menu Container */}
          <div className="bg-white/95 backdrop-blur rounded-lg shadow-xl border-2 border-gray-300">
            {/* Collapse/Expand Button */}
            <div className="flex items-center justify-center p-2 border-b-2 border-gray-300">
              <button
                onClick={() => setMenuCollapsed(!menuCollapsed)}
                className="w-full flex items-center justify-center hover:bg-gray-100 rounded-lg p-1 transition-colors"
                title={menuCollapsed ? "Expand Menu" : "Collapse Menu"}
              >
                {menuCollapsed ? (
                  <ChevronDown className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronUp className="h-5 w-5 text-gray-600" />
                )}
              </button>
            </div>

            {!menuCollapsed && (
              <>
                {/* Bypass Mode Banner (Extracted Component) */}
                <BypassModeBanner show={bypassPolygonFetch} />

                {/* Mode Switcher (Site/System) */}
                <div className="p-3 border-b-2 border-gray-300">
                  <ModeSwitcher
                    mode={siteSystemMode}
                    compact={false}
                    onChange={(newMode) => {
                      setSiteSystemMode(newMode);
                      setEditMode(newMode);
                      if (newMode === 'system') {
                        setIsDrawingObstruction(false);
                        setSelectedObstruction(null);
                        setEditingObstruction(null);
                      } else {
                        setSelectedSegment(null);
                        setIsDraggingSegment(false);
                        setIsResizingSegmentCorner(false);
                        setIsRotatingSegment(false);
                        setIsStretchingSegmentEdge(false);
                        setIsDrawingSegment(false);
                        setPreviewModules([]);
                        setSelectedModule(null);
                        setSelectedModules(new Set());
                        setIsPlacingModule(false);
                      }
                    }}
                  />
                </div>

                {/* Segment vertex edit bar: when editing segment vertices (double-click on segment) */}
                {editingSegmentVertices !== null && (
                  <div className="p-3 border-b-2 border-orange-300 bg-orange-50">
                    <p className="text-xs font-semibold text-orange-800 mb-2">Editando vértices del segmento</p>
                    <div className="text-xs text-gray-600 space-y-1 mb-2">
                      <p>• Arrastra nodos para ajustar</p>
                      <p>• Alt/Option+click en borde: añadir nodo</p>
                      <p>• Alt/Option+click en nodo: eliminarlo, o usa el botón</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setRemoveVertexMode((v) => !v)}
                        className={`flex-1 min-w-0 py-2 px-3 rounded-lg text-xs font-semibold transition-colors ${
                          removeVertexMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                        title={removeVertexMode ? 'Desactivar' : 'Activar: clic en un nodo lo elimina'}
                      >
                        {removeVertexMode ? '✓ Eliminar nodo (activo)' : 'Eliminar nodo'}
                      </button>
                      <button
                        onClick={() => { setEditingSegmentVertices(null); setRemoveVertexMode(false); }}
                        className="flex-1 min-w-0 py-2 px-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-xs font-semibold"
                      >
                        Listo
                      </button>
                    </div>
                  </div>
                )}

                {/* Menu Items - Site Mode */}
                {siteSystemMode === 'site' && (
                  <SiteModePanel
                    roofCardCollapsed={roofCardCollapsed}
                    setRoofCardCollapsed={setRoofCardCollapsed}
                    obstructionCardCollapsed={obstructionCardCollapsed}
                    setObstructionCardCollapsed={setObstructionCardCollapsed}
                    showMeasurements={showMeasurements}
                    setShowMeasurements={setShowMeasurements}
                    showAllPolygons={showAllPolygons}
                    setShowAllPolygons={setShowAllPolygons}
                    roofs={Array.from(establishedRoofIds).map(roofId => ({
                      id: roofId,
                      area: calculatePolygonArea(getPolygonCoords(roofId)),
                      moduleCount: modules[roofId]?.length || 0,
                    }))}
                    activeRoofId={activeRoofId}
                    selectedPolygon={selectedPolygon}
                    onSelectRoof={(roofId) => {
                      setSelectedPolygon(roofId);
                      setActiveRoofId(roofId);
                    }}
                    onRemoveRoof={(roofId) => {
                      unestablishRoof(roofId);
                      const newSelection = selectedPolygons.filter(id => id !== roofId);
                      setSelectedPolygons(newSelection);
                      if (selectedPolygon === roofId) {
                        if (newSelection.length > 0) {
                          setSelectedPolygon(newSelection[0]);
                          setActiveRoofId(newSelection[0]);
                        } else {
                          setSelectedPolygon(null);
                          setActiveRoofId(null);
                        }
                      }
                      const newModules = { ...modules };
                      delete newModules[roofId];
                      setModules(newModules);
                    }}
                    isRoofSelectMode={isRoofSelectMode}
                    setIsRoofSelectMode={setIsRoofSelectMode}
                    calculatePolygonArea={calculatePolygonArea}
                    getPolygonCoords={getPolygonCoords}
                    isRoofEstablished={isRoofEstablished}
                    onConvertToRoof={async (polygonId) => {
                      if (snapEdgeIndex[polygonId] === undefined) {
                        const coords = getPolygonCoords(polygonId);
                        const bestEdgeIndex = autoDetectBestEdge(coords);
                        setSnapEdgeIndex({ ...snapEdgeIndex, [polygonId]: bestEdgeIndex });
                      }
                      await orthogonalizePolygon(polygonId, {
                        angleThreshold: 10,
                        simplify: true,
                        tolerance: 1.0,
                        maxVertices: 12
                      });
                      establishRoof(polygonId);
                    }}
                    editingPolygonVertex={editingPolygonVertex}
                    setEditingPolygonVertex={setEditingPolygonVertex}
                    onEditPolygon={(polygonId) => setEditingPolygonVertex(polygonId)}
                    onDeletePolygon={(polygonId) => {
                      if (polygonData) {
                        const updatedPolygons = polygonData.polygons.filter(p => p.id !== polygonId);
                        setPolygonData({ ...polygonData, polygons: updatedPolygons });
                        setSelectedPolygon(null);
                        setEditingPolygonVertex(null);
                        setSelectedPolygonForEdit(null);
                        const newEditedCoords = { ...editedPolygonCoords };
                        delete newEditedCoords[polygonId];
                        setEditedPolygonCoords(newEditedCoords);
                        const newObstructions = { ...obstructions };
                        delete newObstructions[polygonId];
                        setObstructions(newObstructions);
                      }
                    }}
                    snapEdgeIndex={snapEdgeIndex}
                    calculateEdgeAzimuth={calculateEdgeAzimuth}
                    isSelectingSnapEdge={isSelectingSnapEdge}
                    setIsSelectingSnapEdge={setIsSelectingSnapEdge}
                    isDrawingRoof={isDrawingRoof}
                    onStartDrawingRoof={() => {
                      setIsDrawingRoof(true);
                      setRoofDrawingVertices([]);
                      setSelectedPolygon(null);
                    }}
                    onResetPolygons={() => {
                      if (originalPolygonData) {
                        setPolygonData(JSON.parse(JSON.stringify(originalPolygonData)));
                      }
                      setSelectedPolygon(null);
                      setEditingPolygonVertex(null);
                      setSelectedPolygonForEdit(null);
                      setEditedPolygonCoords({});
                      setObstructions({});
                      setSelectedObstruction(null);
                      setEstablishedRoofIds(new Set());
                      setSnapEdgeIndex({});
                      setManualAzimuth({});
                      setIsDrawingRoof(false);
                      setRoofDrawingVertices([]);
                      setIsDrawingObstruction(false);
                    }}
                    obstructions={obstructions}
                    isDrawingObstruction={isDrawingObstruction}
                    setIsDrawingObstruction={setIsDrawingObstruction}
                    obstructionDrawMode={obstructionDrawMode}
                    setObstructionDrawMode={setObstructionDrawMode}
                    selectedObstruction={selectedObstruction}
                    setSelectedObstruction={setSelectedObstruction}
                    onDeleteObstruction={(polygonId, index) => {
                      const newObstructions = { ...obstructions };
                      newObstructions[polygonId] = newObstructions[polygonId].filter((_, idx) => idx !== index);
                      setObstructions(newObstructions);
                      setSelectedObstruction(null);
                    }}
                  />
                )}

                {/* Menu Items - System Mode */}
                {siteSystemMode === 'system' && (
                  <SystemModePanel
                    moduleCardCollapsed={moduleCardCollapsed}
                    setModuleCardCollapsed={setModuleCardCollapsed}
                    fieldSegmentsCollapsed={fieldSegmentsCollapsed}
                    setFieldSegmentsCollapsed={setFieldSegmentsCollapsed}
                    moduleControlsProps={{
                      selectedPolygon,
                      polygonData,
                      selectedSegment,
                      moduleSegments,
                      availableBrands,
                      selectedBrand,
                      onBrandChange: handleBrandChange,
                      availablePanels,
                      selectedPanel,
                      onPanelChange: handlePanelChange,
                      panelsLoading,
                      moduleWidth,
                      moduleHeight,
                      onWidthChange: setModuleWidth,
                      onHeightChange: setModuleHeight,
                      moduleOrientation,
                      onOrientationChange: setModuleOrientation,
                      modulePowerWatts,
                      onPowerChange: setModulePowerWatts,
                      moduleCount: selectedPolygon !== null ? (modules[selectedPolygon] || []).length : 0,
                      totalModuleArea: selectedPolygon !== null ? (modules[selectedPolygon] || []).length * moduleWidth * moduleHeight : 0,
                      roofCoverage: selectedPolygon !== null && polygonData ? (() => {
                        const poly = polygonData.polygons.find((p: any) => p.id === selectedPolygon);
                        if (poly) {
                          const roofArea = poly.area_m2;
                          return ((modules[selectedPolygon] || []).length * moduleWidth * moduleHeight) / roofArea * 100;
                        }
                        return 0;
                      })() : 0,
                      systemCapacity: selectedPolygon !== null ? (modules[selectedPolygon] || []).length * modulePowerWatts / 1000 : 0,
                      moduleSpacing,
                      onModuleSpacingChange: setModuleSpacing,
                      rowSpacingMultiplier,
                      onRowSpacingChange: setRowSpacingMultiplier,
                      edgeSetback,
                      onEdgeSetbackChange: setEdgeSetback,
                      obstructionSetback,
                      onObstructionSetbackChange: setObstructionSetback,
                      onFillRoof: handleAutoFill,
                      onDrawSegment: () => setIsDrawingSegment(true),
                      isFreeFieldMode,
                      onFreeFieldModeChange: setIsFreeFieldMode,
                      freeFieldAzimuth,
                      onFreeFieldAzimuthChange: (newAzimuth) => {
                        setFreeFieldAzimuth(newAzimuth);
                        if (selectedSegment && selectedSegment.polygonId === FREE_FIELD_POLYGON_ID) {
                          const segment = moduleSegments[FREE_FIELD_POLYGON_ID]?.[selectedSegment.index];
                          if (segment) {
                            const updatedSegments = [...(moduleSegments[FREE_FIELD_POLYGON_ID] || [])];
                            updatedSegments[selectedSegment.index] = { ...segment, rotation: newAzimuth, azimuth: newAzimuth };
                            setModuleSegments({ ...moduleSegments, [FREE_FIELD_POLYGON_ID]: updatedSegments });
                            setTimeout(() => refillSegment(FREE_FIELD_POLYGON_ID, selectedSegment.index, segment.bounds), 50);
                          }
                        }
                      },
                      onCopySegment: handleCopySegment,
                      onSegmentSpacingChange: (modSpacing, rowSpacing) => {
                        if (selectedSegment && selectedPolygon !== null) {
                          const updatedSegments = [...moduleSegments[selectedPolygon]];
                          updatedSegments[selectedSegment.index].config.moduleSpacing = modSpacing;
                          updatedSegments[selectedSegment.index].config.rowSpacing = rowSpacing;
                          setModuleSegments({ ...moduleSegments, [selectedPolygon]: updatedSegments });
                        }
                      },
                      onRefillSegment: () => {
                        if (selectedSegment && selectedPolygon !== null) {
                          const segment = moduleSegments[selectedPolygon][selectedSegment.index];
                          refillSegment(selectedPolygon, selectedSegment.index, segment.bounds);
                        }
                      },
                      onClearModules: () => {
                        if (selectedPolygon !== null) setModules({ ...modules, [selectedPolygon]: [] });
                      },
                      targetModuleCount,
                      onTargetModuleCountChange: setTargetModuleCount,
                      targetFillPercentage,
                      onTargetFillPercentageChange: setTargetFillPercentage,
                      isAutoRoofMode: loadingMode !== 'manual',
                      collapsed: false,
                    }}
                    moduleSegments={moduleSegments}
                    setModuleSegments={setModuleSegments}
                    selectedSegment={selectedSegment}
                    setSelectedSegment={setSelectedSegment}
                    modules={modules}
                    setModules={setModules}
                    modulePowerWatts={modulePowerWatts}
                    moduleSpacing={moduleSpacing}
                    rowSpacingMultiplier={rowSpacingMultiplier}
                    FREE_FIELD_POLYGON_ID={FREE_FIELD_POLYGON_ID}
                    refillSegment={refillSegment}
                    isDrawingSegment={isDrawingSegment}
                    onStartDrawingSegment={() => setIsDrawingSegment(true)}
                    onCancelDrawingSegment={() => {
                      setIsDrawingSegment(false);
                      setPreviewModules([]);
                      setDragPreviewRect(null);
                      setFreeFieldPolygonVertices([]);
                    }}
                    onCopySegment={handleCopySegment}
                    isFreeFieldMode={isFreeFieldMode}
                    onFreeFieldModeChange={setIsFreeFieldMode}
                  />
                )}
              </>
            )}

            {/* Compact Mode: Icon-only vertical stack with direct hover menus */}
            {menuCollapsed && (
              <CompactModePanel
                siteSystemMode={siteSystemMode}
                onModeChange={(newMode) => {
                  setSiteSystemMode(newMode);
                  setEditMode(newMode);
                  if (newMode === 'system') {
                    setIsDrawingObstruction(false);
                    setSelectedObstruction(null);
                    setEditingObstruction(null);
                  } else {
                    setSelectedSegment(null);
                    setIsDraggingSegment(false);
                    setIsResizingSegmentCorner(false);
                    setIsRotatingSegment(false);
                    setIsStretchingSegmentEdge(false);
                    setIsDrawingSegment(false);
                    setPreviewModules([]);
                    setSelectedModule(null);
                    setSelectedModules(new Set());
                    setIsPlacingModule(false);
                  }
                }}
                roofCardCollapsed={roofCardCollapsed}
                setRoofCardCollapsed={setRoofCardCollapsed}
                obstructionCardCollapsed={obstructionCardCollapsed}
                setObstructionCardCollapsed={setObstructionCardCollapsed}
                moduleCardCollapsed={moduleCardCollapsed}
                setModuleCardCollapsed={setModuleCardCollapsed}
                roofControlsProps={{
                  isManualMode: loadingMode === 'manual',
                  selectedPolygon,
                  polygons: polygonData?.polygons || [],
                  onSelectPolygon: (id) => setSelectedPolygon(id),
                  isSelectMode: isRoofSelectMode,
                  onToggleSelectMode: () => setIsRoofSelectMode(!isRoofSelectMode),
                  isAddRoofMode,
                  onToggleAddRoofMode: () => setIsAddRoofMode(!isAddRoofMode),
                  selectedPolygons,
                  activeRoofId,
                  showAllPolygons,
                  onToggleAllPolygons: (show) => setShowAllPolygons(show),
                  showMeasurements,
                  onToggleMeasurements: (show) => setShowMeasurements(show),
                  isRoofEstablished: isRoofEstablished(selectedPolygon),
                  onConvertToRoof: () => {
                    if (selectedPolygon !== null) {
                      if (snapEdgeIndex[selectedPolygon] === undefined) {
                        const coords = getPolygonCoords(selectedPolygon);
                        const bestEdgeIndex = autoDetectBestEdge(coords);
                        setSnapEdgeIndex({ ...snapEdgeIndex, [selectedPolygon]: bestEdgeIndex });
                      }
                      establishRoof(selectedPolygon);
                    }
                  },
                  isEditing: selectedPolygonForEdit !== null,
                  onStartEdit: () => setSelectedPolygonForEdit(selectedPolygon),
                  onSaveEdit: () => {
                    if (selectedPolygonForEdit !== null && editedPolygonCoords[selectedPolygonForEdit]) {
                      const updatedPolygons = polygonData?.polygons.map((p: any) =>
                        p.id === selectedPolygonForEdit
                          ? { ...p, coordinates: editedPolygonCoords[selectedPolygonForEdit] }
                          : p
                      );
                      setPolygonData({ ...polygonData, polygons: updatedPolygons });
                      setSelectedPolygonForEdit(null);
                      setEditingPolygonVertex(null);
                    }
                  },
                  onCancelEdit: () => {
                    setSelectedPolygonForEdit(null);
                    setEditingPolygonVertex(null);
                    setEditedPolygonCoords({});
                  },
                  onDeletePolygon: () => {
                    if (selectedPolygon !== null && polygonData) {
                      const updatedPolygons = polygonData.polygons.filter((p: any) => p.id !== selectedPolygon);
                      setPolygonData({ ...polygonData, polygons: updatedPolygons });
                      setSelectedPolygon(null);
                    }
                  },
                  onStartRedraw: () => {
                    setIsDrawingRoof(true);
                    setRoofDrawingVertices([]);
                  },
                  onResetPolygons: () => {
                    if (originalPolygonData) {
                      setPolygonData(JSON.parse(JSON.stringify(originalPolygonData)));
                      setSelectedPolygon(null);
                      setEditedPolygonCoords({});
                    }
                  },
                  isSelectingEdge: isSelectingSnapEdge,
                  onStartEdgeSelection: () => setIsSelectingSnapEdge(true),
                  onStopEdgeSelection: () => setIsSelectingSnapEdge(false),
                  selectedEdgeIndex: selectedPolygon !== null ? snapEdgeIndex[selectedPolygon] : undefined,
                  edgeAzimuth: selectedPolygon !== null ? snapEdgeAzimuth[selectedPolygon] : undefined,
                  manualAzimuth: selectedPolygon !== null ? manualAzimuth[selectedPolygon] : undefined,
                  onManualAzimuthChange: (azimuth) => {
                    if (selectedPolygon !== null) {
                      setManualAzimuth({ ...manualAzimuth, [selectedPolygon]: azimuth });
                    }
                  },
                  collapsed: false,
                }}
                obstructionControlsProps={{
                  selectedPolygon,
                  obstructions,
                  isDrawing: isDrawingObstruction,
                  drawMode: obstructionDrawMode,
                  onStartDrawing: (mode) => {
                    setObstructionDrawMode(mode);
                    setIsDrawingObstruction(true);
                  },
                  onStopDrawing: () => setIsDrawingObstruction(false),
                  selectedObstruction,
                  onSelectObstruction: (obs) => setSelectedObstruction(obs),
                  onDeleteObstruction: () => {
                    if (selectedObstruction) {
                      const { polygonId, index } = selectedObstruction;
                      const newObstructions = { ...obstructions };
                      newObstructions[polygonId] = newObstructions[polygonId].filter((_, i) => i !== index);
                      setObstructions(newObstructions);
                      setSelectedObstruction(null);
                    }
                  },
                  editingObstruction,
                  collapsed: false,
                }}
                moduleControlsProps={{
                  selectedPolygon,
                  polygonData,
                  selectedSegment,
                  moduleSegments,
                  availableBrands,
                  selectedBrand,
                  onBrandChange: handleBrandChange,
                  availablePanels,
                  selectedPanel,
                  onPanelChange: handlePanelChange,
                  panelsLoading,
                  moduleWidth,
                  moduleHeight,
                  onWidthChange: setModuleWidth,
                  onHeightChange: setModuleHeight,
                  moduleOrientation,
                  onOrientationChange: setModuleOrientation,
                  modulePowerWatts,
                  onPowerChange: setModulePowerWatts,
                  moduleCount: selectedPolygon !== null ? (modules[selectedPolygon] || []).length : 0,
                  totalModuleArea: selectedPolygon !== null ? (modules[selectedPolygon] || []).length * moduleWidth * moduleHeight : 0,
                  roofCoverage: selectedPolygon !== null && polygonData ?
                    (() => {
                      const poly = polygonData.polygons.find((p: any) => p.id === selectedPolygon);
                      if (poly) {
                        const roofArea = poly.area_m2;
                        return ((modules[selectedPolygon] || []).length * moduleWidth * moduleHeight) / roofArea * 100;
                      }
                      return 0;
                    })() : 0,
                  systemCapacity: selectedPolygon !== null ? (modules[selectedPolygon] || []).length * modulePowerWatts / 1000 : 0,
                  moduleSpacing,
                  onModuleSpacingChange: setModuleSpacing,
                  rowSpacingMultiplier,
                  onRowSpacingChange: setRowSpacingMultiplier,
                  edgeSetback,
                  onEdgeSetbackChange: setEdgeSetback,
                  obstructionSetback,
                  onObstructionSetbackChange: setObstructionSetback,
                  onFillRoof: handleAutoFill,
                  onDrawSegment: () => setIsDrawingSegment(true),
                  isFreeFieldMode,
                  onFreeFieldModeChange: setIsFreeFieldMode,
                  freeFieldAzimuth,
                  onFreeFieldAzimuthChange: (newAzimuth) => {
                    setFreeFieldAzimuth(newAzimuth);
                    if (selectedSegment && selectedSegment.polygonId === FREE_FIELD_POLYGON_ID) {
                      const segment = moduleSegments[FREE_FIELD_POLYGON_ID]?.[selectedSegment.index];
                      if (segment) {
                        const updatedSegments = [...(moduleSegments[FREE_FIELD_POLYGON_ID] || [])];
                        updatedSegments[selectedSegment.index] = {
                          ...segment,
                          rotation: newAzimuth,
                          azimuth: newAzimuth
                        };
                        setModuleSegments({
                          ...moduleSegments,
                          [FREE_FIELD_POLYGON_ID]: updatedSegments
                        });
                        setTimeout(() => {
                          refillSegment(FREE_FIELD_POLYGON_ID, selectedSegment.index, segment.bounds);
                        }, 50);
                      }
                    }
                  },
                  onCopySegment: handleCopySegment,
                  onSegmentSpacingChange: (moduleSpacingVal, rowSpacing) => {
                    if (selectedSegment && selectedPolygon !== null) {
                      const updatedSegments = [...moduleSegments[selectedPolygon]];
                      updatedSegments[selectedSegment.index].config.moduleSpacing = moduleSpacingVal;
                      updatedSegments[selectedSegment.index].config.rowSpacing = rowSpacing;
                      setModuleSegments({
                        ...moduleSegments,
                        [selectedPolygon]: updatedSegments
                      });
                    }
                  },
                  onRefillSegment: () => {
                    if (selectedSegment && selectedPolygon !== null) {
                      const segment = moduleSegments[selectedPolygon][selectedSegment.index];
                      refillSegment(selectedPolygon, selectedSegment.index, segment.bounds);
                    }
                  },
                  onClearModules: () => {
                    if (selectedPolygon !== null) {
                      setModules({ ...modules, [selectedPolygon]: [] });
                    }
                  },
                  targetModuleCount,
                  onTargetModuleCountChange: setTargetModuleCount,
                  targetFillPercentage,
                  onTargetFillPercentageChange: setTargetFillPercentage,
                  isAutoRoofMode: loadingMode !== 'manual',
                  collapsed: false,
                }}
              />
            )}
          </div>
        </div>
        )}

        {/* Right Panel - System Metrics (Permanent) - Hidden when hideSystemMetrics is true */}
        {!hideSystemMetrics && (
          <div className="absolute right-4 top-4 z-[1000] w-60">
            {/* Calculate total modules including free-field segments */}
            {(() => {
              const roofModuleCount = selectedPolygon !== null ? (modules[selectedPolygon] || []).length : 0;
              const freeFieldModuleCount = (modules[FREE_FIELD_POLYGON_ID] || []).length;
              const totalModuleCount = roofModuleCount + freeFieldModuleCount;
              const totalModuleAreaVal = totalModuleCount * moduleWidth * moduleHeight;

              // Calculate roof area (selected polygon)
              const roofAreaVal = selectedPolygon !== null ? calculatePolygonArea(getPolygonCoords(selectedPolygon)) : 0;

              // Calculate free-field segment areas (count as usable area)
              const freeFieldSegmentArea = (moduleSegments[FREE_FIELD_POLYGON_ID] || []).reduce((total, seg) => {
                return total + (seg.bounds?.length >= 3 ? calculatePolygonArea(seg.bounds) : 0);
              }, 0);

              // Total usable area = roof area + free-field segment areas
              const totalUsableArea = roofAreaVal + freeFieldSegmentArea;

              // Calculate obstructions (both roof and free-field)
              const roofObstructionArea = selectedPolygon !== null
                ? (obstructions[selectedPolygon] || []).reduce((total, obs) => total + calculatePolygonArea(obs), 0)
                : 0;
              const freeFieldObstructionArea = (obstructions[FREE_FIELD_POLYGON_ID] || []).reduce((total, obs) => total + calculatePolygonArea(obs), 0);
              const totalObstructionArea = roofObstructionArea + freeFieldObstructionArea;

              return (
                <SystemMetrics
                  roofArea={totalUsableArea > 0 ? totalUsableArea : undefined}
                  obstructionArea={totalObstructionArea}
                  netArea={totalUsableArea > 0 ? totalUsableArea - totalObstructionArea : undefined}
                  moduleCount={totalModuleCount}
                  totalModuleArea={totalModuleAreaVal}
                  roofCoverage={totalUsableArea > 0 ? (totalModuleAreaVal / totalUsableArea * 100) : 0}
                  systemCapacity={totalModuleCount * modulePowerWatts / 1000}
                  modulePower={modulePowerWatts}
                  moduleBrand={selectedBrand}
                  moduleModel={typeof selectedPanel === 'string' ? selectedPanel : selectedPanel?.model}
                  moduleAzimuth={selectedPolygon !== null ? snapEdgeAzimuth[selectedPolygon] : undefined}
                  freeFieldModuleCount={freeFieldModuleCount > 0 ? freeFieldModuleCount : undefined}
                  freeFieldSegmentArea={freeFieldSegmentArea > 0 ? freeFieldSegmentArea : undefined}
                />
              );
            })()}
          </div>
        )}

        {/* Floating Ruler + Screenshot - Hidden in embed mode (hideControls) */}
        {!hideControls && (
        <>
        <FloatingRulerButton
          isRulerMode={isRulerMode}
          rulerPoints={rulerPoints}
          onToggleRuler={() => {
            if (isRulerMode) {
              setIsRulerMode(false);
              setRulerPoints([]);
              setRulerCursorPos(null);
            } else {
              setIsRulerMode(true);
              setRulerPoints([]);
              setRulerCursorPos(null);
            }
          }}
          formatDistance={formatDistance}
          calculateTotalDistance={calculateTotalDistance}
        />

        {/* Floating Screenshot Buttons - Bottom Left next to Ruler */}
        <FloatingScreenshotButton
          hasProjectId={!!currentProjectId}
          onCaptureAndSave={async () => {
            try {
              const { captureMapScreenshot, saveScreenshotToBackend, getInstallationBoundsFromSegments } = await import('../utils/export/mapScreenshotUtils');
              if (!leafletMapRef.current) {
                return { success: false, error: 'Map not available' };
              }
              if (!currentProjectId) {
                return { success: false, error: 'No project saved' };
              }
              const frameBounds = getInstallationBoundsFromSegments(moduleSegmentsRef.current);
              const captureOpts = frameBounds ? { frameBounds, zoomOutPercent: 20 } : {};
              const result = await captureMapScreenshot(leafletMapRef.current, captureOpts);

              if (result.success && result.blob) {
                const saveResult = await saveScreenshotToBackend(currentProjectId, result.blob, {
                  rpu: currentRPU || undefined,
                  dealId: loadedProjectData?.metadata?.deal_id ?? undefined,
                });
                return saveResult;
              }

              return result;
            } catch (error) {
              return { success: false, error: (error as Error).message };
            }
          }}
          onCaptureAndDownload={async () => {
            try {
              const { captureMapScreenshot, downloadScreenshot, getInstallationBoundsFromSegments } = await import('../utils/export/mapScreenshotUtils');
              if (!leafletMapRef.current) {
                return { success: false, error: 'Map not available' };
              }
              const frameBounds = getInstallationBoundsFromSegments(moduleSegmentsRef.current);
              const captureOpts = frameBounds ? { frameBounds, zoomOutPercent: 20 } : {};
              const result = await captureMapScreenshot(leafletMapRef.current, captureOpts);

              if (result.success && result.dataUrl) {
                // Generate timestamp for unique filename (YYYYMMDD_HHMMSS in local timezone)
                const now = new Date();
                const timestamp = now.getFullYear().toString() +
                  (now.getMonth() + 1).toString().padStart(2, '0') +
                  now.getDate().toString().padStart(2, '0') + '_' +
                  now.getHours().toString().padStart(2, '0') +
                  now.getMinutes().toString().padStart(2, '0') +
                  now.getSeconds().toString().padStart(2, '0');

                const filename = currentRPU
                  ? `solar-design-${currentRPU}_${timestamp}.png`
                  : currentProjectId
                  ? `solar-design-${currentProjectId}_${timestamp}.png`
                  : `solar-design_${timestamp}.png`;
                downloadScreenshot(result.dataUrl, filename);
              }

              return result;
            } catch (error) {
              console.error('Screenshot download error:', error);
              return { success: false, error: (error as Error).message };
            }
          }}
          onRequestSave={() => {
            // Trigger project save
            saveProjectV2MultiRoof();
          }}
        />
        </>
        )}
      </div>
    </div>
  );
}
